import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  Button,
  Avatar,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  DeleteOutline,
  DoneAll,
  GetApp,
  InsertDriveFile,
} from "@material-ui/icons";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { hasMenuPermission } from "../../layout/MainListItems";
import {
  buildMessageVariables,
  fetchContactCustomFieldDetails,
  fetchContactCustomFieldNames,
  fetchWasheetMessageVariables,
} from "../../utils/messageVariables";
import { usePrivacyCensorSettings } from "../../utils/privacyCensor";
import chatBackgroundMask from "../../assets/background.svg";

const NO_FOLLOWUP_CONDITION_LABEL = "__no_followup_condition_label__";
const NO_FOLLOWUP_CONDITION_QUEUE = "__no_followup_condition_queue__";
const ALL_AI_PROCESSES = "all-ai-processes";

const useStyles = makeStyles(theme => ({
  textField: {
    width: "100%",
  },
  formControl: {
    width: "100%",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  stickyDialogTitle: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  dialogTitleContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  activeSwitch: {
    marginRight: 0,
  },
  block: {
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 6,
    background: "#fafafa",
  },
  blockHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  blockActions: {
    display: "flex",
    gap: theme.spacing(0.5),
  },
  dialogBody: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    gridTemplateRows: "auto minmax(0, 1fr)",
    gap: theme.spacing(2),
    height: "calc(100vh - 188px)",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      height: "auto",
      overflowY: "auto",
    },
  },
  basicFields: {
    gridColumn: 2,
    gridRow: 1,
    minWidth: 0,
    [theme.breakpoints.down("sm")]: {
      gridColumn: "auto",
      gridRow: "auto",
      order: 0,
    },
  },
  messageBuilder: {
    gridColumn: 1,
    gridRow: "1 / 3",
    minHeight: 0,
    minWidth: 0,
    overflowY: "auto",
    paddingRight: theme.spacing(0.5),
    [theme.breakpoints.down("sm")]: {
      gridColumn: "auto",
      gridRow: "auto",
      overflowY: "visible",
      order: 1,
    },
  },
  configArea: {
    gridColumn: 2,
    gridRow: 2,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    minWidth: 0,
    overflowY: "auto",
    paddingRight: theme.spacing(0.5),
    [theme.breakpoints.down("sm")]: {
      gridColumn: "auto",
      gridRow: "auto",
      overflowY: "visible",
      order: 2,
    },
  },
  configAddArea: {
    order: 9999,
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  configAddButton: {
    width: 54,
    height: 54,
    border: `2px dashed ${theme.palette.primary.main}`,
    background: theme.palette.background.paper,
    color: theme.palette.primary.main,
    "&:hover": {
      background: theme.palette.action.hover,
    },
  },
  blockGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 165px",
    gap: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  messageBlockGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(185px, 0.72fr) minmax(0, 1fr) 90px",
    gap: theme.spacing(1),
    alignItems: "start",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "minmax(185px, 0.78fr) minmax(0, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  messageDelayField: {
    [theme.breakpoints.down("md")]: {
      gridColumn: "2",
    },
    [theme.breakpoints.down("xs")]: {
      gridColumn: "auto",
    },
  },
  pollFieldsGrid: {
    [theme.breakpoints.up("md")]: {
      gridColumn: "2 / 4",
    },
  },
  previewPane: {
    minHeight: 143,
    padding: theme.spacing(1),
    borderRadius: 6,
    backgroundColor: "#efeae2",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "flex-start",
    overflow: "hidden",
    position: "relative",
    minWidth: 0,
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      backgroundColor: "rgba(0, 0, 0, 0.08)",
      maskImage: `url(${chatBackgroundMask})`,
      maskRepeat: "repeat",
      maskMode: "alpha",
      maskSize: "540px 960px",
      WebkitMaskImage: `url(${chatBackgroundMask})`,
      WebkitMaskRepeat: "repeat",
      WebkitMaskSize: "540px 960px",
    },
  },
  previewBubble: {
    width: "100%",
    maxWidth: 255,
    minHeight: 42,
    padding: 5,
    paddingBottom: 18,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    backgroundColor: "#dcf8c6",
    color: "#303030",
    boxShadow: "0 1px 1px #b3b3b3",
    position: "relative",
    whiteSpace: "pre-wrap",
    overflowWrap: "break-word",
    zIndex: 1,
  },
  previewMedia: {
    display: "block",
    width: "100%",
    maxHeight: 150,
    objectFit: "cover",
    borderRadius: 8,
    backgroundColor: "#d8d8d8",
  },
  previewCaption: {
    display: "block",
    padding: "5px 4px 0",
    fontSize: 13,
    lineHeight: "18px",
  },
  previewEmpty: {
    color: "#6f6f6f",
    fontSize: 12,
    lineHeight: "17px",
  },
  previewDocumentShell: {
    borderRadius: 8,
    marginBottom: 2,
    overflow: "hidden",
  },
  previewDocumentFrame: {
    height: 127,
    width: "calc(100% + 20px)",
    border: 0,
    display: "block",
    backgroundColor: "#ffffff",
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
    pointerEvents: "none",
    marginLeft: -2,
    marginTop: -2,
    marginRight: -18,
  },
  previewDocumentCard: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    color: "#111b21",
    textDecoration: "none",
    overflow: "hidden",
    backgroundColor: "rgba(255, 255, 255, 0.24)",
  },
  previewDocumentIconWrap: {
    width: 34,
    height: 42,
    flex: "0 0 34px",
    borderRadius: 6,
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "inset 0 -1px 0 rgba(0, 0, 0, 0.14)",
  },
  previewDocumentIcon: {
    fontSize: 22,
    lineHeight: 1,
  },
  previewDocumentExtension: {
    maxWidth: 30,
    marginTop: -2,
    fontSize: 8,
    lineHeight: "10px",
    fontWeight: 700,
    textTransform: "uppercase",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  previewDocumentInfo: {
    minWidth: 0,
    flex: "1 1 auto",
  },
  previewDocumentName: {
    display: "block",
    fontSize: 14,
    lineHeight: "18px",
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  previewDocumentMeta: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginTop: 3,
    color: "#667781",
    fontSize: 11,
    lineHeight: "14px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  previewDocumentDownloadIcon: {
    flex: "0 0 auto",
    color: "#667781",
    fontSize: 22,
  },
  previewAudio: {
    width: "100%",
    minWidth: 0,
    height: 36,
  },
  previewTime: {
    position: "absolute",
    right: 6,
    bottom: 3,
    fontSize: 10,
    color: "#7a7a7a",
  },
  pollBubble: {
    minWidth: 0,
    maxWidth: 245,
    padding: "8px 8px 4px",
  },
  blockTypeActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
  pollTitle: {
    fontSize: 15,
    fontWeight: 600,
    lineHeight: "20px",
    marginBottom: 8,
    color: "#111b21",
  },
  pollHint: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    color: "#667781",
    marginBottom: 8,
  },
  pollOption: {
    display: "grid",
    gridTemplateColumns: "24px 1fr auto",
    columnGap: 8,
    alignItems: "center",
    padding: "6px 0 8px",
  },
  pollCheck: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    border: "2px solid #8696a0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#00a884",
  },
  pollOptionName: {
    fontSize: 14,
    lineHeight: "18px",
    color: "#111b21",
    overflowWrap: "anywhere",
  },
  pollVotes: {
    fontSize: 12,
    color: "#667781",
    minWidth: 18,
    textAlign: "right",
  },
  pollProgressTrack: {
    gridColumn: "2 / 4",
    height: 4,
    borderRadius: 999,
    backgroundColor: "rgba(134, 150, 160, 0.25)",
    overflow: "hidden",
    marginTop: 4,
  },
  pollProgressBar: {
    height: "100%",
    borderRadius: 999,
    backgroundColor: "#00a884",
  },
  pollFooter: {
    borderTop: "1px solid rgba(134, 150, 160, 0.25)",
    color: "#008069",
    fontSize: 13,
    fontWeight: 500,
    textAlign: "center",
    paddingTop: 8,
    marginTop: 2,
  },
  listBody: {
    fontSize: 14,
    lineHeight: "19px",
    color: "#111b21",
    marginBottom: 8,
    overflowWrap: "anywhere",
  },
  listButton: {
    borderTop: "1px solid rgba(134, 150, 160, 0.25)",
    color: "#008069",
    fontSize: 13,
    fontWeight: 500,
    textAlign: "center",
    paddingTop: 8,
    marginTop: 8,
  },
  listOption: {
    padding: "6px 0",
    borderTop: "1px solid rgba(134, 150, 160, 0.16)",
  },
  listOptionTitle: {
    display: "block",
    fontSize: 14,
    lineHeight: "18px",
    color: "#111b21",
    overflowWrap: "anywhere",
  },
  listOptionDescription: {
    display: "block",
    fontSize: 12,
    lineHeight: "16px",
    color: "#667781",
    overflowWrap: "anywhere",
  },
  previewLocationMap: {
    height: 108,
    borderRadius: 8,
    background:
      "linear-gradient(135deg, #c7ead0 0%, #c7ead0 36%, #b7d4f0 36%, #b7d4f0 58%, #f2dfb8 58%, #f2dfb8 100%)",
    position: "relative",
    overflow: "hidden",
    marginBottom: theme.spacing(0.75),
    "&::after": {
      content: '""',
      position: "absolute",
      left: "50%",
      top: "50%",
      width: 16,
      height: 16,
      transform: "translate(-50%, -70%) rotate(45deg)",
      borderRadius: "50% 50% 50% 0",
      backgroundColor: "#e53935",
      boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
    },
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  fileName: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: theme.palette.text.secondary,
  },
  helper: {
    display: "block",
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  variablesBox: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 6,
    background: "#fff",
  },
  mediaTranscriptionBox: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    padding: "0px 8px",
    border: "1px solid rgba(0, 168, 132, 0.28)",
    borderRadius: 6,
    background: "rgba(0, 168, 132, 0.06)",
  },
  variablesLabel: {
    display: "block",
    marginBottom: theme.spacing(0.75),
    color: theme.palette.text.secondary,
  },
  variablesChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
  },
  sequenceActions: {
    marginTop: theme.spacing(0.5),
  },
  scheduleDayList: {
    display: "grid",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  scheduleDayRow: {
    display: "grid",
    gridTemplateColumns: "86px 1fr auto 1fr",
    alignItems: "center",
    gap: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
      alignItems: "stretch",
    },
  },
  scheduleDayLabel: {
    margin: 0,
  },
  scheduleSeparator: {
    color: theme.palette.text.secondary,
    textAlign: "center",
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  censoredText: {
    filter: "blur(4px)",
    userSelect: "none",
  },
  censoredPhoto: {
    filter: "blur(6px)",
  },
}));

const acceptedMedia =
  "image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.zip,.rar,.webp";

const weekDays = [
  { value: 1, label: "Lun" },
  { value: 2, label: "Mar" },
  { value: 3, label: "Mie" },
  { value: 4, label: "Jue" },
  { value: 5, label: "Vie" },
  { value: 6, label: "Sab" },
  { value: 0, label: "Dom" },
];

const createScheduleDays = () =>
  weekDays.map(day => ({
    day: day.value,
    enabled: [1, 2, 3, 4, 5].includes(day.value),
    startTime: "09:00",
    endTime: [6, 0].includes(day.value) ? "13:00" : "18:00",
  }));

const normalizeScheduleDays = days => {
  if (!Array.isArray(days)) return createScheduleDays();

  return weekDays.map(day => {
    const savedDay = days.find(item => Number(item?.day) === day.value);

    return {
      day: day.value,
      enabled: Boolean(savedDay),
      startTime: savedDay?.startTime || "09:00",
      endTime: savedDay?.endTime || ([6, 0].includes(day.value) ? "13:00" : "18:00"),
    };
  });
};

const clampDelaySeconds = value => Math.min(60, Math.max(0, Number(value || 0)));
const NO_PARENT_SEQUENCE = "no-parent";
const NO_INACTIVITY_SEQUENCE = "no-inactivity-sequence";
const NO_FOLLOWUP_SEQUENCE = "no-followup-sequence";
const NO_FOLLOWUP_CANCEL_SEQUENCE = "no-followup-cancel-sequence";
const NO_OUT_OF_HOURS_SEQUENCE = "no-out-of-hours-sequence";
const NO_AFTER_EXECUTION_SEQUENCE = "no-after-execution-sequence";
const NO_ORDERS_MAIN_MENU_SEQUENCE = "no-orders-main-menu-sequence";
const NO_CART_MAIN_MENU_SEQUENCE = "no-cart-main-menu-sequence";
const NO_PRODUCT = "no-product";
const NO_PRODUCT_INTRO_SEQUENCE = "no-product-intro-sequence";
const NO_PRODUCT_MAIN_MENU_SEQUENCE = "no-product-main-menu-sequence";
const NO_STREAMING_SERVICES_MAIN_MENU_SEQUENCE = "no-streaming-services-main-menu-sequence";
const NO_PROCESS_SEQUENCE_PROCESS = "no-process-sequence-process";
const NO_PROCESS_MAIN_MENU_SEQUENCE = "no-process-main-menu-sequence";
const NO_AGENDA_SERVICE = "no-agenda-service";
const ALL_AGENDA_SERVICES = "all-agenda-services";
const NO_AGENDA_MAIN_MENU_SEQUENCE = "no-agenda-main-menu-sequence";
const NO_AGENDA_RESERVATIONS_MAIN_MENU_SEQUENCE = "no-agenda-reservations-main-menu-sequence";
const NO_TRANSFER_TYPE = "no-transfer";
const NO_TRANSFER_QUEUE = "no-transfer-queue";
const NO_TRANSFER_USER = "no-transfer-user";
const TRANSFER_AUTO_ASSIGN = "auto";
const NO_AI_ACTION = "no-ai-action";
const NO_CHATBOT_ACTION = "no-chatbot-action";
const NO_FUNNEL_ACTION = "no-funnel-action";
const NO_FUNNEL_STAGE = "no-funnel-stage";
const NO_FOLLOWUP_CONDITION_SEQUENCE = "no-followup-condition-sequence";
const sequenceSelectMenuProps = {
  PaperProps: {
    style: {
      maxHeight: 320,
    },
  },
};
const LABEL_CONDITION_NONE = "none";
const NO_LABEL_CONDITION_LABEL = "no-label-condition-label";
const NO_CAPTURE_PROCESS = "no-capture-process";
const NO_CAPTURE_PROCESS_FIELD = "no-capture-process-field";
const NO_CAPTURE_PROCESS_FILE = "no-capture-process-file";
const NO_CAPTURE_CUSTOM_FIELD = "no-capture-custom-field";
const NO_CAPTURE_WASHEET_DOCUMENT = "no-capture-washeet-document";
const NO_CAPTURE_WASHEET_FIELD = "no-capture-washeet-field";

const toOptionalApiValue = (value, emptyValue) => (value === emptyValue ? "" : value || "");

const createFollowupCondition = () => ({
  type: "label",
  operator: "is",
  labelId: "",
  funnelId: NO_FUNNEL_ACTION,
  funnelStageId: NO_FUNNEL_STAGE,
  queueId: "",
  contactField: "name",
  contactValue: "",
  sequenceId: NO_FOLLOWUP_CONDITION_SEQUENCE,
});

const createBlock = () => ({
  type: "message",
  text: "",
  delaySeconds: 0,
  inactivityValue: 1,
  inactivityUnit: "minutes",
  inactivitySequenceId: NO_INACTIVITY_SEQUENCE,
  followupValue: 1,
  followupUnit: "days",
  followupMode: "normal",
  followupSequenceId: NO_FOLLOWUP_SEQUENCE,
  followupCancelSequenceId: NO_FOLLOWUP_CANCEL_SEQUENCE,
  followupConditions: [createFollowupCondition()],
  file: null,
  media: null,
  mediaTranscriptionEnabled: false,
  mediaTranscriptionText: "",
});

const createInactivityBlock = () => ({
  type: "inactivity",
  text: "",
  delaySeconds: 0,
  inactivityValue: 5,
  inactivityUnit: "minutes",
  inactivitySequenceId: NO_INACTIVITY_SEQUENCE,
  file: null,
  media: null,
});

const createFollowupBlock = () => ({
  type: "followup",
  text: "",
  delaySeconds: 0,
  inactivityValue: 1,
  inactivityUnit: "minutes",
  inactivitySequenceId: NO_INACTIVITY_SEQUENCE,
  followupValue: 1,
  followupUnit: "days",
  followupSequenceId: NO_FOLLOWUP_SEQUENCE,
  followupCancelSequenceId: NO_FOLLOWUP_CANCEL_SEQUENCE,
  file: null,
  media: null,
});

const createNotifyBlock = () => ({
  type: "notify",
  text: "",
  delaySeconds: 0,
  inactivityValue: 1,
  inactivityUnit: "minutes",
  inactivitySequenceId: NO_INACTIVITY_SEQUENCE,
  followupValue: 1,
  followupUnit: "days",
  followupSequenceId: NO_FOLLOWUP_SEQUENCE,
  followupCancelSequenceId: NO_FOLLOWUP_CANCEL_SEQUENCE,
  notifyContactId: "",
  notifyContact: null,
  file: null,
  media: null,
  mediaTranscriptionEnabled: false,
  mediaTranscriptionText: "",
});

const createPollBlock = () => ({
  type: "poll",
  pollTitle: "",
  pollOptions: "",
  pollAllowMultiple: false,
  pollSelectableCount: 1,
  delaySeconds: 0,
});

const createWhatsAppListBlock = () => ({
  type: "whatsappList",
  listTitle: "",
  listBody: "",
  listButtonText: "Ver opciones",
  listFooter: "",
  listSections: [{ title: "Opciones", rows: [{ title: "", description: "" }] }],
  delaySeconds: 0,
});

const createContactBlock = () => ({
  type: "contact",
  contactName: "",
  contactPhone: "",
  delaySeconds: 0,
});

const createLocationBlock = () => ({
  type: "location",
  locationLatitude: "",
  locationLongitude: "",
  locationName: "",
  locationAddress: "",
  delaySeconds: 0,
});

const getBlockLabel = block => {
  if (block.type === "inactivity") return i18n.t("chatbotModal.form.inactivityMessage");
  if (block.type === "followup") return "Seguimiento";
  if (block.type === "notify") return "Notificar";
  if (block.type === "poll") return "Encuesta";
  if (block.type === "whatsappList") return "Lista";
  if (block.type === "contact") return "Contacto";
  if (block.type === "location") return "Ubicacion";
  return i18n.t("chatbotModal.form.message");
};

const getPollAllowMultiple = block =>
  block.pollAllowMultiple !== undefined
    ? Boolean(block.pollAllowMultiple)
    : Number(block.pollSelectableCount || 1) > 1;

const createListRow = () => ({ title: "", description: "" });

const createListSection = () => ({
  title: "Opciones",
  rows: [createListRow()],
});

const normalizeListSections = block => {
  if (Array.isArray(block?.listSections) && block.listSections.length) {
    return block.listSections.map(section => ({
      title: section.title || "Opciones",
      rows: Array.isArray(section.rows) && section.rows.length
        ? section.rows.map(row => ({
            title: row.title || "",
            description: row.description || "",
          }))
        : [createListRow()],
    }));
  }

  return [createListSection()];
};

const flattenListSections = block =>
  normalizeListSections(block).flatMap(section =>
    section.rows.map(row => ({
      ...row,
      sectionTitle: section.title,
    }))
  );

const getSavedMediaUrl = media => {
  if (!media) return "";
  if (media.url || media.mediaUrl) return media.url || media.mediaUrl;
  if (!media.filename) return "";

  const baseUrl = String(api.defaults.baseURL || window.location.origin).replace(/\/+$/, "");
  return `${baseUrl}/public/${encodeURI(media.filename)}`;
};

const getMimeTypeFromName = name => {
  const extension = String(name || "").split(".").pop()?.toLowerCase();

  if (["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(extension)) {
    return "image/*";
  }
  if (["mp4", "webm", "mov", "m4v", "avi"].includes(extension)) {
    return "video/*";
  }
  if (["mp3", "ogg", "wav", "m4a", "opus"].includes(extension)) {
    return "audio/*";
  }

  return "";
};

const getBlockMediaInfo = block => {
  const file = block.file || null;
  const media = block.media || null;
  const name = file?.name || media?.originalName || media?.filename || "";
  const mimetype = file?.type || media?.mimetype || getMimeTypeFromName(name);

  return {
    file,
    name,
    mimetype,
    savedUrl: getSavedMediaUrl(media),
  };
};

const getDocumentExtension = name => {
  const cleanName = String(name || "").split("?")[0];
  const extension = cleanName.includes(".") ? cleanName.split(".").pop() : "";

  return extension ? extension.toUpperCase().slice(0, 5) : "FILE";
};

const getDocumentIconColor = extension => {
  const normalizedExtension = String(extension || "").toLowerCase();

  if (normalizedExtension === "pdf") return "#f15c5c";
  if (["doc", "docx"].includes(normalizedExtension)) return "#4285f4";
  if (["xls", "xlsx", "csv"].includes(normalizedExtension)) return "#21a366";
  if (["ppt", "pptx"].includes(normalizedExtension)) return "#d24726";
  if (["zip", "rar", "7z"].includes(normalizedExtension)) return "#7b61ff";

  return "#667781";
};

const isPdfDocumentPreview = ({ name, mimetype, mediaUrl }) =>
  /\.pdf(\?|$)/i.test(name || "") ||
  /\.pdf(\?|$)/i.test(mediaUrl || "") ||
  /pdf/i.test(mimetype || "");

const renderDocumentPreview = ({ classes, name, mimetype, mediaUrl }) => {
  const filename = name || "Archivo adjunto";
  const extension = getDocumentExtension(filename);
  const showPreview = mediaUrl && isPdfDocumentPreview({ name: filename, mimetype, mediaUrl });

  return (
    <div className={classes.previewDocumentShell}>
      {showPreview && (
        <iframe
          title={filename}
          className={classes.previewDocumentFrame}
          src={mediaUrl}
        />
      )}
      <div className={classes.previewDocumentCard}>
        <span
          className={classes.previewDocumentIconWrap}
          style={{ backgroundColor: getDocumentIconColor(extension) }}
        >
          <InsertDriveFile className={classes.previewDocumentIcon} />
          <span className={classes.previewDocumentExtension}>{extension}</span>
        </span>
        <span className={classes.previewDocumentInfo}>
          <span className={classes.previewDocumentName}>{filename}</span>
          <span className={classes.previewDocumentMeta}>{extension}</span>
        </span>
        <GetApp className={classes.previewDocumentDownloadIcon} />
      </div>
    </div>
  );
};

const ChatbotMessagePreview = ({ block, classes }) => {
  const [fileUrl, setFileUrl] = useState("");
  const { file, name, mimetype, savedUrl } = getBlockMediaInfo(block);
  const mediaUrl = fileUrl || savedUrl;
  const text = block.text || "";
  const hasMedia = Boolean(file || savedUrl);
  const isImage = mimetype.startsWith("image/");
  const isVideo = mimetype.startsWith("video/");
  const isAudio = mimetype.startsWith("audio/");

  useEffect(() => {
    if (!file) {
      setFileUrl("");
      return undefined;
    }

    const nextUrl = URL.createObjectURL(file);
    setFileUrl(nextUrl);

    return () => URL.revokeObjectURL(nextUrl);
  }, [file]);

  return (
    <div className={classes.previewPane}>
      <div className={classes.previewBubble}>
        {hasMedia ? (
          isImage ? (
            <img className={classes.previewMedia} src={mediaUrl} alt={name || "Vista previa"} />
          ) : isVideo ? (
            <video className={classes.previewMedia} src={mediaUrl} controls muted />
          ) : isAudio ? (
            <audio className={classes.previewAudio} src={mediaUrl} controls />
          ) : (
            renderDocumentPreview({ classes, name, mimetype, mediaUrl })
          )
        ) : null}
        {text ? (
          <span className={classes.previewCaption}>{text}</span>
        ) : !hasMedia ? (
          <span className={classes.previewEmpty}>Vista previa del mensaje</span>
        ) : null}
        <span className={classes.previewTime}>ahora</span>
      </div>
    </div>
  );
};

const ChatbotPollPreview = ({ block, classes }) => {
  const rawOptions = (block.pollOptions || "")
    .split(/\r?\n/)
    .map(option => option.trim())
    .filter(Boolean)
    .slice(0, 4);
  const visibleOptions = (rawOptions.length ? rawOptions : ["Opcion 1", "Opcion 2"]).map(
    (name, index) => ({
      id: `preview-poll-${index}`,
      name,
      votes: 0,
      percentage: 0,
    })
  );
  const allowMultiple = getPollAllowMultiple(block);

  return (
    <div className={classes.previewPane}>
      <div className={classes.previewBubble}>
        <div className={classes.pollBubble}>
          <div className={classes.pollTitle}>{block.pollTitle || "Encuesta"}</div>
          <div className={classes.pollHint}>
            <DoneAll style={{ fontSize: 16 }} />
            <span>
              {allowMultiple ? "Selecciona una opcion o mas." : "Selecciona una opcion."}
            </span>
          </div>
          {visibleOptions.map(option => (
            <div className={classes.pollOption} key={option.id}>
              <span className={classes.pollCheck} />
              <span className={classes.pollOptionName}>{option.name}</span>
              <span className={classes.pollVotes}>{option.votes}</span>
              <span className={classes.pollProgressTrack}>
                <span
                  className={classes.pollProgressBar}
                  style={{ width: `${option.percentage}%` }}
                />
              </span>
            </div>
          ))}
          <div className={classes.pollFooter}>0 votos</div>
        </div>
        <span className={classes.previewTime}>ahora</span>
      </div>
    </div>
  );
};

const ChatbotListPreview = ({ block, classes }) => {
  const options = flattenListSections(block).filter(row => row.title).slice(0, 3);
  const visibleOptions = options.length
    ? options
    : [
        { title: "Opcion 1", description: "Descripcion opcional" },
        { title: "Opcion 2", description: "" },
      ];

  return (
    <div className={classes.previewPane}>
      <div className={classes.previewBubble}>
        <div className={classes.pollBubble}>
          <div className={classes.pollTitle}>{block.listTitle || "Lista"}</div>
          <div className={classes.listBody}>
            {block.listBody || "Elige una opcion de la lista."}
          </div>
          {visibleOptions.map((option, optionIndex) => (
            <div className={classes.listOption} key={`preview-list-${optionIndex}`}>
              <span className={classes.listOptionTitle}>{option.title}</span>
              {option.description ? (
                <span className={classes.listOptionDescription}>
                  {option.description}
                </span>
              ) : null}
            </div>
          ))}
          <div className={classes.listButton}>{block.listButtonText || "Ver opciones"}</div>
        </div>
        <span className={classes.previewTime}>ahora</span>
      </div>
    </div>
  );
};

const ChatbotContactPreview = ({ block, classes }) => {
  const name = block.contactName || "Nombre";
  const initial = name.trim()[0] || "C";

  return (
    <div className={classes.previewPane}>
      <div className={classes.previewBubble}>
        <div style={{ width: "100%", maxWidth: "245px", padding: "8px 8px 4px" }}>
          <Grid
            container
            spacing={1}
            alignItems="center"
            style={{ justifyContent: "space-between" }}
          >
            <Grid item xs={3}>
              <Avatar
                style={{
                  width: 40,
                  height: 40,
                  backgroundColor: "#dfe5e7",
                  color: "#667781",
                }}
              >
                {initial}
              </Avatar>
            </Grid>
            <Grid item xs={8}>
              <Typography
                style={{
                  marginLeft: 4,
                  fontSize: 15,
                  fontWeight: 600,
                  lineHeight: "20px",
                  color: "#111b21",
                  overflowWrap: "anywhere",
                }}
                variant="subtitle1"
                gutterBottom
              >
                {name}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider style={{ backgroundColor: "rgba(134, 150, 160, 0.25)" }} />
              <Button
                fullWidth
                color="primary"
                disabled
                style={{
                  color: "#008069",
                  fontSize: 13,
                  fontWeight: 500,
                  textTransform: "none",
                  paddingTop: 8,
                }}
              >
                Conversar
              </Button>
            </Grid>
          </Grid>
        </div>
        <span className={classes.previewTime}>ahora</span>
      </div>
    </div>
  );
};

const ChatbotLocationPreview = ({ block, classes }) => {
  const hasCoordinates = block.locationLatitude !== "" && block.locationLongitude !== "";
  const coordinates = hasCoordinates ? `${block.locationLatitude},${block.locationLongitude}` : "";
  const mapEmbedUrl = hasCoordinates
    ? `https://maps.google.com/maps?q=${encodeURIComponent(coordinates)}&z=17&output=embed`
    : "";
  const description = [
    block.locationName || "Ubicacion",
    block.locationAddress || "",
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <div className={classes.previewPane}>
      <div className={classes.previewBubble}>
        <div style={{ width: "100%", maxWidth: "245px", padding: "8px 8px 4px" }}>
          <div>
            {mapEmbedUrl ? (
              <iframe
                src={mapEmbedUrl}
                title="Ubicacion"
                style={{
                  width: "100%",
                  height: "120px",
                  border: 0,
                  borderRadius: 8,
                  display: "block",
                }}
                loading="lazy"
              />
            ) : (
              <div className={classes.previewLocationMap} />
            )}
          </div>
          {description && (
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                lineHeight: "20px",
                color: "#111b21",
                textAlign: "center",
                margin: "10px 8px 8px",
                overflowWrap: "anywhere",
                whiteSpace: "pre-line",
              }}
            >
              {description}
            </div>
          )}
          <div style={{ display: "block", content: "", clear: "both" }} />
          <div>
            <Divider style={{ backgroundColor: "rgba(134, 150, 160, 0.25)" }} />
            <Button
              fullWidth
              color="primary"
              disabled
              style={{
                color: "#008069",
                fontSize: 13,
                fontWeight: 500,
                textTransform: "none",
                paddingTop: 8,
              }}
            >
              Visualizar
            </Button>
          </div>
        </div>
        <span className={classes.previewTime}>ahora</span>
      </div>
    </div>
  );
};

const getInitialConfigSections = (
  values,
  canUseLabels,
  canUseFunnels,
  canUseAi,
  canUseOrders,
  canUseProcesses
) => {
  const sections = [];

  if (
    values.matchType !== initialState.matchType ||
    values.trigger ||
    values.parentId !== NO_PARENT_SEQUENCE ||
    (values.childSequenceIds || []).length ||
    values.afterExecutionBehavior !== "root" ||
    values.afterExecutionSequenceId !== NO_AFTER_EXECUTION_SEQUENCE ||
    values.noMatchMessage ||
    values.noMatchAiEnabled ||
    values.noMatchAiPrompt ||
    values.captureEnabled ||
    values.chatbotAction !== NO_CHATBOT_ACTION
  ) {
    sections.push("chatbot");
  }
  if (canUseOrders && values.ordersEnabled) sections.push("orders");
  if (canUseOrders && values.cartEnabled) sections.push("cart");
  if (canUseOrders && values.productEnabled) sections.push("product");
  if (canUseOrders && values.streamingServicesEnabled) sections.push("streamingServices");
  if (canUseProcesses && values.processSequenceEnabled) sections.push("processes");
  if (values.agendaEnabled) sections.push("agenda");
  if (values.agendaReservationsEnabled) sections.push("agendaReservations");
  if (values.quickReplyEnabled) sections.push("quickReply");
  if (values.scheduleEnabled) sections.push("schedule");
  if (
    canUseLabels &&
    (values.labelConditionType !== LABEL_CONDITION_NONE ||
      (values.labelConditionIds || []).length)
  ) {
    sections.push("labelCondition");
  }
  if (values.executionMode && values.executionMode !== "unlimited") sections.push("execution");
  if (
    canUseLabels &&
    (values.transferType !== NO_TRANSFER_TYPE ||
      values.transferQueueId !== NO_TRANSFER_QUEUE ||
      values.transferUserId !== NO_TRANSFER_USER ||
      (values.transferAutoAssignQueueIds || []).length ||
      (values.transferAutoAssignUserIds || []).length)
  ) {
    sections.push("transfer");
  }
  if (
    canUseLabels &&
    ((values.addLabelIds || []).length || (values.removeLabelIds || []).length)
  ) {
    sections.push("labels");
  }
  if (
    canUseFunnels &&
    (values.funnelActionFunnelId !== NO_FUNNEL_ACTION ||
      values.funnelActionStageId !== NO_FUNNEL_STAGE)
  ) {
    sections.push("funnel");
  }
  if (canUseAi && values.aiAction !== NO_AI_ACTION) sections.push("ai");

  return sections;
};

const normalizeConfigSectionOrder = (savedOrder, fallbackSections) => {
  const availableSections = new Set(fallbackSections);
  const orderedSections = Array.isArray(savedOrder)
    ? savedOrder.filter(sectionKey => availableSections.has(sectionKey))
    : [];

  return Array.from(new Set([...orderedSections, ...fallbackSections]));
};

const initialState = {
  name: "",
  category: "General",
  trigger: "",
  matchType: "exact",
  requireAllTriggers: false,
  skipMarkAsRead: false,
  quickReplyEnabled: false,
  quickReplyMatchType: "exact",
  quickReplyTrigger: "",
  connectionId: "",
  sharedConnectionIds: [],
  parentId: NO_PARENT_SEQUENCE,
  isActive: true,
  scheduleEnabled: false,
  scheduleDays: createScheduleDays(),
  labelConditionType: LABEL_CONDITION_NONE,
  labelConditionIds: [],
  executionMode: "unlimited",
  executionLimit: "",
  executionInterval: "",
  executionIntervalUnit: "hours",
  transferType: NO_TRANSFER_TYPE,
  transferQueueId: NO_TRANSFER_QUEUE,
  transferUserId: NO_TRANSFER_USER,
  transferAutoAssignEnabled: false,
  transferAutoAssignQueueIds: [],
  transferAutoAssignUserIds: [],
  addLabelIds: [],
  removeLabelIds: [],
  aiAction: NO_AI_ACTION,
  chatbotAction: NO_CHATBOT_ACTION,
  funnelActionFunnelId: NO_FUNNEL_ACTION,
  funnelActionStageId: NO_FUNNEL_STAGE,
  aiInitialMessage: "",
  aiPrompt: "",
  orchestratorModeEnabled: false,
  useSequencesTool: true,
  useWasheetsTool: true,
  useWebSearchTool: true,
  useAgendaTool: true,
  useOrdersTool: true,
  useWebhookTools: true,
  useContactTool: true,
  useProcessesTool: false,
  selectedProcessIds: [],
  naturalMessageModeEnabled: true,
  outOfHoursSequenceId: NO_OUT_OF_HOURS_SEQUENCE,
  noMatchMessage: "",
  noMatchAiEnabled: false,
  noMatchAiPrompt: "",
  captureEnabled: false,
  captureMode: "text",
  captureTargetType: "contact",
  captureContactField: "name",
  captureContactCustomField: "",
  captureProcessId: NO_CAPTURE_PROCESS,
  captureProcessFieldKey: NO_CAPTURE_PROCESS_FIELD,
  captureProcessFileKey: NO_CAPTURE_PROCESS_FILE,
  captureWasheetDocumentId: NO_CAPTURE_WASHEET_DOCUMENT,
  captureWasheetField: NO_CAPTURE_WASHEET_FIELD,
  captureProcessingMode: "direct",
  captureAiPrompt: "",
  captureOnTypeMismatch: "message",
  captureErrorMessage: "",
  cartEnabled: false,
  cartMainMenuSequenceId: NO_CART_MAIN_MENU_SEQUENCE,
  productEnabled: false,
  productId: NO_PRODUCT,
  productIntroSequenceId: NO_PRODUCT_INTRO_SEQUENCE,
  productMainMenuSequenceId: NO_PRODUCT_MAIN_MENU_SEQUENCE,
  ordersEnabled: false,
  ordersMainMenuSequenceId: NO_ORDERS_MAIN_MENU_SEQUENCE,
  streamingServicesEnabled: false,
  streamingServicesMainMenuSequenceId: NO_STREAMING_SERVICES_MAIN_MENU_SEQUENCE,
  processSequenceEnabled: false,
  processSequenceProcessId: NO_PROCESS_SEQUENCE_PROCESS,
  processSequenceMainMenuSequenceId: NO_PROCESS_MAIN_MENU_SEQUENCE,
  agendaEnabled: false,
  agendaServiceId: ALL_AGENDA_SERVICES,
  agendaMainMenuSequenceId: NO_AGENDA_MAIN_MENU_SEQUENCE,
  agendaReservationStatus: "confirmed",
  agendaReservationsEnabled: false,
  agendaReservationsMainMenuSequenceId: NO_AGENDA_RESERVATIONS_MAIN_MENU_SEQUENCE,
  afterExecutionBehavior: "root",
  afterExecutionSequenceId: NO_AFTER_EXECUTION_SEQUENCE,
  childSequenceIds: [],
};

const normalizeConnectionIds = (...values) => {
  const ids = new Set();
  values.forEach(value => {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach(id => {
        if (id) ids.add(Number(id));
      });
      return;
    }
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          parsed.forEach(id => {
            if (id) ids.add(Number(id));
          });
          return;
        }
      } catch {
        value.split(",").forEach(id => {
          const parsedId = Number(id.trim());
          if (!Number.isNaN(parsedId)) ids.add(parsedId);
        });
        return;
      }
    }
    ids.add(Number(value));
  });
  return Array.from(ids).filter(id => !Number.isNaN(id));
};

const recordOwnsWhatsapp = (record, connectionId) =>
  normalizeConnectionIds(record?.connectionId, record?.sharedConnectionIds).includes(Number(connectionId));

const getWhatsappName = (connections, connectionId) =>
  connections.find(whatsapp => Number(whatsapp.id) === Number(connectionId))?.name ||
  connections.find(whatsapp => Number(whatsapp.id) === Number(connectionId))?.number ||
  `Conexión ${connectionId}`;

const formatSequenceOption = sequence =>
  `ID: ${sequence.id} - ${sequence.name}`;

const selectedProcessLabels = (processes, selectedIds) => {
  const ids = normalizeConnectionIds(selectedIds);
  if (!ids.length) return "Todos los procesos";
  return ids
    .map(id => processes.find(process => Number(process.id) === Number(id))?.name)
    .filter(Boolean)
    .join(", ");
};

const getProcessFields = process =>
  (process?.stages || []).reduce(
    (items, stage) => [...items, ...(stage.fields || [])],
    []
  );

const getProcessFiles = process =>
  (process?.stages || []).reduce(
    (items, stage) => [...items, ...(stage.fileRequirements || [])],
    []
  );

const normalizeFollowupConditions = conditions => {
  if (!Array.isArray(conditions) || !conditions.length) {
    return [createFollowupCondition()];
  }

  return conditions.slice(0, 2).map(condition => ({
    ...createFollowupCondition(),
    ...condition,
    operator: condition.operator === "isNot" ? "isNot" : "is",
    sequenceId: condition.sequenceId || NO_FOLLOWUP_CONDITION_SEQUENCE,
    funnelId: condition.funnelId || NO_FUNNEL_ACTION,
    funnelStageId: condition.funnelStageId || NO_FUNNEL_STAGE,
  }));
};

const ChatbotSchema = Yup.object().shape({
  name: Yup.string().required("Requerido"),
  matchType: Yup.string().required("Requerido"),
});

const getAssignmentMode = mode => (mode === "normal" ? "normal" : "multiagent");

const ChatbotModal = ({
  open,
  onClose,
  onSave,
  sequenceId,
  defaultConnectionId,
  defaultConfigSections = [],
  initialSequenceName,
  initialSequenceCategory = "General",
  categories = [],
  extraVariables = [],
}) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const privacyCensorSettings = usePrivacyCensorSettings(user?.id);
  const hideResellerControls = Boolean(user?.company?.resellerBranding);
  const forceNormalMode = user?.company?.forceNormalMode === true;
  const assignmentModeStorageKey = `ticketAssignmentMode:${
    user?.companyId || user?.company?.id || "default"
  }`;
  const [assignmentMode, setAssignmentMode] = useState(() =>
    getAssignmentMode(localStorage.getItem(assignmentModeStorageKey))
  );
  const canUseLabels = !hideResellerControls && hasMenuPermission(user, "labels");
  const canUseTransfer = canUseLabels && !forceNormalMode;
  const canUseAi = !hideResellerControls && hasMenuPermission(user, "ai");
  const canUseTickets = hasMenuPermission(user, "tickets");
  const canUseFunnels = !hideResellerControls && hasMenuPermission(user, "funnels");
  const canUseOrders = hasMenuPermission(user, "orders");
  const canUseProcesses = hasMenuPermission(user, "processes");
  const canUseAgenda = hasMenuPermission(user, "agenda");
  const canUseWebhooks = hasMenuPermission(user, "webhooks");
  const isMounted = useRef(true);
  const sequenceModalApplySelection = useRef(null);
  const [sequence, setSequence] = useState(initialState);
  const [blocks, setBlocks] = useState([createBlock()]);
  const [sequences, setSequences] = useState([]);
  const [funnels, setFunnels] = useState([]);
  const [queues, setQueues] = useState([]);
  const [users, setUsers] = useState([]);
  const [labels, setLabels] = useState([]);
  const [connections, setConnections] = useState([]);
  const [products, setProducts] = useState([]);
  const [availableProcesses, setAvailableProcesses] = useState([]);
  const [agendaServices, setAgendaServices] = useState([]);
  const [washeetDocuments, setWasheetDocuments] = useState([]);
  const [washeetHeadersByDocument, setWasheetHeadersByDocument] = useState({});
  const [contactOptions, setContactOptions] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [customFieldNames, setCustomFieldNames] = useState([]);
  const [customFieldDetails, setCustomFieldDetails] = useState([]);
  const [washeetVariables, setWasheetVariables] = useState([]);
  const [sequenceModalOpen, setSequenceModalOpen] = useState(false);
  const [sequenceModalId, setSequenceModalId] = useState(null);
  const [sequenceModalBlockIndex, setSequenceModalBlockIndex] = useState(null);
  const [sequenceModalConnectionId, setSequenceModalConnectionId] = useState("");
  const [sequenceModalTarget, setSequenceModalTarget] = useState("block");
  const [configMenuAnchor, setConfigMenuAnchor] = useState(null);
  const [activeConfigSections, setActiveConfigSections] = useState([]);
  const categoryOptions = Array.from(
    new Set(
      [
        "General",
        ...(categories || []),
        ...sequences.map(item => item.category || "General"),
      ].filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b));
  const availableMessageVariables = buildMessageVariables(
    customFieldNames,
    [...washeetVariables, ...(extraVariables || [])]
  );
  const captureCustomFieldDetails = customFieldDetails.filter(
    field => !String(field.name || "").startsWith("Etiquetas-")
  );
  const contactTextCustomFieldNames = captureCustomFieldDetails
    .filter(field => String(field.fieldType || "text") !== "file")
    .map(field => field.name);
  const contactFileCustomFieldNames = captureCustomFieldDetails
    .filter(field => String(field.fieldType || "text") === "file")
    .map(field => field.name);
  const canUseWasheets = washeetDocuments.length > 0;
  const notifyContactOptions = useMemo(() => {
    const options = [...contactOptions];
    blocks.forEach(block => {
      const contact = block.notifyContact;
      if (
        contact?.id &&
        !options.some(option => String(option.id) === String(contact.id))
      ) {
        options.unshift(contact);
      }
    });
    return options;
  }, [contactOptions, blocks]);
  const isNormalAssignmentMode = forceNormalMode || assignmentMode === "normal";

  useEffect(() => {
    const handleAssignmentModeChanged = event => {
      setAssignmentMode(getAssignmentMode(event.detail?.mode));
    };

    window.addEventListener(
      "ticketAssignmentModeChanged",
      handleAssignmentModeChanged
    );

    return () => {
      window.removeEventListener(
        "ticketAssignmentModeChanged",
        handleAssignmentModeChanged
      );
    };
  }, []);

  useEffect(() => {
    if (forceNormalMode) {
      setAssignmentMode("normal");
      return;
    }

    setAssignmentMode(
      getAssignmentMode(localStorage.getItem(assignmentModeStorageKey))
    );

    if (!open) return;

    const fetchAssignmentMode = async () => {
      try {
        const { data } = await api.get("/settings/ticket-assignment-mode");
        const nextMode = getAssignmentMode(data.mode);
        localStorage.setItem(assignmentModeStorageKey, nextMode);
        if (isMounted.current) {
          setAssignmentMode(nextMode);
        }
      } catch (err) {
        toastError(err);
      }
    };

    fetchAssignmentMode();
  }, [assignmentModeStorageKey, forceNormalMode, open]);

  const configSectionOptions = [
    { key: "chatbot", label: i18n.t("chatbotModal.sections.chatbot") },
    { key: "schedule", label: i18n.t("chatbotModal.sections.schedule") },
    ...(canUseLabels
      ? [{ key: "labelCondition", label: "Condicion de etiquetas" }]
      : []),
    { key: "execution", label: i18n.t("chatbotModal.sections.execution") },
    { key: "quickReply", label: i18n.t("chatbotModal.sections.quickReply") },
    ...(canUseOrders
      ? [
          { key: "orders", label: "Pedidos" },
          { key: "cart", label: "Carrito" },
          { key: "product", label: "Producto" },
          { key: "streamingServices", label: "Mis servicios" },
        ]
      : []),
    ...(canUseProcesses ? [{ key: "processes", label: "Procesos" }] : []),
    ...(canUseAgenda ? [{ key: "agenda", label: "Agenda" }] : []),
    ...(canUseAgenda ? [{ key: "agendaReservations", label: "Mis reservas" }] : []),
    ...(canUseTransfer
      ? [{ key: "transfer", label: i18n.t("chatbotModal.sections.transfer") }]
      : []),
    ...(canUseLabels
      ? [{ key: "labels", label: i18n.t("chatbotModal.sections.labels") }]
      : []),
    ...(canUseFunnels ? [{ key: "funnel", label: "Embudo" }] : []),
    ...(canUseAi ? [{ key: "ai", label: i18n.t("chatbotModal.sections.ai") }] : []),
  ];

  const getConfigSectionOrder = sectionKey => {
    const index = activeConfigSections.indexOf(sectionKey);
    return index === -1 ? 0 : index;
  };

  const applyConfigSectionDefaults = (sectionKey, setFieldValue) => {
    if (sectionKey === "orders") {
      setFieldValue("ordersEnabled", true);
      setFieldValue("cartEnabled", false);
      setFieldValue("productEnabled", false);
      setFieldValue("streamingServicesEnabled", false);
      setFieldValue("processSequenceEnabled", false);
      setFieldValue("agendaEnabled", false);
      setFieldValue("agendaReservationsEnabled", false);
      setFieldValue("captureEnabled", false);
      setFieldValue("cartMainMenuSequenceId", NO_CART_MAIN_MENU_SEQUENCE);
      setFieldValue("productId", NO_PRODUCT);
      setFieldValue("productIntroSequenceId", NO_PRODUCT_INTRO_SEQUENCE);
      setFieldValue("productMainMenuSequenceId", NO_PRODUCT_MAIN_MENU_SEQUENCE);
      setFieldValue(
        "streamingServicesMainMenuSequenceId",
        NO_STREAMING_SERVICES_MAIN_MENU_SEQUENCE
      );
      setFieldValue("processSequenceProcessId", NO_PROCESS_SEQUENCE_PROCESS);
      setFieldValue("processSequenceMainMenuSequenceId", NO_PROCESS_MAIN_MENU_SEQUENCE);
      setFieldValue("agendaServiceId", ALL_AGENDA_SERVICES);
      setFieldValue("agendaMainMenuSequenceId", NO_AGENDA_MAIN_MENU_SEQUENCE);
      setFieldValue(
        "agendaReservationsMainMenuSequenceId",
        NO_AGENDA_RESERVATIONS_MAIN_MENU_SEQUENCE
      );
    }

    if (sectionKey === "cart") {
      setFieldValue("cartEnabled", true);
      setFieldValue("ordersEnabled", false);
      setFieldValue("productEnabled", false);
      setFieldValue("streamingServicesEnabled", false);
      setFieldValue("processSequenceEnabled", false);
      setFieldValue("agendaEnabled", false);
      setFieldValue("agendaReservationsEnabled", false);
      setFieldValue("captureEnabled", false);
      setFieldValue("ordersMainMenuSequenceId", NO_ORDERS_MAIN_MENU_SEQUENCE);
      setFieldValue("productId", NO_PRODUCT);
      setFieldValue("productIntroSequenceId", NO_PRODUCT_INTRO_SEQUENCE);
      setFieldValue("productMainMenuSequenceId", NO_PRODUCT_MAIN_MENU_SEQUENCE);
      setFieldValue(
        "streamingServicesMainMenuSequenceId",
        NO_STREAMING_SERVICES_MAIN_MENU_SEQUENCE
      );
      setFieldValue("processSequenceProcessId", NO_PROCESS_SEQUENCE_PROCESS);
      setFieldValue("processSequenceMainMenuSequenceId", NO_PROCESS_MAIN_MENU_SEQUENCE);
      setFieldValue("agendaServiceId", ALL_AGENDA_SERVICES);
      setFieldValue("agendaMainMenuSequenceId", NO_AGENDA_MAIN_MENU_SEQUENCE);
      setFieldValue(
        "agendaReservationsMainMenuSequenceId",
        NO_AGENDA_RESERVATIONS_MAIN_MENU_SEQUENCE
      );
    }

    if (sectionKey === "product") {
      setFieldValue("productEnabled", true);
      setFieldValue("ordersEnabled", false);
      setFieldValue("cartEnabled", false);
      setFieldValue("streamingServicesEnabled", false);
      setFieldValue("processSequenceEnabled", false);
      setFieldValue("agendaEnabled", false);
      setFieldValue("agendaReservationsEnabled", false);
      setFieldValue("captureEnabled", false);
      setFieldValue("ordersMainMenuSequenceId", NO_ORDERS_MAIN_MENU_SEQUENCE);
      setFieldValue("cartMainMenuSequenceId", NO_CART_MAIN_MENU_SEQUENCE);
      setFieldValue("productIntroSequenceId", NO_PRODUCT_INTRO_SEQUENCE);
      setFieldValue(
        "streamingServicesMainMenuSequenceId",
        NO_STREAMING_SERVICES_MAIN_MENU_SEQUENCE
      );
      setFieldValue("processSequenceProcessId", NO_PROCESS_SEQUENCE_PROCESS);
      setFieldValue("processSequenceMainMenuSequenceId", NO_PROCESS_MAIN_MENU_SEQUENCE);
      setFieldValue("agendaServiceId", ALL_AGENDA_SERVICES);
      setFieldValue("agendaMainMenuSequenceId", NO_AGENDA_MAIN_MENU_SEQUENCE);
      setFieldValue(
        "agendaReservationsMainMenuSequenceId",
        NO_AGENDA_RESERVATIONS_MAIN_MENU_SEQUENCE
      );
    }

    if (sectionKey === "streamingServices") {
      setFieldValue("streamingServicesEnabled", true);
      setFieldValue("ordersEnabled", false);
      setFieldValue("cartEnabled", false);
      setFieldValue("productEnabled", false);
      setFieldValue("processSequenceEnabled", false);
      setFieldValue("agendaEnabled", false);
      setFieldValue("agendaReservationsEnabled", false);
      setFieldValue("captureEnabled", false);
      setFieldValue("ordersMainMenuSequenceId", NO_ORDERS_MAIN_MENU_SEQUENCE);
      setFieldValue("cartMainMenuSequenceId", NO_CART_MAIN_MENU_SEQUENCE);
      setFieldValue("productId", NO_PRODUCT);
      setFieldValue("productIntroSequenceId", NO_PRODUCT_INTRO_SEQUENCE);
      setFieldValue("productMainMenuSequenceId", NO_PRODUCT_MAIN_MENU_SEQUENCE);
      setFieldValue("processSequenceProcessId", NO_PROCESS_SEQUENCE_PROCESS);
      setFieldValue("processSequenceMainMenuSequenceId", NO_PROCESS_MAIN_MENU_SEQUENCE);
      setFieldValue("agendaServiceId", ALL_AGENDA_SERVICES);
      setFieldValue("agendaMainMenuSequenceId", NO_AGENDA_MAIN_MENU_SEQUENCE);
      setFieldValue(
        "agendaReservationsMainMenuSequenceId",
        NO_AGENDA_RESERVATIONS_MAIN_MENU_SEQUENCE
      );
    }

    if (sectionKey === "processes") {
      setFieldValue("processSequenceEnabled", true);
      setFieldValue("ordersEnabled", false);
      setFieldValue("cartEnabled", false);
      setFieldValue("productEnabled", false);
      setFieldValue("streamingServicesEnabled", false);
      setFieldValue("agendaEnabled", false);
      setFieldValue("agendaReservationsEnabled", false);
      setFieldValue("captureEnabled", false);
      setFieldValue("ordersMainMenuSequenceId", NO_ORDERS_MAIN_MENU_SEQUENCE);
      setFieldValue("cartMainMenuSequenceId", NO_CART_MAIN_MENU_SEQUENCE);
      setFieldValue("productId", NO_PRODUCT);
      setFieldValue("productIntroSequenceId", NO_PRODUCT_INTRO_SEQUENCE);
      setFieldValue("productMainMenuSequenceId", NO_PRODUCT_MAIN_MENU_SEQUENCE);
      setFieldValue(
        "streamingServicesMainMenuSequenceId",
        NO_STREAMING_SERVICES_MAIN_MENU_SEQUENCE
      );
      setFieldValue("agendaServiceId", ALL_AGENDA_SERVICES);
      setFieldValue("agendaMainMenuSequenceId", NO_AGENDA_MAIN_MENU_SEQUENCE);
      setFieldValue(
        "agendaReservationsMainMenuSequenceId",
        NO_AGENDA_RESERVATIONS_MAIN_MENU_SEQUENCE
      );
    }

    if (sectionKey === "agenda") {
      setFieldValue("agendaEnabled", true);
      setFieldValue("ordersEnabled", false);
      setFieldValue("cartEnabled", false);
      setFieldValue("productEnabled", false);
      setFieldValue("streamingServicesEnabled", false);
      setFieldValue("processSequenceEnabled", false);
      setFieldValue("agendaReservationsEnabled", false);
      setFieldValue("captureEnabled", false);
      setFieldValue("ordersMainMenuSequenceId", NO_ORDERS_MAIN_MENU_SEQUENCE);
      setFieldValue("cartMainMenuSequenceId", NO_CART_MAIN_MENU_SEQUENCE);
      setFieldValue("productId", NO_PRODUCT);
      setFieldValue("productIntroSequenceId", NO_PRODUCT_INTRO_SEQUENCE);
      setFieldValue("productMainMenuSequenceId", NO_PRODUCT_MAIN_MENU_SEQUENCE);
      setFieldValue(
        "streamingServicesMainMenuSequenceId",
        NO_STREAMING_SERVICES_MAIN_MENU_SEQUENCE
      );
      setFieldValue("processSequenceProcessId", NO_PROCESS_SEQUENCE_PROCESS);
      setFieldValue("processSequenceMainMenuSequenceId", NO_PROCESS_MAIN_MENU_SEQUENCE);
    }

    if (sectionKey === "agendaReservations") {
      setFieldValue("agendaReservationsEnabled", true);
      setFieldValue("ordersEnabled", false);
      setFieldValue("cartEnabled", false);
      setFieldValue("productEnabled", false);
      setFieldValue("streamingServicesEnabled", false);
      setFieldValue("processSequenceEnabled", false);
      setFieldValue("agendaEnabled", false);
      setFieldValue("captureEnabled", false);
      setFieldValue("ordersMainMenuSequenceId", NO_ORDERS_MAIN_MENU_SEQUENCE);
      setFieldValue("cartMainMenuSequenceId", NO_CART_MAIN_MENU_SEQUENCE);
      setFieldValue("productId", NO_PRODUCT);
      setFieldValue("productIntroSequenceId", NO_PRODUCT_INTRO_SEQUENCE);
      setFieldValue("productMainMenuSequenceId", NO_PRODUCT_MAIN_MENU_SEQUENCE);
      setFieldValue(
        "streamingServicesMainMenuSequenceId",
        NO_STREAMING_SERVICES_MAIN_MENU_SEQUENCE
      );
      setFieldValue("processSequenceProcessId", NO_PROCESS_SEQUENCE_PROCESS);
      setFieldValue("processSequenceMainMenuSequenceId", NO_PROCESS_MAIN_MENU_SEQUENCE);
      setFieldValue("agendaServiceId", ALL_AGENDA_SERVICES);
      setFieldValue("agendaMainMenuSequenceId", NO_AGENDA_MAIN_MENU_SEQUENCE);
    }
  };

  const addConfigSection = (sectionKey, setFieldValue) => {
    applyConfigSectionDefaults(sectionKey, setFieldValue);
    setActiveConfigSections(prevSections => {
      const compatibleSections =
        sectionKey === "orders"
          ? prevSections.filter(activeSection => !["cart", "product", "streamingServices", "processes", "agenda", "agendaReservations"].includes(activeSection))
          : sectionKey === "cart"
          ? prevSections.filter(activeSection => !["orders", "product", "streamingServices", "processes", "agenda", "agendaReservations"].includes(activeSection))
          : sectionKey === "product"
          ? prevSections.filter(activeSection => !["orders", "cart", "streamingServices", "processes", "agenda", "agendaReservations"].includes(activeSection))
          : sectionKey === "streamingServices"
          ? prevSections.filter(activeSection => !["orders", "cart", "product", "processes", "agenda", "agendaReservations"].includes(activeSection))
          : sectionKey === "processes"
          ? prevSections.filter(activeSection => !["orders", "cart", "product", "streamingServices", "agenda", "agendaReservations"].includes(activeSection))
          : sectionKey === "agenda"
          ? prevSections.filter(activeSection => !["orders", "cart", "product", "streamingServices", "processes", "agendaReservations"].includes(activeSection))
          : sectionKey === "agendaReservations"
          ? prevSections.filter(activeSection => !["orders", "cart", "product", "streamingServices", "processes", "agenda"].includes(activeSection))
          : prevSections;

      return compatibleSections.includes(sectionKey)
        ? compatibleSections
        : [...compatibleSections, sectionKey];
    });
    setConfigMenuAnchor(null);
  };

  const resetConfigSectionValues = (sectionKey, setFieldValue) => {
    const resetValuesBySection = {
      chatbot: {
        trigger: "",
        matchType: "exact",
        requireAllTriggers: false,
        skipMarkAsRead: false,
        parentId: NO_PARENT_SEQUENCE,
        childSequenceIds: [],
        afterExecutionBehavior: "root",
        afterExecutionSequenceId: NO_AFTER_EXECUTION_SEQUENCE,
        noMatchMessage: "",
        noMatchAiEnabled: false,
        noMatchAiPrompt: "",
        captureEnabled: false,
        captureMode: "text",
        captureTargetType: "contact",
        captureContactField: "name",
        captureContactCustomField: "",
        captureProcessId: NO_CAPTURE_PROCESS,
        captureProcessFieldKey: NO_CAPTURE_PROCESS_FIELD,
        captureProcessFileKey: NO_CAPTURE_PROCESS_FILE,
        captureWasheetDocumentId: NO_CAPTURE_WASHEET_DOCUMENT,
        captureWasheetField: NO_CAPTURE_WASHEET_FIELD,
        captureProcessingMode: "direct",
        captureAiPrompt: "",
        captureOnTypeMismatch: "message",
        captureErrorMessage: "",
        chatbotAction: NO_CHATBOT_ACTION,
      },
      cart: {
        cartEnabled: false,
        cartMainMenuSequenceId: NO_CART_MAIN_MENU_SEQUENCE,
      },
      product: {
        productEnabled: false,
        productId: NO_PRODUCT,
        productIntroSequenceId: NO_PRODUCT_INTRO_SEQUENCE,
        productMainMenuSequenceId: NO_PRODUCT_MAIN_MENU_SEQUENCE,
      },
      orders: {
        ordersEnabled: false,
        ordersMainMenuSequenceId: NO_ORDERS_MAIN_MENU_SEQUENCE,
      },
      streamingServices: {
        streamingServicesEnabled: false,
        streamingServicesMainMenuSequenceId: NO_STREAMING_SERVICES_MAIN_MENU_SEQUENCE,
      },
      processes: {
        processSequenceEnabled: false,
        processSequenceProcessId: NO_PROCESS_SEQUENCE_PROCESS,
        processSequenceMainMenuSequenceId: NO_PROCESS_MAIN_MENU_SEQUENCE,
      },
      agenda: {
        agendaEnabled: false,
        agendaServiceId: ALL_AGENDA_SERVICES,
        agendaMainMenuSequenceId: NO_AGENDA_MAIN_MENU_SEQUENCE,
        agendaReservationStatus: "confirmed",
      },
      agendaReservations: {
        agendaReservationsEnabled: false,
        agendaReservationsMainMenuSequenceId: NO_AGENDA_RESERVATIONS_MAIN_MENU_SEQUENCE,
      },
      quickReply: {
        quickReplyEnabled: false,
        quickReplyMatchType: "exact",
        quickReplyTrigger: "",
      },
      schedule: {
        scheduleEnabled: false,
        scheduleDays: createScheduleDays(),
        outOfHoursSequenceId: NO_OUT_OF_HOURS_SEQUENCE,
      },
      labelCondition: {
        labelConditionType: LABEL_CONDITION_NONE,
        labelConditionIds: [],
      },
      execution: {
        executionMode: "unlimited",
        executionLimit: "",
        executionInterval: "",
        executionIntervalUnit: "hours",
      },
      transfer: {
        transferType: NO_TRANSFER_TYPE,
        transferQueueId: NO_TRANSFER_QUEUE,
        transferUserId: NO_TRANSFER_USER,
        transferAutoAssignEnabled: false,
        transferAutoAssignQueueIds: [],
        transferAutoAssignUserIds: [],
      },
      labels: {
        addLabelIds: [],
        removeLabelIds: [],
      },
      funnel: {
        funnelActionFunnelId: NO_FUNNEL_ACTION,
        funnelActionStageId: NO_FUNNEL_STAGE,
      },
      ai: {
        aiAction: NO_AI_ACTION,
        aiInitialMessage: "",
        aiPrompt: "",
        orchestratorModeEnabled: false,
        useSequencesTool: true,
        useWasheetsTool: canUseWasheets,
        useWebSearchTool: true,
        useAgendaTool: true,
        useOrdersTool: true,
        useWebhookTools: true,
        useContactTool: true,
        useProcessesTool: false,
        selectedProcessIds: [],
        naturalMessageModeEnabled: true,
      },
    };

    Object.entries(resetValuesBySection[sectionKey] || {}).forEach(
      ([fieldName, value]) => {
        setFieldValue(fieldName, value);
      }
    );
  };

  const removeConfigSection = (sectionKey, setFieldValue) => {
    setActiveConfigSections(prevSections =>
      prevSections.filter(activeSection => activeSection !== sectionKey)
    );
    resetConfigSectionValues(sectionKey, setFieldValue);
  };

  const loadWasheetHeaders = async documentId => {
    if (!documentId || documentId === NO_CAPTURE_WASHEET_DOCUMENT) return [];

    const document = washeetDocuments.find(
      item => Number(item.id) === Number(documentId)
    );
    if (!document) return [];

    const cacheKey = String(document.id);
    if (washeetHeadersByDocument[cacheKey]) {
      return washeetHeadersByDocument[cacheKey];
    }

    const { data } = await api.get("/washeets/read", {
      params: {
        spreadsheetId: document.spreadsheetId,
        sheetName: document.sheetName,
      },
    });
    const headers = (data.headers || []).filter(
      header => !["id registro", "chat id"].includes(String(header).trim().toLowerCase())
    );
    setWasheetHeadersByDocument(prev => ({
      ...prev,
      [cacheKey]: headers,
    }));
    return headers;
  };

  const renderContactOption = option => (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Avatar
        src={privacyCensorSettings.contactPhotos ? undefined : option?.profilePicUrl}
        className={privacyCensorSettings.contactPhotos ? classes.censoredPhoto : undefined}
        style={{ width: 28, height: 28 }}
      >
        {privacyCensorSettings.contactNames ? undefined : option?.name?.[0]}
      </Avatar>
      <div>
        <Typography
          variant="body2"
          className={privacyCensorSettings.contactNames ? classes.censoredText : undefined}
        >
          {privacyCensorSettings.contactNames
            ? "Contacto privado"
            : option?.name || "Sin nombre"}
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          className={privacyCensorSettings.contactNames ? classes.censoredText : undefined}
        >
          {privacyCensorSettings.contactNames
            ? "Telefono privado"
            : option?.number || ""}
        </Typography>
      </div>
    </div>
  );

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    Promise.all([
      fetchContactCustomFieldNames(),
      fetchContactCustomFieldDetails(),
      fetchWasheetMessageVariables(),
    ])
      .then(([fields, fieldDetails, variables]) => {
        if (isMounted.current) {
          setCustomFieldNames(fields);
          setCustomFieldDetails(fieldDetails);
          setWasheetVariables(variables);
        }
      })
      .catch(() => undefined);
  }, [open]);

  useEffect(() => {
    const normalizedSearch = contactSearch.trim();
    if (normalizedSearch.length < 2 && !open) return undefined;

    const delay = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts", {
          params: { searchParam: normalizedSearch, pageNumber: 1 },
        });
        if (isMounted.current) setContactOptions(data.contacts || []);
      } catch (err) {
        toastError(err);
      }
    }, 400);

    return () => clearTimeout(delay);
  }, [contactSearch, open]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const loadedSequences = await loadSequences();

        const [
          { data: queueData },
          { data: userData },
          labelResponse,
          { data: connectionData },
          funnelResponse,
          { data: processData },
          { data: agendaData },
          { data: washeetData },
          productResponse,
        ] = await Promise.all([
          api.get("/queue"),
          api.get("/users/", { params: { searchParam: "", pageNumber: 1 } }),
          canUseLabels ? api.get("/labels") : Promise.resolve({ data: [] }),
          api.get("/connections/"),
          canUseFunnels ? api.get("/funnels") : Promise.resolve({ data: { funnels: [] } }),
          canUseProcesses ? api.get("/ai-processes") : Promise.resolve({ data: { processes: [] } }),
          canUseAgenda ? api.get("/appointments/services") : Promise.resolve({ data: { services: [] } }),
          api.get("/washeets/documents"),
          canUseOrders && defaultConnectionId
            ? api.get("/orders/products", { params: { connectionId: defaultConnectionId } })
            : Promise.resolve({ data: { products: [] } }),
        ]);

        if (isMounted.current) {
          setQueues(queueData || []);
          setUsers(userData.users || []);
          setLabels(labelResponse.data || []);
          setConnections(connectionData || []);
          setFunnels(funnelResponse.data?.funnels || []);
          setAvailableProcesses(processData.processes || []);
          setAgendaServices(agendaData.services || []);
          setWasheetDocuments(washeetData.documents || []);
          setProducts(productResponse.data?.products || []);
        }

        const hasWasheetDocuments = (washeetData.documents || []).length > 0;

        if (!sequenceId) {
          const sharedConnectionIds = normalizeConnectionIds(defaultConnectionId);
          const nextSequence = {
            ...initialState,
            name: initialSequenceName || "",
            category: initialSequenceCategory || "General",
            connectionId: defaultConnectionId || "",
            sharedConnectionIds,
            childSequenceIds: [],
          };
          setSequence(nextSequence);
          setActiveConfigSections(
            Array.from(
              new Set([
                ...defaultConfigSections,
                ...getInitialConfigSections(
                  nextSequence,
                  canUseLabels,
                  canUseFunnels,
                  canUseAi,
                  canUseOrders,
                  canUseProcesses
                ),
              ])
            )
          );
          setBlocks([createBlock()]);
          return;
        }

        const { data } = await api.get(`/chatbot/sequences/${sequenceId}`);
        const schedule = data.schedule || {};
        const captureConfig = data.captureConfig || {};
        const cartConfig = data.cartConfig || {};
        const productConfig = data.productConfig || {};
        const ordersConfig = data.ordersConfig || {};
        const streamingServicesConfig = data.streamingServicesConfig || {};
        const processSequenceConfig = data.processSequenceConfig || {};
        const agendaConfig = data.agendaConfig || {};
        const agendaReservationsConfig = data.agendaReservationsConfig || {};
        const captureWasheetDocumentId =
          captureConfig.washeetDocumentId || NO_CAPTURE_WASHEET_DOCUMENT;
        const captureWasheetDocument = (washeetData.documents || []).find(
          document => Number(document.id) === Number(captureWasheetDocumentId)
        );
        if (captureWasheetDocument) {
          try {
            const { data: sheetData } = await api.get("/washeets/read", {
              params: {
                spreadsheetId: captureWasheetDocument.spreadsheetId,
                sheetName: captureWasheetDocument.sheetName,
              },
            });
            const headers = (sheetData.headers || []).filter(
              header =>
                !["id registro", "chat id"].includes(
                  String(header).trim().toLowerCase()
                )
            );
            if (isMounted.current) {
              setWasheetHeadersByDocument(prev => ({
                ...prev,
                [String(captureWasheetDocument.id)]: headers,
              }));
            }
          } catch (err) {
            toastError(err);
          }
        }
        const sharedConnectionIds = normalizeConnectionIds(
          data.sharedConnectionIds,
          data.connectionId,
          defaultConnectionId
        );
        if (isMounted.current) {
          const nextSequence = {
            ...initialState,
            ...data,
            category: data.category || "General",
            matchType:
              !canUseTickets && data.matchType === "newChat"
                ? initialState.matchType
                : data.matchType || initialState.matchType,
            requireAllTriggers: data.requireAllTriggers === true,
            skipMarkAsRead: data.skipMarkAsRead === true,
            quickReplyEnabled: data.quickReplyEnabled === true,
            quickReplyMatchType: data.quickReplyMatchType || "exact",
            quickReplyTrigger: data.quickReplyTrigger || "",
            connectionId: data.connectionId || sharedConnectionIds[0] || "",
            sharedConnectionIds,
            parentId: data.parentId || NO_PARENT_SEQUENCE,
            executionLimit: data.executionLimit || "",
            executionInterval: data.executionInterval || "",
            transferType: data.transferType || NO_TRANSFER_TYPE,
            transferQueueId: data.transferQueueId || NO_TRANSFER_QUEUE,
            transferUserId: data.transferUserId || NO_TRANSFER_USER,
            transferAutoAssignEnabled: data.transferAutoAssignEnabled === true,
            transferAutoAssignQueueIds: normalizeConnectionIds(data.transferAutoAssignQueueIds),
            transferAutoAssignUserIds: normalizeConnectionIds(data.transferAutoAssignUserIds),
            labelConditionType: data.labelConditionType || LABEL_CONDITION_NONE,
            labelConditionIds: data.labelConditionIds || [],
            addLabelIds: data.addLabelIds || [],
            removeLabelIds: data.removeLabelIds || [],
            aiAction: data.aiAction || NO_AI_ACTION,
            chatbotAction: data.chatbotAction || NO_CHATBOT_ACTION,
            funnelActionFunnelId: data.funnelActionFunnelId || NO_FUNNEL_ACTION,
            funnelActionStageId: data.funnelActionStageId || NO_FUNNEL_STAGE,
            aiInitialMessage: data.aiInitialMessage || "",
            aiPrompt: data.aiPrompt || "",
            orchestratorModeEnabled: data.orchestratorModeEnabled === true,
            useSequencesTool: data.useSequencesTool !== false,
            useWasheetsTool: hasWasheetDocuments && data.useWasheetsTool !== false,
            useWebSearchTool: data.useWebSearchTool !== false,
            useAgendaTool: canUseAgenda && data.useAgendaTool !== false,
            useOrdersTool: canUseOrders && data.useOrdersTool !== false,
            useWebhookTools: canUseWebhooks && data.useWebhookTools !== false,
            useContactTool: data.useContactTool !== false,
            useProcessesTool: canUseProcesses && data.useProcessesTool === true,
            selectedProcessIds: canUseProcesses
              ? normalizeConnectionIds(data.selectedProcessIds)
              : [],
            naturalMessageModeEnabled: data.naturalMessageModeEnabled !== false,
            outOfHoursSequenceId: data.outOfHoursSequenceId || NO_OUT_OF_HOURS_SEQUENCE,
            noMatchMessage: data.noMatchMessage || "",
            noMatchAiEnabled: data.noMatchAiEnabled === true,
            noMatchAiPrompt: data.noMatchAiPrompt || "",
            captureEnabled: captureConfig.enabled === true,
            captureMode: captureConfig.mode || "text",
            captureTargetType:
              canUseProcesses ||
              !["process_field", "process_file"].includes(captureConfig.targetType)
                ? captureConfig.targetType || "contact"
                : "contact",
            captureContactField: captureConfig.contactField || "name",
            captureContactCustomField: captureConfig.contactCustomField || "",
            captureProcessId: canUseProcesses
              ? captureConfig.processId || NO_CAPTURE_PROCESS
              : NO_CAPTURE_PROCESS,
            captureProcessFieldKey:
              canUseProcesses
                ? captureConfig.processFieldKey || NO_CAPTURE_PROCESS_FIELD
                : NO_CAPTURE_PROCESS_FIELD,
            captureProcessFileKey:
              canUseProcesses
                ? captureConfig.processFileKey || NO_CAPTURE_PROCESS_FILE
                : NO_CAPTURE_PROCESS_FILE,
            captureWasheetDocumentId,
            captureWasheetField:
              captureConfig.washeetField || NO_CAPTURE_WASHEET_FIELD,
            captureProcessingMode: captureConfig.processingMode || "direct",
            captureAiPrompt: captureConfig.aiPrompt || "",
            captureOnTypeMismatch: captureConfig.onTypeMismatch || "message",
            captureErrorMessage: captureConfig.errorMessage || "",
            cartEnabled: cartConfig.enabled === true,
            cartMainMenuSequenceId:
              cartConfig.mainMenuSequenceId ||
              NO_CART_MAIN_MENU_SEQUENCE,
            productEnabled: productConfig.enabled === true,
            productId: productConfig.productId || NO_PRODUCT,
            productIntroSequenceId:
              productConfig.introSequenceId ||
              NO_PRODUCT_INTRO_SEQUENCE,
            productMainMenuSequenceId:
              productConfig.mainMenuSequenceId ||
              NO_PRODUCT_MAIN_MENU_SEQUENCE,
            ordersEnabled: ordersConfig.enabled === true,
            ordersMainMenuSequenceId:
              ordersConfig.mainMenuSequenceId ||
              NO_ORDERS_MAIN_MENU_SEQUENCE,
            streamingServicesEnabled: streamingServicesConfig.enabled === true,
            streamingServicesMainMenuSequenceId:
              streamingServicesConfig.mainMenuSequenceId ||
              NO_STREAMING_SERVICES_MAIN_MENU_SEQUENCE,
            processSequenceEnabled: canUseProcesses && processSequenceConfig.enabled === true,
            processSequenceProcessId:
              canUseProcesses
                ? processSequenceConfig.processId || NO_PROCESS_SEQUENCE_PROCESS
                : NO_PROCESS_SEQUENCE_PROCESS,
            processSequenceMainMenuSequenceId:
              canUseProcesses
                ? processSequenceConfig.mainMenuSequenceId ||
                  NO_PROCESS_MAIN_MENU_SEQUENCE
                : NO_PROCESS_MAIN_MENU_SEQUENCE,
            agendaEnabled: agendaConfig.enabled === true,
            agendaServiceId:
              agendaConfig.serviceId || ALL_AGENDA_SERVICES,
            agendaMainMenuSequenceId:
              agendaConfig.mainMenuSequenceId ||
              NO_AGENDA_MAIN_MENU_SEQUENCE,
            agendaReservationStatus: agendaConfig.reservationStatus || "confirmed",
            agendaReservationsEnabled: agendaReservationsConfig.enabled === true,
            agendaReservationsMainMenuSequenceId:
              agendaReservationsConfig.mainMenuSequenceId ||
              NO_AGENDA_RESERVATIONS_MAIN_MENU_SEQUENCE,
            afterExecutionBehavior: data.afterExecutionBehavior || "root",
            afterExecutionSequenceId:
              data.afterExecutionSequenceId || NO_AFTER_EXECUTION_SEQUENCE,
            childSequenceIds: loadedSequences
              .filter(item => Number(item.parentId) === Number(data.id))
              .map(item => item.id),
            scheduleDays: normalizeScheduleDays(schedule.days),
          };
          setSequence(nextSequence);
          const inferredConfigSections = getInitialConfigSections(
            nextSequence,
            canUseLabels,
            canUseFunnels,
            canUseAi,
            canUseOrders,
            canUseProcesses
          );
          setActiveConfigSections(
            normalizeConfigSectionOrder(
              data.configSectionOrder,
              inferredConfigSections
            )
          );
          setBlocks(
            data.blocks?.length
              ? data.blocks.map(block => ({
                  type: block.type || "message",
                  text: block.text || "",
                  delaySeconds: clampDelaySeconds(block.delaySeconds),
                  inactivityValue: Number(block.inactivityValue || 5),
                  inactivityUnit: block.inactivityUnit || "minutes",
                  inactivitySequenceId: block.inactivitySequenceId || NO_INACTIVITY_SEQUENCE,
                  followupValue: Number(block.followupValue || 1),
                  followupUnit: block.followupUnit || "days",
                  followupMode: block.followupMode || "normal",
                  followupSequenceId: block.followupSequenceId || NO_FOLLOWUP_SEQUENCE,
                  followupCancelSequenceId: block.followupCancelSequenceId || NO_FOLLOWUP_CANCEL_SEQUENCE,
                  followupConditions: normalizeFollowupConditions(block.followupConditions),
                  notifyContactId: block.notifyContactId || "",
                  notifyContact: block.notifyContact || null,
                  pollTitle: block.pollTitle || "",
                  pollOptions: block.pollOptions || "",
                  pollAllowMultiple: getPollAllowMultiple(block),
                  pollSelectableCount: Number(block.pollSelectableCount || (getPollAllowMultiple(block) ? 2 : 1)),
                  listTitle: block.listTitle || "",
                  listBody: block.listBody || "",
                  listButtonText: block.listButtonText || "Ver opciones",
                  listFooter: block.listFooter || "",
                  listSections: normalizeListSections(block),
                  contactName: block.contactName || "",
                  contactPhone: block.contactPhone || "",
                  locationLatitude: block.locationLatitude ?? "",
                  locationLongitude: block.locationLongitude ?? "",
                  locationName: block.locationName || "",
                  locationAddress: block.locationAddress || "",
                  file: null,
                  media: block.media || null,
                  mediaTranscriptionEnabled: Boolean(block.mediaTranscriptionEnabled),
                  mediaTranscriptionText: block.mediaTranscriptionText || "",
                }))
              : [createBlock()]
          );
        }
      } catch (err) {
        toastError(err);
      }
    };

    if (open) fetchData();
  }, [
    open,
    sequenceId,
    defaultConnectionId,
    initialSequenceName,
    initialSequenceCategory,
    canUseLabels,
    canUseTickets,
    canUseFunnels,
    canUseAi,
    canUseOrders,
    canUseProcesses,
    canUseAgenda
  ]);

  useEffect(() => {
    const fetchProducts = async () => {
      if (!open || !canUseOrders || !sequence.connectionId) {
        if (isMounted.current) setProducts([]);
        return;
      }

      try {
        const { data } = await api.get("/orders/products", {
          params: { connectionId: sequence.connectionId },
        });
        if (isMounted.current) setProducts(data.products || []);
      } catch (err) {
        toastError(err);
      }
    };

    fetchProducts();
  }, [open, canUseOrders, sequence.connectionId]);

  const loadSequences = async () => {
    const { data } = await api.get("/chatbot/sequences-flow");
    const loadedSequences = data.sequences || [];
    if (isMounted.current) {
      setSequences(loadedSequences);
    }
    return loadedSequences;
  };

  const getFunnelStages = funnelId => {
    const selectedFunnel = funnels.find(
      funnel => String(funnel.id) === String(funnelId)
    );
    return selectedFunnel?.stages || [];
  };

  const handleClose = () => {
    onClose();
    setSequence(initialState);
    setBlocks([createBlock()]);
    setConfigMenuAnchor(null);
    setActiveConfigSections([]);
  };

  const updateBlock = (index, patch) => {
    setBlocks(prevBlocks =>
      prevBlocks.map((block, blockIndex) =>
        blockIndex === index ? { ...block, ...patch } : block
      )
    );
  };

  const updateListSection = (blockIndex, sectionIndex, patch) => {
    setBlocks(prevBlocks =>
      prevBlocks.map((block, currentBlockIndex) => {
        if (currentBlockIndex !== blockIndex) return block;

        const sections = normalizeListSections(block).map((section, currentSectionIndex) =>
          currentSectionIndex === sectionIndex ? { ...section, ...patch } : section
        );

        return { ...block, listSections: sections };
      })
    );
  };

  const updateListRow = (blockIndex, sectionIndex, rowIndex, patch) => {
    setBlocks(prevBlocks =>
      prevBlocks.map((block, currentBlockIndex) => {
        if (currentBlockIndex !== blockIndex) return block;

        const sections = normalizeListSections(block).map((section, currentSectionIndex) => {
          if (currentSectionIndex !== sectionIndex) return section;

          return {
            ...section,
            rows: section.rows.map((row, currentRowIndex) =>
              currentRowIndex === rowIndex ? { ...row, ...patch } : row
            ),
          };
        });

        return { ...block, listSections: sections };
      })
    );
  };

  const addListSection = blockIndex => {
    setBlocks(prevBlocks =>
      prevBlocks.map((block, currentBlockIndex) =>
        currentBlockIndex === blockIndex
          ? { ...block, listSections: [...normalizeListSections(block), createListSection()] }
          : block
      )
    );
  };

  const removeListSection = (blockIndex, sectionIndex) => {
    setBlocks(prevBlocks =>
      prevBlocks.map((block, currentBlockIndex) => {
        if (currentBlockIndex !== blockIndex) return block;

        const sections = normalizeListSections(block).filter(
          (_section, currentSectionIndex) => currentSectionIndex !== sectionIndex
        );

        return { ...block, listSections: sections.length ? sections : [createListSection()] };
      })
    );
  };

  const addListRow = (blockIndex, sectionIndex) => {
    setBlocks(prevBlocks =>
      prevBlocks.map((block, currentBlockIndex) => {
        if (currentBlockIndex !== blockIndex) return block;

        const sections = normalizeListSections(block).map((section, currentSectionIndex) =>
          currentSectionIndex === sectionIndex
            ? { ...section, rows: [...section.rows, createListRow()] }
            : section
        );

        return { ...block, listSections: sections };
      })
    );
  };

  const removeListRow = (blockIndex, sectionIndex, rowIndex) => {
    setBlocks(prevBlocks =>
      prevBlocks.map((block, currentBlockIndex) => {
        if (currentBlockIndex !== blockIndex) return block;

        const sections = normalizeListSections(block).map((section, currentSectionIndex) => {
          if (currentSectionIndex !== sectionIndex) return section;

          const rows = section.rows.filter(
            (_row, currentRowIndex) => currentRowIndex !== rowIndex
          );

          return { ...section, rows: rows.length ? rows : [createListRow()] };
        });

        return { ...block, listSections: sections };
      })
    );
  };

  const moveBlock = (index, direction) => {
    setBlocks(prevBlocks => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= prevBlocks.length) return prevBlocks;

      const nextBlocks = [...prevBlocks];
      const current = nextBlocks[index];
      nextBlocks[index] = nextBlocks[nextIndex];
      nextBlocks[nextIndex] = current;
      return nextBlocks;
    });
  };

  const removeBlock = index => {
    setBlocks(prevBlocks =>
      prevBlocks.length === 1
        ? [createBlock()]
        : prevBlocks.filter((_, blockIndex) => blockIndex !== index)
    );
  };

  const addVariableToBlock = (index, variable) => {
    setBlocks(prevBlocks =>
      prevBlocks.map((block, blockIndex) => {
        if (blockIndex !== index) return block;
        const separator = block.text?.trim() ? " " : "";
        return { ...block, text: `${block.text || ""}${separator}${variable}` };
      })
    );
  };

  const openCreateSequence = (blockIndex, connectionId, target = "block", applySelection = null) => {
    setSequenceModalBlockIndex(blockIndex);
    setSequenceModalConnectionId(connectionId || sequence.connectionId || defaultConnectionId || "");
    setSequenceModalTarget(target);
    sequenceModalApplySelection.current = applySelection;
    setSequenceModalId(null);
    setSequenceModalOpen(true);
  };

  const openEditSequence = (blockIndex, sequenceToEditId, connectionId, target = "block", applySelection = null) => {
    if (!sequenceToEditId) return;
    setSequenceModalBlockIndex(blockIndex);
    setSequenceModalConnectionId(connectionId || sequence.connectionId || defaultConnectionId || "");
    setSequenceModalTarget(target);
    sequenceModalApplySelection.current = applySelection;
    setSequenceModalId(sequenceToEditId);
    setSequenceModalOpen(true);
  };

  const handleSequenceSaved = async savedSequence => {
    const savedSequenceId = savedSequence?.id || sequenceModalId;
    const nextSequences = await loadSequences();

    if (
      savedSequence &&
      savedSequenceId &&
      !nextSequences.some(item => String(item.id) === String(savedSequenceId))
    ) {
      setSequences(prevSequences => [...prevSequences, savedSequence]);
    }

    if (sequenceModalApplySelection.current && savedSequenceId) {
      sequenceModalApplySelection.current(String(savedSequenceId));
      handleSequenceModalClose();
      return;
    }

    if (sequenceModalTarget === "block" && sequenceModalBlockIndex !== null && savedSequenceId) {
      updateBlock(sequenceModalBlockIndex, {
        inactivitySequenceId: String(savedSequenceId),
      });
    }

    if (sequenceModalTarget === "followup" && sequenceModalBlockIndex !== null && savedSequenceId) {
      updateBlock(sequenceModalBlockIndex, {
        followupSequenceId: String(savedSequenceId),
      });
    }

    if (sequenceModalTarget === "followupCancel" && sequenceModalBlockIndex !== null && savedSequenceId) {
      updateBlock(sequenceModalBlockIndex, {
        followupCancelSequenceId: String(savedSequenceId),
      });
    }

    handleSequenceModalClose();
  };

  const handleSequenceModalClose = () => {
    setSequenceModalOpen(false);
    setSequenceModalId(null);
    setSequenceModalBlockIndex(null);
    setSequenceModalConnectionId("");
    setSequenceModalTarget("block");
    sequenceModalApplySelection.current = null;
  };

  const renderInactivitySequenceSelector = (block, index, values) => {
    const selectedSequenceId = block.inactivitySequenceId || NO_INACTIVITY_SEQUENCE;

    return (
      <>
        <FormControl variant="outlined" className={classes.formControl}>
          <InputLabel>{i18n.t("chatbotModal.form.inactivitySequence")}</InputLabel>
          <Select
            value={selectedSequenceId}
            label={i18n.t("chatbotModal.form.inactivitySequence")}
            MenuProps={sequenceSelectMenuProps}
            onChange={event => {
              if (event.target.value === "__create__") {
                openCreateSequence(index, values.connectionId);
                return;
              }

              updateBlock(index, { inactivitySequenceId: event.target.value });
            }}
          >
            <MenuItem value={NO_INACTIVITY_SEQUENCE}>
              {i18n.t("chatbotModal.form.noInactivitySequence")}
            </MenuItem>
            <MenuItem value="__create__">Crear secuencia...</MenuItem>
            {sequences
              .filter(
                item =>
                  item.id !== sequenceId &&
                  recordOwnsWhatsapp(item, values.connectionId)
              )
              .map(item => (
                <MenuItem key={item.id} value={String(item.id)}>
                  {formatSequenceOption(item)}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <div className={classes.sequenceActions}>
          <Button
            color="primary"
            disabled={selectedSequenceId === NO_INACTIVITY_SEQUENCE}
            onClick={() =>
              openEditSequence(index, selectedSequenceId, values.connectionId)
            }
          >
            Editar secuencia
          </Button>
        </div>
      </>
    );
  };

  const renderFollowupSequenceSelector = (block, index, values) => {
    const selectedSequenceId = block.followupSequenceId || NO_FOLLOWUP_SEQUENCE;

    return (
      <>
        <FormControl variant="outlined" className={classes.formControl}>
          <InputLabel>Secuencia a ejecutar</InputLabel>
          <Select
            value={selectedSequenceId}
            label="Secuencia a ejecutar"
            MenuProps={sequenceSelectMenuProps}
            onChange={event => {
              if (event.target.value === "__create__") {
                openCreateSequence(index, values.connectionId, "followup");
                return;
              }

              updateBlock(index, { followupSequenceId: event.target.value });
            }}
          >
            <MenuItem value={NO_FOLLOWUP_SEQUENCE}>
              No ejecutar secuencia
            </MenuItem>
            <MenuItem value="__create__">Crear secuencia...</MenuItem>
            {sequences
              .filter(
                item =>
                  item.id !== sequenceId &&
                  recordOwnsWhatsapp(item, values.connectionId)
              )
              .map(item => (
                <MenuItem key={item.id} value={String(item.id)}>
                  {formatSequenceOption(item)}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <div className={classes.sequenceActions}>
          <Button
            color="primary"
            disabled={selectedSequenceId === NO_FOLLOWUP_SEQUENCE}
            onClick={() =>
              openEditSequence(index, selectedSequenceId, values.connectionId, "followup")
            }
          >
            Editar secuencia
          </Button>
        </div>
      </>
    );
  };

  const renderFollowupConditionSequenceSelector = (
    block,
    blockIndex,
    condition,
    conditionIndex,
    values
  ) => {
    const selectedSequenceId =
      condition.sequenceId || NO_FOLLOWUP_CONDITION_SEQUENCE;
    const updateCondition = patch => {
      const nextConditions = normalizeFollowupConditions(block.followupConditions);
      nextConditions[conditionIndex] = {
        ...nextConditions[conditionIndex],
        ...patch,
      };
      updateBlock(blockIndex, { followupConditions: nextConditions });
    };

    return (
      <>
        <FormControl variant="outlined" className={classes.formControl}>
          <InputLabel>Secuencia</InputLabel>
          <Select
            value={selectedSequenceId}
            label="Secuencia"
            MenuProps={sequenceSelectMenuProps}
            onChange={event => {
              if (event.target.value === "__create__") {
                openCreateSequence(
                  blockIndex,
                  values.connectionId,
                  "followupCondition",
                  sequenceId => updateCondition({ sequenceId })
                );
                return;
              }

              updateCondition({ sequenceId: event.target.value });
            }}
          >
            <MenuItem value={NO_FOLLOWUP_CONDITION_SEQUENCE}>
              No ejecutar secuencia
            </MenuItem>
            <MenuItem value="__create__">Crear secuencia...</MenuItem>
            {sequences
              .filter(
                item =>
                  item.id !== sequenceId &&
                  recordOwnsWhatsapp(item, values.connectionId)
              )
              .map(item => (
                <MenuItem key={item.id} value={String(item.id)}>
                  {formatSequenceOption(item)}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <div className={classes.sequenceActions}>
          <Button
            color="primary"
            disabled={selectedSequenceId === NO_FOLLOWUP_CONDITION_SEQUENCE}
            onClick={() =>
              openEditSequence(
                blockIndex,
                selectedSequenceId,
                values.connectionId,
                "followupCondition",
                sequenceId => updateCondition({ sequenceId })
              )
            }
          >
            Editar secuencia
          </Button>
        </div>
      </>
    );
  };

  const renderFollowupConditions = (block, index, values) => {
    const conditions = normalizeFollowupConditions(block.followupConditions);
    const contactFieldOptions = [
      { value: "name", label: "Nombre" },
      { value: "number", label: "Telefono" },
      { value: "email", label: "Email" },
      ...customFieldNames.map(fieldName => ({
        value: `custom:${fieldName}`,
        label: fieldName,
      })),
    ];
    const conditionTypes = [
      ...(canUseLabels ? [{ value: "label", label: "Etiqueta" }] : []),
      ...(canUseFunnels ? [{ value: "funnel", label: "Embudo" }] : []),
      ...(isNormalAssignmentMode
        ? []
        : [{ value: "queue", label: "Area/Departamento" }]),
      { value: "contact", label: "Datos de contacto" },
    ];

    const updateCondition = (conditionIndex, patch) => {
      const nextConditions = [...conditions];
      nextConditions[conditionIndex] = {
        ...nextConditions[conditionIndex],
        ...patch,
      };
      updateBlock(index, { followupConditions: nextConditions });
    };

    const createAvailableCondition = () => ({
      ...createFollowupCondition(),
      type: conditionTypes[0]?.value || "contact",
    });

    const addCondition = () => {
      if (conditions.length >= 2) return;
      updateBlock(index, {
        followupConditions: [...conditions, createAvailableCondition()],
      });
    };

    const removeCondition = conditionIndex => {
      updateBlock(index, {
        followupConditions:
          conditions.length === 1
            ? [createAvailableCondition()]
            : conditions.filter((_, itemIndex) => itemIndex !== conditionIndex),
      });
    };

    return (
      <Grid item xs={12}>
        {conditions.map((condition, conditionIndex) => {
          const conditionType = conditionTypes.some(
            type => type.value === condition.type
          )
            ? condition.type
            : conditionTypes[0]?.value || "contact";

          return (
            <Paper
              key={conditionIndex}
              elevation={0}
              className={classes.block}
            >
            <div className={classes.blockHeader}>
              <Typography variant="subtitle2">
                Condicion {conditionIndex + 1}
              </Typography>
              <IconButton
                size="small"
                onClick={() => removeCondition(conditionIndex)}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </div>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={conditionType}
                    label="Tipo"
                    onChange={event =>
                      updateCondition(conditionIndex, {
                        ...createAvailableCondition(),
                        type: event.target.value,
                      })
                    }
                  >
                    {conditionTypes.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel>Operador</InputLabel>
                  <Select
                    value={condition.operator || "is"}
                    label="Operador"
                    onChange={event =>
                      updateCondition(conditionIndex, {
                        operator: event.target.value,
                      })
                    }
                  >
                    <MenuItem value="is">Es</MenuItem>
                    <MenuItem value="isNot">No es</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {conditionType === "funnel" ? (
                <>
                  <Grid item xs={12} md={3}>
                    <FormControl variant="outlined" className={classes.formControl}>
                      <InputLabel>Embudo</InputLabel>
                      <Select
                        value={condition.funnelId || NO_FUNNEL_ACTION}
                        label="Embudo"
                        onChange={event =>
                          updateCondition(conditionIndex, {
                            funnelId: event.target.value,
                            funnelStageId: NO_FUNNEL_STAGE,
                          })
                        }
                      >
                        <MenuItem value={NO_FUNNEL_ACTION}>Selecciona embudo</MenuItem>
                        {funnels.map(funnel => (
                          <MenuItem key={funnel.id} value={funnel.id}>
                            {funnel.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl variant="outlined" className={classes.formControl}>
                      <InputLabel>Etapa</InputLabel>
                      <Select
                        value={condition.funnelStageId || NO_FUNNEL_STAGE}
                        label="Etapa"
                        disabled={condition.funnelId === NO_FUNNEL_ACTION}
                        onChange={event =>
                          updateCondition(conditionIndex, {
                            funnelStageId: event.target.value,
                          })
                        }
                      >
                        <MenuItem value={NO_FUNNEL_STAGE}>Selecciona etapa</MenuItem>
                        {getFunnelStages(condition.funnelId).map(stage => (
                          <MenuItem key={stage.id} value={stage.id}>
                            {stage.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              ) : conditionType === "label" ? (
                <Grid item xs={12} md={4}>
                  <FormControl variant="outlined" className={classes.formControl}>
                    <InputLabel>Etiqueta</InputLabel>
                    <Select
                      value={condition.labelId || NO_FOLLOWUP_CONDITION_LABEL}
                      label="Etiqueta"
                      onChange={event =>
                        updateCondition(conditionIndex, {
                          labelId:
                            event.target.value === NO_FOLLOWUP_CONDITION_LABEL
                              ? ""
                              : event.target.value,
                        })
                      }
                    >
                      <MenuItem value={NO_FOLLOWUP_CONDITION_LABEL}>Selecciona etiqueta</MenuItem>
                      {labels
                        .filter(label => recordOwnsWhatsapp(label, values.connectionId))
                        .map(label => (
                          <MenuItem key={label.id} value={label.id}>
                            {label.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              ) : conditionType === "queue" ? (
                <Grid item xs={12} md={4}>
                  <FormControl variant="outlined" className={classes.formControl}>
                    <InputLabel>Area/departamento</InputLabel>
                    <Select
                      value={condition.queueId || NO_FOLLOWUP_CONDITION_QUEUE}
                      label="Area/departamento"
                      onChange={event =>
                        updateCondition(conditionIndex, {
                          queueId:
                            event.target.value === NO_FOLLOWUP_CONDITION_QUEUE
                              ? ""
                              : event.target.value,
                        })
                      }
                    >
                      <MenuItem value={NO_FOLLOWUP_CONDITION_QUEUE}>Selecciona area</MenuItem>
                      {queues
                        .filter(queue =>
                          recordOwnsWhatsapp(
                            { ...queue, connectionId: queue.connectionId || queue.connection?.id },
                            values.connectionId
                          )
                        )
                        .map(queue => (
                          <MenuItem key={queue.id} value={queue.id}>
                            {queue.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} md={3}>
                    <FormControl variant="outlined" className={classes.formControl}>
                      <InputLabel>Dato</InputLabel>
                      <Select
                        value={condition.contactField || "name"}
                        label="Dato"
                        onChange={event =>
                          updateCondition(conditionIndex, {
                            contactField: event.target.value,
                          })
                        }
                      >
                        {contactFieldOptions.map(field => (
                          <MenuItem key={field.value} value={field.value}>
                            {field.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Valor"
                      value={condition.contactValue || ""}
                      onChange={event =>
                        updateCondition(conditionIndex, {
                          contactValue: event.target.value,
                        })
                      }
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                {renderFollowupConditionSequenceSelector(
                  block,
                  index,
                  condition,
                  conditionIndex,
                  values
                )}
              </Grid>
            </Grid>
          </Paper>
          );
        })}
        <Button
          color="primary"
          startIcon={<Add />}
          disabled={conditions.length >= 2}
          onClick={addCondition}
        >
          Agregar condicion
        </Button>
      </Grid>
    );
  };

  const renderFollowupCancelSequenceSelector = (block, index, values) => {
    const selectedSequenceId =
      block.followupCancelSequenceId || NO_FOLLOWUP_CANCEL_SEQUENCE;

    return (
      <>
        <FormControl variant="outlined" className={classes.formControl}>
          <InputLabel>Secuencia que cancela los seguimientos</InputLabel>
          <Select
            value={selectedSequenceId}
            label="Secuencia que cancela los seguimientos"
            MenuProps={sequenceSelectMenuProps}
            onChange={event => {
              if (event.target.value === "__create__") {
                openCreateSequence(index, values.connectionId, "followupCancel");
                return;
              }

              updateBlock(index, { followupCancelSequenceId: event.target.value });
            }}
          >
            <MenuItem value={NO_FOLLOWUP_CANCEL_SEQUENCE}>
              No cancelar por secuencia
            </MenuItem>
            <MenuItem value="__create__">Crear secuencia...</MenuItem>
            {sequences
              .filter(
                item =>
                  item.id !== sequenceId &&
                  recordOwnsWhatsapp(item, values.connectionId)
              )
              .map(item => (
                <MenuItem key={item.id} value={String(item.id)}>
                  {formatSequenceOption(item)}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <div className={classes.sequenceActions}>
          <Button
            color="primary"
            disabled={selectedSequenceId === NO_FOLLOWUP_CANCEL_SEQUENCE}
            onClick={() =>
              openEditSequence(index, selectedSequenceId, values.connectionId, "followupCancel")
            }
          >
            Editar secuencia
          </Button>
        </div>
      </>
    );
  };

  const renderOutOfHoursSequenceSelector = (values, setFieldValue) => {
    const selectedSequenceId = values.outOfHoursSequenceId || NO_OUT_OF_HOURS_SEQUENCE;

    return (
      <>
        <FormControl variant="outlined" className={classes.formControl}>
          <InputLabel>{i18n.t("chatbotModal.form.outOfHoursSequence")}</InputLabel>
          <Select
            value={selectedSequenceId}
            label={i18n.t("chatbotModal.form.outOfHoursSequence")}
            MenuProps={sequenceSelectMenuProps}
            onChange={event => {
              if (event.target.value === "__create__") {
                openCreateSequence(
                  null,
                  values.connectionId,
                  "outOfHours",
                  sequenceId => setFieldValue("outOfHoursSequenceId", sequenceId)
                );
                return;
              }

              setFieldValue("outOfHoursSequenceId", event.target.value);
            }}
          >
            <MenuItem value={NO_OUT_OF_HOURS_SEQUENCE}>
              {i18n.t("chatbotModal.form.noOutOfHoursSequence")}
            </MenuItem>
            <MenuItem value="__create__">Crear secuencia...</MenuItem>
            {sequences
              .filter(
                item =>
                  item.id !== sequenceId &&
                  recordOwnsWhatsapp(item, values.connectionId)
              )
              .map(item => (
                <MenuItem key={item.id} value={String(item.id)}>
                  {formatSequenceOption(item)}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <div className={classes.sequenceActions}>
          <Button
            color="primary"
            disabled={selectedSequenceId === NO_OUT_OF_HOURS_SEQUENCE}
            onClick={() =>
              openEditSequence(
                null,
                selectedSequenceId,
                values.connectionId,
                "outOfHours",
                sequenceId => setFieldValue("outOfHoursSequenceId", sequenceId)
              )
            }
          >
            Editar secuencia
          </Button>
        </div>
      </>
    );
  };

  const buildFormData = values => {
    const normalizedBlocks = blocks.map(block => ({
      type: block.type || "message",
      text: block.text || "",
      delaySeconds: clampDelaySeconds(block.delaySeconds),
      inactivityValue: Number(block.inactivityValue || 0),
      inactivityUnit: block.inactivityUnit || "",
      inactivitySequenceId: toOptionalApiValue(block.inactivitySequenceId, NO_INACTIVITY_SEQUENCE),
      followupValue: Number(block.followupValue || 0),
      followupUnit: block.followupUnit || "",
      followupMode: block.followupMode || "normal",
      followupSequenceId: toOptionalApiValue(block.followupSequenceId, NO_FOLLOWUP_SEQUENCE),
      followupCancelSequenceId: toOptionalApiValue(
        block.followupCancelSequenceId,
        NO_FOLLOWUP_CANCEL_SEQUENCE
      ),
      followupConditions: normalizeFollowupConditions(block.followupConditions)
        .filter(
          condition => !(isNormalAssignmentMode && condition.type === "queue")
        )
        .map(condition => ({
          type: condition.type || "label",
          operator: condition.operator === "isNot" ? "isNot" : "is",
          labelId: condition.labelId || "",
          funnelId: toOptionalApiValue(condition.funnelId, NO_FUNNEL_ACTION),
          funnelStageId: toOptionalApiValue(condition.funnelStageId, NO_FUNNEL_STAGE),
          queueId: condition.queueId || "",
          contactField: condition.contactField || "name",
          contactValue: condition.contactValue || "",
          sequenceId: toOptionalApiValue(
            condition.sequenceId,
            NO_FOLLOWUP_CONDITION_SEQUENCE
          ),
        })),
      notifyContactId: block.notifyContactId || "",
      notifyContact: block.notifyContact || null,
      pollTitle: block.pollTitle || "",
      pollOptions: block.pollOptions || "",
      pollAllowMultiple: getPollAllowMultiple(block),
      pollSelectableCount: getPollAllowMultiple(block)
        ? Number(block.pollSelectableCount || 2)
        : 1,
      listTitle: block.listTitle || "",
      listBody: block.listBody || "",
      listButtonText: block.listButtonText || "Ver opciones",
      listFooter: block.listFooter || "",
      listSections: normalizeListSections(block),
      contactName: block.contactName || "",
      contactPhone: block.contactPhone || "",
      locationLatitude: block.locationLatitude ?? "",
      locationLongitude: block.locationLongitude ?? "",
      locationName: block.locationName || "",
      locationAddress: block.locationAddress || "",
      mediaTranscriptionEnabled: Boolean(block.mediaTranscriptionEnabled),
      mediaTranscriptionText: block.mediaTranscriptionText || "",
      media: block.file ? undefined : block.media,
    }));
    const schedule = {
      days: (values.scheduleDays || [])
        .filter(day => day.enabled)
        .map(day => ({
          day: Number(day.day),
          startTime: day.startTime || "09:00",
          endTime: day.endTime || "18:00",
        })),
    };
    const rawCaptureTargetType =
      canUseProcesses ||
      !["process_field", "process_file"].includes(values.captureTargetType)
        ? values.captureTargetType || "contact"
        : "contact";
    const captureTargetType =
      rawCaptureTargetType === "washeet_field" && !canUseWasheets
        ? "contact"
        : rawCaptureTargetType;
    const captureMode =
      captureTargetType === "process_file"
        ? "file"
        : values.captureMode || "text";
    const captureContactCustomField =
      captureTargetType === "contact" && captureMode === "file"
        ? contactFileCustomFieldNames.includes(values.captureContactCustomField)
          ? values.captureContactCustomField
          : contactFileCustomFieldNames[0] || ""
        : contactTextCustomFieldNames.includes(values.captureContactCustomField)
        ? values.captureContactCustomField
        : "";
    const captureConfig = values.captureEnabled
      ? {
          enabled: true,
          mode: captureMode,
          targetType: captureTargetType,
          contactField:
            captureTargetType === "contact" && captureMode === "file"
              ? "custom"
              : values.captureContactField || "name",
          contactCustomField:
            captureTargetType === "contact" &&
            (captureMode === "file" || values.captureContactField === "custom") &&
            captureContactCustomField !== NO_CAPTURE_CUSTOM_FIELD
              ? captureContactCustomField || ""
              : "",
          processId:
            canUseProcesses &&
            values.captureProcessId &&
            values.captureProcessId !== NO_CAPTURE_PROCESS
              ? Number(values.captureProcessId)
              : null,
          processFieldKey:
            canUseProcesses &&
            values.captureProcessFieldKey !== NO_CAPTURE_PROCESS_FIELD
              ? values.captureProcessFieldKey || ""
              : "",
          processFileKey:
            canUseProcesses &&
            values.captureProcessFileKey !== NO_CAPTURE_PROCESS_FILE
              ? values.captureProcessFileKey || ""
              : "",
          washeetDocumentId:
            captureTargetType === "washeet_field" &&
            values.captureWasheetDocumentId &&
            values.captureWasheetDocumentId !== NO_CAPTURE_WASHEET_DOCUMENT
              ? Number(values.captureWasheetDocumentId)
              : null,
          washeetField:
            captureTargetType === "washeet_field" &&
            values.captureWasheetField !== NO_CAPTURE_WASHEET_FIELD
              ? values.captureWasheetField || ""
              : "",
          processingMode:
            canUseAi && values.captureProcessingMode === "ai" ? "ai" : "direct",
          aiPrompt:
            canUseAi && values.captureProcessingMode === "ai"
              ? values.captureAiPrompt || ""
              : "",
          onTypeMismatch:
            canUseAi && values.captureOnTypeMismatch === "ai" ? "ai" : "message",
          errorMessage: values.captureErrorMessage || "",
        }
      : null;
    const cartConfig = canUseOrders && values.cartEnabled
      ? {
          enabled: true,
          mainMenuSequenceId:
            values.cartMainMenuSequenceId !== NO_CART_MAIN_MENU_SEQUENCE
              ? Number(values.cartMainMenuSequenceId)
              : null,
        }
      : null;
    const productConfig = canUseOrders && values.productEnabled
      ? {
          enabled: true,
          productId:
            values.productId !== NO_PRODUCT ? Number(values.productId) : null,
          introSequenceId:
            values.productIntroSequenceId !== NO_PRODUCT_INTRO_SEQUENCE
              ? Number(values.productIntroSequenceId)
              : null,
          mainMenuSequenceId:
            values.productMainMenuSequenceId !== NO_PRODUCT_MAIN_MENU_SEQUENCE
              ? Number(values.productMainMenuSequenceId)
              : null,
        }
      : null;
    const ordersConfig = canUseOrders && values.ordersEnabled
      ? {
          enabled: true,
          mainMenuSequenceId:
            values.ordersMainMenuSequenceId !== NO_ORDERS_MAIN_MENU_SEQUENCE
              ? Number(values.ordersMainMenuSequenceId)
              : null,
        }
      : null;
    const streamingServicesConfig = canUseOrders && values.streamingServicesEnabled
      ? {
          enabled: true,
          mainMenuSequenceId:
            values.streamingServicesMainMenuSequenceId !==
            NO_STREAMING_SERVICES_MAIN_MENU_SEQUENCE
              ? Number(values.streamingServicesMainMenuSequenceId)
              : null,
        }
      : null;
    const processSequenceConfig = canUseProcesses && values.processSequenceEnabled
      ? {
          enabled: true,
          processId:
            values.processSequenceProcessId !== NO_PROCESS_SEQUENCE_PROCESS
              ? Number(values.processSequenceProcessId)
              : null,
          mainMenuSequenceId:
            values.processSequenceMainMenuSequenceId !==
            NO_PROCESS_MAIN_MENU_SEQUENCE
              ? Number(values.processSequenceMainMenuSequenceId)
              : null,
        }
      : null;
    const agendaConfig = canUseAgenda && values.agendaEnabled
      ? {
          enabled: true,
          serviceId:
            values.agendaServiceId !== ALL_AGENDA_SERVICES &&
            values.agendaServiceId !== NO_AGENDA_SERVICE
              ? Number(values.agendaServiceId)
              : null,
          mainMenuSequenceId:
            values.agendaMainMenuSequenceId !== NO_AGENDA_MAIN_MENU_SEQUENCE
              ? Number(values.agendaMainMenuSequenceId)
              : null,
          reservationStatus: values.agendaReservationStatus || "confirmed",
        }
      : null;
    const agendaReservationsConfig = canUseAgenda && values.agendaReservationsEnabled
      ? {
          enabled: true,
          mainMenuSequenceId:
            values.agendaReservationsMainMenuSequenceId !==
            NO_AGENDA_RESERVATIONS_MAIN_MENU_SEQUENCE
              ? Number(values.agendaReservationsMainMenuSequenceId)
              : null,
        }
      : null;

    const formData = new FormData();
    formData.append("name", values.name);
    formData.append("category", values.category || "General");
    formData.append("trigger", values.captureEnabled ? "" : values.trigger || "");
    formData.append(
      "matchType",
      values.captureEnabled
        ? "exact"
        : !canUseTickets && values.matchType === "newChat"
        ? initialState.matchType
        : values.matchType
    );
    formData.append(
      "requireAllTriggers",
      values.matchType === "conditional" ? "true" : "false"
    );
    formData.append("skipMarkAsRead", values.skipMarkAsRead ? "true" : "false");
    formData.append("connectionId", values.connectionId || "");
    formData.append(
      "sharedConnectionIds",
      JSON.stringify(normalizeConnectionIds(values.sharedConnectionIds, values.connectionId))
    );
    formData.append("parentId", toOptionalApiValue(values.parentId, NO_PARENT_SEQUENCE));
    formData.append("isActive", values.isActive ? "true" : "false");
    formData.append("quickReplyEnabled", values.quickReplyEnabled ? "true" : "false");
    formData.append("quickReplyMatchType", values.quickReplyMatchType || "exact");
    formData.append("quickReplyTrigger", values.quickReplyTrigger || "");
    formData.append("scheduleEnabled", values.scheduleEnabled ? "true" : "false");
    formData.append("schedule", JSON.stringify(schedule));
    formData.append("executionMode", values.executionMode);
    formData.append("executionLimit", values.executionLimit || "");
    formData.append("executionInterval", values.executionInterval || "");
    formData.append("executionIntervalUnit", values.executionIntervalUnit || "");
    formData.append("transferType", canUseLabels ? toOptionalApiValue(values.transferType, NO_TRANSFER_TYPE) : "");
    formData.append("transferQueueId", canUseLabels ? toOptionalApiValue(values.transferQueueId, NO_TRANSFER_QUEUE) : "");
    formData.append("transferUserId", canUseLabels ? toOptionalApiValue(values.transferUserId, NO_TRANSFER_USER) : "");
    formData.append(
      "transferAutoAssignEnabled",
      canUseLabels && values.transferType === TRANSFER_AUTO_ASSIGN
        ? "true"
        : "false"
    );
    formData.append(
      "transferAutoAssignQueueIds",
      JSON.stringify(
        canUseLabels && values.transferType === TRANSFER_AUTO_ASSIGN
          ? normalizeConnectionIds(values.transferAutoAssignQueueIds)
          : []
      )
    );
    formData.append(
      "transferAutoAssignUserIds",
      JSON.stringify(
        canUseLabels && values.transferType === TRANSFER_AUTO_ASSIGN
          ? normalizeConnectionIds(values.transferAutoAssignUserIds)
          : []
      )
    );
    formData.append("labelConditionType", canUseLabels ? values.labelConditionType || LABEL_CONDITION_NONE : LABEL_CONDITION_NONE);
    formData.append(
      "labelConditionIds",
      JSON.stringify(
        canUseLabels && values.labelConditionType !== LABEL_CONDITION_NONE
          ? (values.labelConditionIds || []).map(Number).filter(Boolean)
          : []
      )
    );
    formData.append("addLabelIds", JSON.stringify(canUseLabels ? values.addLabelIds || [] : []));
    formData.append("removeLabelIds", JSON.stringify(canUseLabels ? values.removeLabelIds || [] : []));
    formData.append("aiAction", canUseAi ? toOptionalApiValue(values.aiAction, NO_AI_ACTION) : "");
    formData.append("chatbotAction", toOptionalApiValue(values.chatbotAction, NO_CHATBOT_ACTION));
    formData.append("funnelActionFunnelId", canUseFunnels ? toOptionalApiValue(values.funnelActionFunnelId, NO_FUNNEL_ACTION) : "");
    formData.append("funnelActionStageId", canUseFunnels ? toOptionalApiValue(values.funnelActionStageId, NO_FUNNEL_STAGE) : "");
    formData.append("aiInitialMessage", canUseAi ? values.aiInitialMessage || "" : "");
    formData.append("aiPrompt", canUseAi ? values.aiPrompt || "" : "");
    formData.append("orchestratorModeEnabled", canUseAi ? values.orchestratorModeEnabled === true : false);
    formData.append("useSequencesTool", canUseAi ? values.useSequencesTool !== false : true);
    formData.append("useWasheetsTool", canUseAi && canUseWasheets ? values.useWasheetsTool !== false : false);
    formData.append("useWebSearchTool", canUseAi ? values.useWebSearchTool !== false : true);
    formData.append("useAgendaTool", canUseAi && canUseAgenda ? values.useAgendaTool !== false : false);
    formData.append("useOrdersTool", canUseAi && canUseOrders ? values.useOrdersTool !== false : false);
    formData.append("useWebhookTools", canUseAi && canUseWebhooks ? values.useWebhookTools !== false : false);
    formData.append("useContactTool", canUseAi ? values.useContactTool !== false : true);
    formData.append("useProcessesTool", canUseAi && canUseProcesses ? values.useProcessesTool === true : false);
    formData.append(
      "selectedProcessIds",
      JSON.stringify(
        canUseAi && canUseProcesses && values.useProcessesTool === true
          ? values.selectedProcessIds || []
          : []
      )
    );
    formData.append("naturalMessageModeEnabled", canUseAi ? values.naturalMessageModeEnabled === true : false);
    formData.append("outOfHoursSequenceId", toOptionalApiValue(values.outOfHoursSequenceId, NO_OUT_OF_HOURS_SEQUENCE));
    formData.append("noMatchMessage", values.noMatchMessage || "");
    formData.append(
      "noMatchAiEnabled",
      canUseAi &&
        values.captureEnabled !== true
        ? values.noMatchAiEnabled === true
        : false
    );
    formData.append(
      "noMatchAiPrompt",
      canUseAi &&
        values.captureEnabled !== true
        ? values.noMatchAiPrompt || ""
        : ""
    );
    formData.append("captureConfig", JSON.stringify(captureConfig));
    formData.append("cartConfig", JSON.stringify(cartConfig));
    formData.append("productConfig", JSON.stringify(productConfig));
    formData.append("ordersConfig", JSON.stringify(ordersConfig));
    formData.append("streamingServicesConfig", JSON.stringify(streamingServicesConfig));
    formData.append("processSequenceConfig", JSON.stringify(processSequenceConfig));
    formData.append("agendaConfig", JSON.stringify(agendaConfig));
    formData.append("agendaReservationsConfig", JSON.stringify(agendaReservationsConfig));
    formData.append(
      "configSectionOrder",
      JSON.stringify(
        activeConfigSections.filter(section => {
          if (
            !canUseOrders &&
            ["orders", "cart", "product", "streamingServices"].includes(section)
          ) {
            return false;
          }

          if (!canUseProcesses && section === "processes") {
            return false;
          }

          return true;
        })
      )
    );
    formData.append("afterExecutionBehavior", values.afterExecutionBehavior || "root");
    formData.append(
      "afterExecutionSequenceId",
      ["sequence", "execute_sequence"].includes(values.afterExecutionBehavior)
        ? toOptionalApiValue(
            values.afterExecutionSequenceId,
            NO_AFTER_EXECUTION_SEQUENCE
          )
        : ""
    );
    formData.append(
      "childSequenceIds",
      JSON.stringify(values.childSequenceIds || [])
    );
    formData.append("blocks", JSON.stringify(normalizedBlocks));

    blocks.forEach((block, index) => {
      if (block.file) {
        formData.append(`blockFile_${index}`, block.file);
      }
    });

    return formData;
  };

  const validateBlocks = values =>
    (canUseAi && values.aiAction !== NO_AI_ACTION) ||
    values.chatbotAction !== NO_CHATBOT_ACTION ||
    (canUseFunnels && values.funnelActionStageId !== NO_FUNNEL_STAGE) ||
    (canUseOrders &&
      (values.ordersEnabled ||
        values.cartEnabled ||
        values.productEnabled ||
        values.streamingServicesEnabled)) ||
    (canUseProcesses && values.processSequenceEnabled) ||
    values.agendaEnabled ||
    values.agendaReservationsEnabled ||
    blocks.some(block =>
      block.type === "inactivity"
        ? block.inactivityValue &&
          block.inactivityUnit &&
          block.inactivitySequenceId !== NO_INACTIVITY_SEQUENCE
        : block.type === "followup"
        ? block.followupValue &&
          block.followupUnit &&
          (block.followupMode === "conditions"
            ? normalizeFollowupConditions(block.followupConditions).some(
                condition =>
                  condition.sequenceId !== NO_FOLLOWUP_CONDITION_SEQUENCE &&
                  (condition.type === "label"
                    ? condition.labelId
                    : condition.type === "funnel"
                    ? condition.funnelId !== NO_FUNNEL_ACTION &&
                      condition.funnelStageId !== NO_FUNNEL_STAGE
                    : condition.type === "queue"
                    ? !isNormalAssignmentMode && condition.queueId
                    : condition.contactField && condition.contactValue?.trim())
              )
            : block.followupSequenceId !== NO_FOLLOWUP_SEQUENCE)
        : block.type === "notify"
        ? block.notifyContactId && (block.text.trim() || block.file || block.media)
        : block.type === "poll"
        ? block.pollTitle?.trim() &&
          (block.pollOptions || "")
            .split(/\r?\n/)
            .map(option => option.trim())
            .filter(Boolean).length >= 2
        : block.type === "whatsappList"
        ? block.listTitle?.trim() &&
          block.listBody?.trim() &&
          (block.listButtonText || "").trim() &&
          flattenListSections(block).some(row => row.title?.trim())
        : block.type === "contact"
        ? block.contactName?.trim() && block.contactPhone?.trim()
        : block.type === "location"
        ? block.locationLatitude !== "" &&
          block.locationLongitude !== "" &&
          !Number.isNaN(Number(block.locationLatitude)) &&
          !Number.isNaN(Number(block.locationLongitude))
        : block.text.trim() || block.file || block.media
    );

  const handleSave = async values => {
    try {
      const sharedConnectionIds = normalizeConnectionIds(values.sharedConnectionIds, values.connectionId);
      if (!sharedConnectionIds.length) {
        toast.error("Selecciona las conexiones donde funcionara la secuencia.");
        return;
      }

      if (!validateBlocks(values)) {
        toast.error("Agrega al menos un texto, archivo o accion de IA.");
        return;
      }

      if (values.captureEnabled) {
        if (
          values.captureTargetType === "contact" &&
          values.captureMode === "file" &&
          !contactFileCustomFieldNames.includes(values.captureContactCustomField)
        ) {
          toast.error("Selecciona el campo de archivo donde se guardara la captura.");
          return;
        }

        if (
          values.captureTargetType === "contact" &&
          values.captureMode !== "file" &&
          values.captureContactField === "custom" &&
          !contactTextCustomFieldNames.includes(values.captureContactCustomField)
        ) {
          toast.error("Selecciona el campo adicional donde se guardara la captura.");
          return;
        }

        if (
          canUseProcesses &&
          ["process_field", "process_file"].includes(values.captureTargetType) &&
          (!values.captureProcessId || values.captureProcessId === NO_CAPTURE_PROCESS)
        ) {
          toast.error("Selecciona el proceso donde se guardara la captura.");
          return;
        }

        if (
          canUseProcesses &&
          values.captureTargetType === "process_field" &&
          (!values.captureProcessFieldKey ||
            values.captureProcessFieldKey === NO_CAPTURE_PROCESS_FIELD)
        ) {
          toast.error("Selecciona el campo del proceso donde se guardara la captura.");
          return;
        }

        if (
          canUseProcesses &&
          values.captureTargetType === "process_file" &&
          (!values.captureProcessFileKey ||
            values.captureProcessFileKey === NO_CAPTURE_PROCESS_FILE)
        ) {
          toast.error("Selecciona el archivo requerido del proceso.");
          return;
        }

        if (
          canUseWasheets &&
          values.captureTargetType === "washeet_field" &&
          (!values.captureWasheetDocumentId ||
            values.captureWasheetDocumentId === NO_CAPTURE_WASHEET_DOCUMENT)
        ) {
          toast.error("Selecciona el documento de Google Sheets.");
          return;
        }

        if (
          canUseWasheets &&
          values.captureTargetType === "washeet_field" &&
          (!values.captureWasheetField ||
            values.captureWasheetField === NO_CAPTURE_WASHEET_FIELD)
        ) {
          toast.error("Selecciona el campo del formulario Google Sheets.");
          return;
        }

        if (
          canUseAi &&
          values.captureProcessingMode === "ai" &&
          !String(values.captureAiPrompt || "").trim()
        ) {
          toast.error("Escribe el prompt para que la IA capture el dato.");
          return;
        }

        if (
          (!canUseAi || values.captureProcessingMode !== "ai") &&
          values.captureOnTypeMismatch === "message" &&
          !String(values.captureErrorMessage || "").trim()
        ) {
          toast.error("Escribe el mensaje de error para la captura.");
          return;
        }
      }

      if (
        canUseProcesses &&
        values.processSequenceEnabled &&
        (!values.processSequenceProcessId ||
          values.processSequenceProcessId === NO_PROCESS_SEQUENCE_PROCESS)
      ) {
        toast.error("Selecciona el proceso que ejecutara la secuencia.");
        return;
      }

      if (
        canUseOrders &&
        values.productEnabled &&
        (!values.productId || values.productId === NO_PRODUCT)
      ) {
        toast.error("Selecciona el producto que ejecutara la secuencia.");
        return;
      }

      const formData = buildFormData(values);
      const config = { headers: { "Content-Type": "multipart/form-data" } };

      let savedSequence;
      if (sequenceId) {
        const { data } = await api.put(`/chatbot/sequences/${sequenceId}`, formData, config);
        savedSequence = data;
      } else {
        const { data } = await api.post("/chatbot/sequences", formData, config);
        savedSequence = data;
      }

      toast.success(i18n.t("chatbotModal.success"));
      if (onSave) onSave(savedSequence);
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth scroll="paper">
      <Formik
        initialValues={sequence}
        enableReinitialize
        validationSchema={ChatbotSchema}
        onSubmit={(values, actions) => {
          handleSave(values).finally(() => actions.setSubmitting(false));
        }}
      >
        {({ values, errors, touched, isSubmitting, setFieldValue }) => (
          <Form>
            <DialogTitle className={classes.stickyDialogTitle}>
              <div className={classes.dialogTitleContent}>
                <span>
                  {sequenceId
                    ? i18n.t("chatbotModal.title.edit")
                    : i18n.t("chatbotModal.title.add")}
                </span>
                <FormControlLabel
                  className={classes.activeSwitch}
                  control={
                    <Switch
                      checked={values.isActive}
                      onChange={event =>
                        setFieldValue("isActive", event.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label={i18n.t("chatbotModal.form.active")}
                />
              </div>
            </DialogTitle>
            <DialogContent dividers className={classes.dialogBody}>
              <Grid container spacing={2} className={classes.basicFields}>
                <Grid item xs={12} md={4}>
                  <Field
                    as={TextField}
                    label={i18n.t("chatbotModal.form.name")}
                    name="name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    freeSolo
                    options={categoryOptions}
                    value={values.category || "General"}
                    onChange={(_, value) =>
                      setFieldValue("category", value || "General")
                    }
                    onInputChange={(_, value) =>
                      setFieldValue("category", value || "General")
                    }
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={i18n.t("chatbotModal.form.category")}
                        variant="outlined"
                        margin="dense"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl variant="outlined" className={classes.formControl}>
                    <InputLabel>Conexiones</InputLabel>
                    <Select
                      multiple
                      value={values.sharedConnectionIds || []}
                      label="Conexiones"
                      renderValue={selected =>
                        selected.map(id => getWhatsappName(connections, id)).join(", ")
                      }
                      onChange={event => {
                        const selectedIds = event.target.value;
                        if (!selectedIds.length) {
                          toast.error("Selecciona las conexiones donde funcionara la secuencia.");
                          return;
                        }

                        const nextPrimaryId = selectedIds.includes(Number(values.connectionId))
                          ? values.connectionId
                          : selectedIds[0] || "";
                        setFieldValue("sharedConnectionIds", selectedIds);
                        setFieldValue("connectionId", nextPrimaryId);
                        setFieldValue("parentId", NO_PARENT_SEQUENCE);
                        setFieldValue("outOfHoursSequenceId", NO_OUT_OF_HOURS_SEQUENCE);
                        setFieldValue("afterExecutionSequenceId", NO_AFTER_EXECUTION_SEQUENCE);
                        setFieldValue("childSequenceIds", []);
                        setFieldValue("transferQueueId", NO_TRANSFER_QUEUE);
                        setFieldValue("transferUserId", NO_TRANSFER_USER);
                        setFieldValue("transferAutoAssignQueueIds", []);
                        setFieldValue("transferAutoAssignUserIds", []);
                        setFieldValue("funnelActionFunnelId", NO_FUNNEL_ACTION);
                        setFieldValue("funnelActionStageId", NO_FUNNEL_STAGE);
                        setFieldValue("captureProcessId", NO_CAPTURE_PROCESS);
                        setFieldValue("captureProcessFieldKey", NO_CAPTURE_PROCESS_FIELD);
                        setFieldValue("captureProcessFileKey", NO_CAPTURE_PROCESS_FILE);
                        setFieldValue("labelConditionType", LABEL_CONDITION_NONE);
                        setFieldValue("labelConditionIds", []);
                        setFieldValue("addLabelIds", []);
                        setFieldValue("removeLabelIds", []);
                        setBlocks(prevBlocks =>
                          prevBlocks.map(block =>
                            block.type === "inactivity"
                              ? { ...block, inactivitySequenceId: NO_INACTIVITY_SEQUENCE }
                              : block.type === "followup"
                              ? {
                                  ...block,
                                  followupSequenceId: NO_FOLLOWUP_SEQUENCE,
                                  followupCancelSequenceId: NO_FOLLOWUP_CANCEL_SEQUENCE,
                                }
                              : block
                          )
                        );
                      }}
                    >
                      {connections.map(whatsapp => (
                        <MenuItem key={whatsapp.id} value={whatsapp.id}>
                          <Checkbox checked={(values.sharedConnectionIds || []).indexOf(whatsapp.id) > -1} />
                          <ListItemText
                            primary={whatsapp.name || whatsapp.number || `Conexión ${whatsapp.id}`}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <div className={classes.configArea}>
              {activeConfigSections.includes("chatbot") && (
                <Paper
                  className={classes.block}
                  elevation={0}
                  style={{ order: getConfigSectionOrder("chatbot") }}
                >
                  <div className={classes.blockHeader}>
                    <Typography variant="subtitle2">
                      {i18n.t("chatbotModal.sections.chatbot")}
                    </Typography>
                    <div className={classes.blockActions}>
                      <IconButton size="small" onClick={() => removeConfigSection("chatbot", setFieldValue)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
              <Grid container spacing={2}>
                {values.captureEnabled !== true && (
                  <Grid item xs={12} md={4}>
                    <FormControl variant="outlined" className={classes.formControl}>
                      <InputLabel>{i18n.t("chatbotModal.form.matchType")}</InputLabel>
                      <Field as={Select} name="matchType" label={i18n.t("chatbotModal.form.matchType")}>
                        <MenuItem value="exact">{i18n.t("chatbotModal.matchTypes.exact")}</MenuItem>
                        <MenuItem value="contains">{i18n.t("chatbotModal.matchTypes.contains")}</MenuItem>
                        <MenuItem value="conditional">{i18n.t("chatbotModal.matchTypes.conditional")}</MenuItem>
                        <MenuItem value="anything">{i18n.t("chatbotModal.matchTypes.anything")}</MenuItem>
                        {canUseTickets && (
                          <MenuItem value="newChat">{i18n.t("chatbotModal.matchTypes.newChat")}</MenuItem>
                        )}
                      </Field>
                    </FormControl>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={values.skipMarkAsRead === true}
                          onChange={event =>
                            setFieldValue("skipMarkAsRead", event.target.checked)
                          }
                        />
                      }
                      label="No marcar como leido al ejecutar"
                    />
                  </Grid>
                )}
                {values.captureEnabled !== true && !["anything", "newChat"].includes(values.matchType) && (
                  <Grid item xs={12} md={4}>
                    <Field
                      as={TextField}
                      label={i18n.t("chatbotModal.form.trigger")}
                      name="trigger"
                      variant="outlined"
                      margin="dense"
                      multiline
                      minRows={4}
                      className={classes.textField}
                      helperText={
                        values.matchType === "conditional"
                          ? i18n.t("chatbotModal.form.conditionalTriggerHelp")
                          : i18n.t("chatbotModal.form.triggerHelp")
                      }
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={4}>
                  <FormControl variant="outlined" className={classes.formControl}>
                    <InputLabel>{i18n.t("chatbotModal.form.parent")}</InputLabel>
                    <Field
                      as={Select}
                      name="parentId"
                      label={i18n.t("chatbotModal.form.parent")}
                      MenuProps={sequenceSelectMenuProps}
                    >
                      <MenuItem value={NO_PARENT_SEQUENCE}>
                        {i18n.t("chatbotModal.form.noParent")}
                      </MenuItem>
                      {sequences
                        .filter(
                          item =>
                            item.id !== sequenceId &&
                            recordOwnsWhatsapp(item, values.connectionId)
                        )
                        .map(item => (
                          <MenuItem key={item.id} value={item.id}>
                            {formatSequenceOption(item)}
                          </MenuItem>
                        ))}
                    </Field>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl variant="outlined" className={classes.formControl}>
                    <InputLabel>Secuencias hijas</InputLabel>
                    <Select
                      multiple
                      value={values.childSequenceIds || []}
                      label="Secuencias hijas"
                      MenuProps={sequenceSelectMenuProps}
                      renderValue={selected =>
                        selected
                          .map(id => sequences.find(item => Number(item.id) === Number(id))?.name)
                          .filter(Boolean)
                          .join(", ")
                      }
                      onChange={event => {
                        setFieldValue("childSequenceIds", event.target.value);
                        if (!event.target.value.length) {
                          setFieldValue("noMatchMessage", "");
                          setFieldValue("noMatchAiEnabled", false);
                          setFieldValue("noMatchAiPrompt", "");
                        }
                      }}
                    >
                      {sequences
                        .filter(
                          item =>
                            item.id !== sequenceId &&
                            recordOwnsWhatsapp(item, values.connectionId)
                        )
                        .map(item => (
                          <MenuItem key={item.id} value={item.id}>
                            <Checkbox
                              checked={(values.childSequenceIds || []).some(
                                id => Number(id) === Number(item.id)
                              )}
                            />
                            <ListItemText
                              primary={formatSequenceOption(item)}
                              secondary={
                                item.parentId && Number(item.parentId) !== Number(sequenceId)
                                  ? `Submenu actual de ID ${item.parentId}`
                                  : ""
                              }
                            />
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl variant="outlined" className={classes.formControl}>
                    <InputLabel>Comportamiento</InputLabel>
                    <Field
                      as={Select}
                      name="afterExecutionBehavior"
                      label="Comportamiento"
                      onChange={event => {
                        setFieldValue("afterExecutionBehavior", event.target.value);
                        if (!["sequence", "execute_sequence"].includes(event.target.value)) {
                          setFieldValue(
                            "afterExecutionSequenceId",
                            NO_AFTER_EXECUTION_SEQUENCE
                          );
                        }
                      }}
                    >
                      <MenuItem value="root">Volver al menu principal</MenuItem>
                      <MenuItem value="stay">Mantener en el submenu actual</MenuItem>
                      <MenuItem value="sequence">Ir a otro submenu</MenuItem>
                      <MenuItem value="execute_sequence">Ejecutar secuencia</MenuItem>
                    </Field>
                  </FormControl>
                </Grid>
                {["sequence", "execute_sequence"].includes(values.afterExecutionBehavior) && (
                  <Grid item xs={12} md={4}>
                    <FormControl variant="outlined" className={classes.formControl}>
                      <InputLabel>
                        {values.afterExecutionBehavior === "execute_sequence"
                          ? "Secuencia"
                          : "Submenu destino"}
                      </InputLabel>
                      <Select
                        value={
                          values.afterExecutionSequenceId ||
                          NO_AFTER_EXECUTION_SEQUENCE
                        }
                        label={
                          values.afterExecutionBehavior === "execute_sequence"
                            ? "Secuencia"
                            : "Submenu destino"
                        }
                        MenuProps={sequenceSelectMenuProps}
                        onChange={event => {
                          if (event.target.value === "__create__") {
                            openCreateSequence(
                              null,
                              values.connectionId,
                              "afterExecution",
                              sequenceId =>
                                setFieldValue("afterExecutionSequenceId", sequenceId)
                            );
                            return;
                          }

                          setFieldValue("afterExecutionSequenceId", event.target.value);
                        }}
                      >
                        <MenuItem value={NO_AFTER_EXECUTION_SEQUENCE}>
                          Sin secuencia
                        </MenuItem>
                        <MenuItem value="__create__">Crear secuencia...</MenuItem>
                        {sequences
                          .filter(
                            item =>
                              item.id !== sequenceId &&
                              recordOwnsWhatsapp(item, values.connectionId)
                          )
                          .map(item => (
                            <MenuItem key={item.id} value={item.id}>
                              {formatSequenceOption(item)}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <div className={classes.sequenceActions}>
                      <Button
                        color="primary"
                        disabled={
                          values.afterExecutionSequenceId ===
                          NO_AFTER_EXECUTION_SEQUENCE
                        }
                        onClick={() =>
                          openEditSequence(
                            null,
                            values.afterExecutionSequenceId,
                            values.connectionId,
                            "afterExecution",
                            sequenceId =>
                              setFieldValue("afterExecutionSequenceId", sequenceId)
                          )
                        }
                      >
                        Editar secuencia
                      </Button>
                    </div>
                  </Grid>
                )}
                {values.captureEnabled !== true &&
                  (values.childSequenceIds || []).length > 0 && (
                    <>
                      <Grid item xs={12} md={4}>
                        <FormControl variant="outlined" className={classes.formControl}>
                          <InputLabel>Si no hay coincidencias</InputLabel>
                          <Select
                            value={
                              values.noMatchAiEnabled === true &&
                              canUseAi &&
                              values.captureEnabled !== true
                                ? "ai"
                                : "message"
                            }
                            label="Si no hay coincidencias"
                            onChange={event => {
                              const useAi = event.target.value === "ai";
                              setFieldValue("noMatchAiEnabled", useAi);
                              if (useAi) {
                                setFieldValue("noMatchMessage", "");
                              } else {
                                setFieldValue("noMatchAiPrompt", "");
                              }
                            }}
                          >
                            <MenuItem value="message">Enviar mensaje de error</MenuItem>
                            {canUseAi && values.captureEnabled !== true && (
                              <MenuItem value="ai">Usar IA como apoyo</MenuItem>
                            )}
                          </Select>
                        </FormControl>
                      </Grid>
                      {values.noMatchAiEnabled === true &&
                      canUseAi &&
                      values.captureEnabled !== true ? (
                        <Grid item xs={12}>
                          <Field
                            as={TextField}
                            label="Metas e instrucciones para la IA de no coincidencia"
                            name="noMatchAiPrompt"
                            variant="outlined"
                            margin="dense"
                            multiline
                            minRows={4}
                            className={classes.textField}
                            helperText="Indica como decidir la ruta correcta dentro de los submenus disponibles."
                          />
                        </Grid>
                      ) : (
                        <Grid item xs={12}>
                          <Field
                            as={TextField}
                            label="Mensaje de error cuando no hay coincidencias"
                            name="noMatchMessage"
                            variant="outlined"
                            margin="dense"
                            multiline
                            minRows={4}
                            className={classes.textField}
                            helperText="Se envia cuando el contacto esta dentro de este submenu y no coincide con ninguna secuencia hija."
                          />
                        </Grid>
                      )}
                    </>
                  )}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.captureEnabled === true}
                        onChange={event => {
                          const enabled = event.target.checked;
                          setFieldValue("captureEnabled", enabled);
                          if (enabled) {
                            setFieldValue("cartEnabled", false);
                            setFieldValue("ordersEnabled", false);
                            setFieldValue("productEnabled", false);
                            setFieldValue("childSequenceIds", []);
                            setFieldValue("noMatchMessage", "");
                            setFieldValue("noMatchAiEnabled", false);
                            setFieldValue("noMatchAiPrompt", "");
                            setActiveConfigSections(prevSections =>
                              prevSections.filter(
                                section => !["cart", "orders", "product"].includes(section)
                              )
                            );
                            setFieldValue(
                              "cartMainMenuSequenceId",
                              NO_CART_MAIN_MENU_SEQUENCE
                            );
                            setFieldValue(
                              "ordersMainMenuSequenceId",
                              NO_ORDERS_MAIN_MENU_SEQUENCE
                            );
                            setFieldValue("productId", NO_PRODUCT);
                            setFieldValue(
                              "productIntroSequenceId",
                              NO_PRODUCT_INTRO_SEQUENCE
                            );
                            setFieldValue(
                              "productMainMenuSequenceId",
                              NO_PRODUCT_MAIN_MENU_SEQUENCE
                            );
                          }
                          if (!enabled) {
                            setFieldValue("captureMode", "text");
                            setFieldValue("captureTargetType", "contact");
                            setFieldValue("captureContactField", "name");
                            setFieldValue("captureContactCustomField", "");
                            setFieldValue("captureProcessId", NO_CAPTURE_PROCESS);
                            setFieldValue(
                              "captureProcessFieldKey",
                              NO_CAPTURE_PROCESS_FIELD
                            );
                            setFieldValue(
                              "captureProcessFileKey",
                              NO_CAPTURE_PROCESS_FILE
                            );
                            setFieldValue(
                              "captureWasheetDocumentId",
                              NO_CAPTURE_WASHEET_DOCUMENT
                            );
                            setFieldValue(
                              "captureWasheetField",
                              NO_CAPTURE_WASHEET_FIELD
                            );
                            setFieldValue("captureProcessingMode", "direct");
                            setFieldValue("captureAiPrompt", "");
                            setFieldValue("captureOnTypeMismatch", "message");
                            setFieldValue("captureErrorMessage", "");
                          }
                        }}
                        color="primary"
                      />
                    }
                    label="Capturar dato."
                  />
                  <Typography variant="caption" color="textSecondary" display="block">
                    Guarda texto o archivos recibidos y luego continua el comportamiento normal del chatbot.
                  </Typography>
                </Grid>
                {values.captureEnabled === true && (
                  <>
                    <Grid item xs={12} md={4}>
                      <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel>Tipo esperado</InputLabel>
                        <Select
                          value={values.captureMode || "text"}
                          label="Tipo esperado"
                          onChange={event => {
                            const nextMode = event.target.value;
                            setFieldValue("captureMode", nextMode);
                            if (nextMode === "file") {
                              setFieldValue(
                                "captureTargetType",
                                values.captureTargetType === "washeet_field" && canUseWasheets
                                  ? "washeet_field"
                                  : canUseProcesses
                                  ? "process_file"
                                  : "contact"
                              );
                              setFieldValue("captureContactField", "custom");
                              setFieldValue(
                                "captureContactCustomField",
                                contactFileCustomFieldNames[0] || NO_CAPTURE_CUSTOM_FIELD
                              );
                              setFieldValue("captureProcessFieldKey", NO_CAPTURE_PROCESS_FIELD);
                              if (values.captureTargetType !== "washeet_field" || !canUseWasheets) {
                                setFieldValue(
                                  "captureWasheetDocumentId",
                                  NO_CAPTURE_WASHEET_DOCUMENT
                                );
                                setFieldValue(
                                  "captureWasheetField",
                                  NO_CAPTURE_WASHEET_FIELD
                                );
                              }
                            } else {
                              setFieldValue(
                                "captureTargetType",
                                values.captureTargetType === "washeet_field" && canUseWasheets
                                  ? "washeet_field"
                                  : "contact"
                              );
                              if (values.captureContactField === "custom") {
                                setFieldValue(
                                  "captureContactCustomField",
                                  contactTextCustomFieldNames.includes(
                                    values.captureContactCustomField
                                  )
                                    ? values.captureContactCustomField
                                    : contactTextCustomFieldNames[0] ||
                                        NO_CAPTURE_CUSTOM_FIELD
                                );
                              }
                              setFieldValue("captureProcessFileKey", NO_CAPTURE_PROCESS_FILE);
                              if (values.captureTargetType !== "washeet_field" || !canUseWasheets) {
                                setFieldValue("captureProcessId", NO_CAPTURE_PROCESS);
                                setFieldValue(
                                  "captureWasheetDocumentId",
                                  NO_CAPTURE_WASHEET_DOCUMENT
                                );
                                setFieldValue(
                                  "captureWasheetField",
                                  NO_CAPTURE_WASHEET_FIELD
                                );
                              }
                            }
                          }}
                        >
                          <MenuItem value="text">Texto</MenuItem>
                          <MenuItem value="file">Archivo o multimedia</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel>Guardar en</InputLabel>
                        <Select
                          value={(() => {
                            const currentTarget =
                              canUseProcesses ||
                              !["process_field", "process_file"].includes(
                                values.captureTargetType
                              )
                                ? values.captureTargetType || "contact"
                                : "contact";
                            return currentTarget === "washeet_field" && !canUseWasheets
                              ? "contact"
                              : currentTarget;
                          })()}
                          label="Guardar en"
                          onChange={event => {
                            const nextTarget = event.target.value;
                            setFieldValue("captureTargetType", nextTarget);
                            setFieldValue(
                              "captureMode",
                              nextTarget === "process_file"
                                ? "file"
                                : nextTarget === "washeet_field"
                                ? values.captureMode || "text"
                                : nextTarget === "contact"
                                ? values.captureMode || "text"
                                : "text"
                            );
                            if (nextTarget === "contact" && values.captureMode === "file") {
                              setFieldValue("captureContactField", "custom");
                              setFieldValue(
                                "captureContactCustomField",
                                contactFileCustomFieldNames.includes(
                                  values.captureContactCustomField
                                )
                                  ? values.captureContactCustomField
                                  : contactFileCustomFieldNames[0] ||
                                      NO_CAPTURE_CUSTOM_FIELD
                              );
                            }
                            if (nextTarget !== "process_field") {
                              setFieldValue("captureProcessFieldKey", NO_CAPTURE_PROCESS_FIELD);
                            }
                            if (nextTarget !== "process_file") {
                              setFieldValue("captureProcessFileKey", NO_CAPTURE_PROCESS_FILE);
                            }
                            if (!["process_field", "process_file"].includes(nextTarget)) {
                              setFieldValue("captureProcessId", NO_CAPTURE_PROCESS);
                            }
                            if (nextTarget !== "washeet_field") {
                              setFieldValue(
                                "captureWasheetDocumentId",
                                NO_CAPTURE_WASHEET_DOCUMENT
                              );
                              setFieldValue(
                                "captureWasheetField",
                                NO_CAPTURE_WASHEET_FIELD
                              );
                            }
                          }}
                        >
                          <MenuItem value="contact">Datos de Contacto</MenuItem>
                          {canUseProcesses && values.captureMode !== "file" && (
                            <MenuItem value="process_field">Datos de Proceso</MenuItem>
                          )}
                          {canUseWasheets && (
                            <MenuItem value="washeet_field">Datos de Google Sheets</MenuItem>
                          )}
                          {canUseProcesses && values.captureMode === "file" && (
                            <MenuItem value="process_file">Archivo de Proceso</MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    {values.captureTargetType === "contact" && (
                      <>
                        <Grid item xs={12} md={4}>
                          <FormControl variant="outlined" className={classes.formControl}>
                            <InputLabel>
                              {values.captureMode === "file"
                                ? "Archivo de contacto"
                                : "Dato de contacto"}
                            </InputLabel>
                            {values.captureMode === "file" ? (
                              <Select
                                value={
                                  contactFileCustomFieldNames.includes(
                                    values.captureContactCustomField
                                  )
                                    ? values.captureContactCustomField
                                    : contactFileCustomFieldNames[0] ||
                                        NO_CAPTURE_CUSTOM_FIELD
                                }
                                label="Archivo de contacto"
                                onChange={event => {
                                  setFieldValue("captureContactField", "custom");
                                  setFieldValue(
                                    "captureContactCustomField",
                                    event.target.value
                                  );
                                }}
                              >
                                {!contactFileCustomFieldNames.length && (
                                  <MenuItem value={NO_CAPTURE_CUSTOM_FIELD} disabled>
                                    No hay campos de archivo
                                  </MenuItem>
                                )}
                                {contactFileCustomFieldNames.map(fieldName => (
                                  <MenuItem key={fieldName} value={fieldName}>
                                    {fieldName}
                                  </MenuItem>
                                ))}
                              </Select>
                            ) : (
                              <Select
                                value={values.captureContactField || "name"}
                                label="Dato de contacto"
                                onChange={event => {
                                  setFieldValue("captureContactField", event.target.value);
                                  if (event.target.value === "custom") {
                                    setFieldValue(
                                      "captureContactCustomField",
                                      contactTextCustomFieldNames[0] ||
                                        NO_CAPTURE_CUSTOM_FIELD
                                    );
                                  } else {
                                    setFieldValue("captureContactCustomField", "");
                                  }
                                }}
                              >
                                <MenuItem value="name">Nombre</MenuItem>
                                <MenuItem value="number">Numero de WhatsApp</MenuItem>
                                <MenuItem value="email">Correo Electronico</MenuItem>
                                <MenuItem value="custom">Informacion adicional</MenuItem>
                              </Select>
                            )}
                          </FormControl>
                        </Grid>
                        {values.captureMode !== "file" &&
                          values.captureContactField === "custom" && (
                          <Grid item xs={12} md={4}>
                            <FormControl variant="outlined" className={classes.formControl}>
                              <InputLabel>Campo adicional</InputLabel>
                              <Select
                                value={
                                  contactTextCustomFieldNames.includes(
                                    values.captureContactCustomField
                                  )
                                    ? values.captureContactCustomField
                                    : contactTextCustomFieldNames[0] ||
                                        NO_CAPTURE_CUSTOM_FIELD
                                }
                                label="Campo adicional"
                                onChange={event =>
                                  setFieldValue(
                                    "captureContactCustomField",
                                    event.target.value
                                  )
                                }
                              >
                                {!contactTextCustomFieldNames.length && (
                                  <MenuItem value={NO_CAPTURE_CUSTOM_FIELD} disabled>
                                    No hay campos adicionales
                                  </MenuItem>
                                )}
                                {contactTextCustomFieldNames.map(fieldName => (
                                  <MenuItem key={fieldName} value={fieldName}>
                                    {fieldName}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}
                      </>
                    )}
                    {canUseProcesses && ["process_field", "process_file"].includes(values.captureTargetType) && (
                      <>
                        <Grid item xs={12} md={4}>
                          <FormControl variant="outlined" className={classes.formControl}>
                            <InputLabel>Proceso</InputLabel>
                            <Select
                              value={values.captureProcessId || NO_CAPTURE_PROCESS}
                              label="Proceso"
                              onChange={event => {
                                setFieldValue("captureProcessId", event.target.value);
                                setFieldValue("captureProcessFieldKey", NO_CAPTURE_PROCESS_FIELD);
                                setFieldValue("captureProcessFileKey", NO_CAPTURE_PROCESS_FILE);
                              }}
                            >
                              <MenuItem value={NO_CAPTURE_PROCESS}>
                                Selecciona un proceso
                              </MenuItem>
                              {availableProcesses
                                .filter(
                                  process =>
                                    !values.connectionId ||
                                    recordOwnsWhatsapp(process, values.connectionId)
                                )
                                .map(process => (
                                  <MenuItem key={process.id} value={Number(process.id)}>
                                    {process.name}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        {values.captureTargetType === "process_field" && (
                          <Grid item xs={12} md={4}>
                            <FormControl variant="outlined" className={classes.formControl}>
                              <InputLabel>Campo del proceso</InputLabel>
                              <Select
                                value={
                                  values.captureProcessFieldKey ||
                                  NO_CAPTURE_PROCESS_FIELD
                                }
                                label="Campo del proceso"
                                onChange={event =>
                                  setFieldValue("captureProcessFieldKey", event.target.value)
                                }
                              >
                                <MenuItem value={NO_CAPTURE_PROCESS_FIELD}>
                                  Selecciona un campo
                                </MenuItem>
                                {getProcessFields(
                                  availableProcesses.find(
                                    process =>
                                      Number(process.id) === Number(values.captureProcessId)
                                  )
                                ).map(field => (
                                  <MenuItem key={field.key} value={field.key}>
                                    {field.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}
                        {values.captureTargetType === "process_file" && (
                          <Grid item xs={12} md={4}>
                            <FormControl variant="outlined" className={classes.formControl}>
                              <InputLabel>Archivo requerido</InputLabel>
                              <Select
                                value={
                                  values.captureProcessFileKey ||
                                  NO_CAPTURE_PROCESS_FILE
                                }
                                label="Archivo requerido"
                                onChange={event =>
                                  setFieldValue("captureProcessFileKey", event.target.value)
                                }
                              >
                                <MenuItem value={NO_CAPTURE_PROCESS_FILE}>
                                  Selecciona un archivo
                                </MenuItem>
                                {getProcessFiles(
                                  availableProcesses.find(
                                    process =>
                                      Number(process.id) === Number(values.captureProcessId)
                                  )
                                ).map(file => (
                                  <MenuItem key={file.key} value={file.key}>
                                    {file.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}
                      </>
                    )}
                    {canUseWasheets && values.captureTargetType === "washeet_field" && (
                      <>
                        <Grid item xs={12} md={4}>
                          <FormControl variant="outlined" className={classes.formControl}>
                            <InputLabel>Documento Google Sheets</InputLabel>
                            <Select
                              value={
                                values.captureWasheetDocumentId ||
                                NO_CAPTURE_WASHEET_DOCUMENT
                              }
                              label="Documento Google Sheets"
                              onChange={async event => {
                                const documentId = event.target.value;
                                setFieldValue("captureWasheetDocumentId", documentId);
                                setFieldValue(
                                  "captureWasheetField",
                                  NO_CAPTURE_WASHEET_FIELD
                                );
                                try {
                                  await loadWasheetHeaders(documentId);
                                } catch (err) {
                                  toastError(err);
                                }
                              }}
                            >
                              <MenuItem value={NO_CAPTURE_WASHEET_DOCUMENT}>
                                Selecciona un documento
                              </MenuItem>
                              {washeetDocuments.map(document => (
                                <MenuItem key={document.id} value={Number(document.id)}>
                                  {document.name || document.sheetName}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <FormControl variant="outlined" className={classes.formControl}>
                            <InputLabel>Campo del formulario</InputLabel>
                            <Select
                              value={
                                values.captureWasheetField || NO_CAPTURE_WASHEET_FIELD
                              }
                              label="Campo del formulario"
                              onChange={event =>
                                setFieldValue("captureWasheetField", event.target.value)
                              }
                            >
                              <MenuItem value={NO_CAPTURE_WASHEET_FIELD}>
                                Selecciona un campo
                              </MenuItem>
                              {(
                                washeetHeadersByDocument[
                                  String(values.captureWasheetDocumentId)
                                ] || []
                              ).map(header => (
                                <MenuItem key={header} value={header}>
                                  {header}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </>
                    )}
                    {canUseAi && (
                      <>
                        <Grid item xs={12} md={4}>
                          <FormControl variant="outlined" className={classes.formControl}>
                            <InputLabel>Procesar captura</InputLabel>
                            <Select
                              value={canUseAi ? values.captureProcessingMode || "direct" : "direct"}
                              label="Procesar captura"
                              onChange={event => {
                                const nextProcessingMode = canUseAi
                                  ? event.target.value
                                  : "direct";
                                setFieldValue("captureProcessingMode", nextProcessingMode);
                                if (nextProcessingMode === "ai") {
                                  setFieldValue("captureOnTypeMismatch", "message");
                                  setFieldValue("captureErrorMessage", "");
                                  setFieldValue("noMatchAiEnabled", false);
                                  setFieldValue("noMatchAiPrompt", "");
                                } else {
                                  setFieldValue("captureAiPrompt", "");
                                }
                              }}
                            >
                              <MenuItem value="direct">Guardar respuesta directa</MenuItem>
                              {canUseAi && (
                                <MenuItem value="ai">Extraer dato con IA</MenuItem>
                              )}
                            </Select>
                          </FormControl>
                        </Grid>
                        {canUseAi && values.captureProcessingMode === "ai" && (
                          <Grid item xs={12}>
                            <Field
                              as={TextField}
                              label="Prompt para capturar con IA"
                              name="captureAiPrompt"
                              variant="outlined"
                              margin="dense"
                              multiline
                              minRows={4}
                              className={classes.textField}
                              helperText="Indica que dato debe extraer, validar o normalizar. Si no hay dato valido, la IA debe pedirlo de nuevo."
                            />
                          </Grid>
                        )}
                      </>
                    )}
                    {(!canUseAi || values.captureProcessingMode !== "ai") && (
                      <>
                        <Grid item xs={12} md={4}>
                          <FormControl variant="outlined" className={classes.formControl}>
                            <InputLabel>Si llega otro tipo</InputLabel>
                            <Select
                              value={values.captureOnTypeMismatch || "message"}
                              label="Si llega otro tipo"
                              onChange={event => {
                                setFieldValue("captureOnTypeMismatch", event.target.value);
                              }}
                            >
                              <MenuItem value="message">Enviar mensaje de error</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        {values.captureOnTypeMismatch === "message" && (
                          <Grid item xs={12}>
                            <Field
                              as={TextField}
                              label="Mensaje de error de captura"
                              name="captureErrorMessage"
                              variant="outlined"
                              margin="dense"
                              multiline
                              minRows={4}
                              className={classes.textField}
                              helperText="Ejemplo: Necesito que envies el comprobante como archivo para continuar."
                            />
                          </Grid>
                        )}
                      </>
                    )}
                  </>
                )}
                <Grid item xs={12}>
                  <div style={{ maxWidth: 380 }}>
                    <FormControl variant="outlined" className={classes.formControl}>
                      <InputLabel>{i18n.t("chatbotModal.form.chatbotAction")}</InputLabel>
                      <Field
                        as={Select}
                        name="chatbotAction"
                        label={i18n.t("chatbotModal.form.chatbotAction")}
                      >
                        <MenuItem value={NO_CHATBOT_ACTION}>
                          {i18n.t("chatbotModal.chatbotActions.none")}
                        </MenuItem>
                        <MenuItem value="activate">
                          {i18n.t("chatbotModal.chatbotActions.activate")}
                        </MenuItem>
                        <MenuItem value="activateReset">
                          {i18n.t("chatbotModal.chatbotActions.activateReset")}
                        </MenuItem>
                        <MenuItem value="deactivate">
                          {i18n.t("chatbotModal.chatbotActions.deactivate")}
                        </MenuItem>
                        <MenuItem value="deactivateReset">
                          {i18n.t("chatbotModal.chatbotActions.deactivateReset")}
                        </MenuItem>
                      </Field>
                    </FormControl>
                  </div>
                </Grid>
              </Grid>
                </Paper>
              )}

              {canUseOrders && activeConfigSections.includes("orders") && (
                <Paper
                  className={classes.block}
                  elevation={0}
                  style={{ order: getConfigSectionOrder("orders") }}
                >
                  <div className={classes.blockHeader}>
                    <Typography variant="subtitle2">Pedidos</Typography>
                    <div className={classes.blockActions}>
                      <IconButton size="small" onClick={() => removeConfigSection("orders", setFieldValue)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                  <Typography variant="caption" color="textSecondary" display="block">
                    Lista los pedidos anteriores del contacto y permite volver a pedir uno desde el chatbot.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel>Secuencia para volver al menu principal</InputLabel>
                        <Select
                          value={
                            values.ordersMainMenuSequenceId ||
                            NO_ORDERS_MAIN_MENU_SEQUENCE
                          }
                          label="Secuencia para volver al menu principal"
                          MenuProps={sequenceSelectMenuProps}
                          onChange={event => {
                            if (event.target.value === "__create__") {
                              openCreateSequence(
                                null,
                                values.connectionId,
                                "ordersMainMenu",
                                sequenceId =>
                                  setFieldValue("ordersMainMenuSequenceId", sequenceId)
                              );
                              return;
                            }

                            setFieldValue("ordersMainMenuSequenceId", event.target.value);
                          }}
                        >
                          <MenuItem value={NO_ORDERS_MAIN_MENU_SEQUENCE}>
                            Sin secuencia
                          </MenuItem>
                          <MenuItem value="__create__">Crear secuencia...</MenuItem>
                          {sequences
                            .filter(
                              item =>
                                item.id !== sequenceId &&
                                recordOwnsWhatsapp(item, values.connectionId)
                            )
                            .map(item => (
                              <MenuItem key={item.id} value={item.id}>
                                {formatSequenceOption(item)}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      <div className={classes.sequenceActions}>
                        <Button
                          color="primary"
                          disabled={
                            values.ordersMainMenuSequenceId ===
                            NO_ORDERS_MAIN_MENU_SEQUENCE
                          }
                          onClick={() =>
                            openEditSequence(
                              null,
                              values.ordersMainMenuSequenceId,
                              values.connectionId,
                              "ordersMainMenu",
                              sequenceId =>
                                setFieldValue("ordersMainMenuSequenceId", sequenceId)
                            )
                          }
                        >
                          Editar secuencia
                        </Button>
                      </div>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {canUseOrders && activeConfigSections.includes("streamingServices") && (
                <Paper
                  className={classes.block}
                  elevation={0}
                  style={{ order: getConfigSectionOrder("streamingServices") }}
                >
                  <div className={classes.blockHeader}>
                    <Typography variant="subtitle2">Mis servicios</Typography>
                    <div className={classes.blockActions}>
                      <IconButton size="small" onClick={() => removeConfigSection("streamingServices", setFieldValue)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                  <Typography variant="caption" color="textSecondary" display="block">
                    Lista los servicios streaming activos del contacto y permite renovar uno o varios en el mismo carrito.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel>Secuencia para volver al menu principal</InputLabel>
                        <Select
                          value={
                            values.streamingServicesMainMenuSequenceId ||
                            NO_STREAMING_SERVICES_MAIN_MENU_SEQUENCE
                          }
                          label="Secuencia para volver al menu principal"
                          MenuProps={sequenceSelectMenuProps}
                          onChange={event => {
                            if (event.target.value === "__create__") {
                              openCreateSequence(
                                null,
                                values.connectionId,
                                "streamingServicesMainMenu",
                                sequenceId =>
                                  setFieldValue("streamingServicesMainMenuSequenceId", sequenceId)
                              );
                              return;
                            }

                            setFieldValue("streamingServicesMainMenuSequenceId", event.target.value);
                          }}
                        >
                          <MenuItem value={NO_STREAMING_SERVICES_MAIN_MENU_SEQUENCE}>
                            Sin secuencia
                          </MenuItem>
                          <MenuItem value="__create__">Crear secuencia...</MenuItem>
                          {sequences
                            .filter(
                              item =>
                                item.id !== sequenceId &&
                                recordOwnsWhatsapp(item, values.connectionId)
                            )
                            .map(item => (
                              <MenuItem key={item.id} value={item.id}>
                                {formatSequenceOption(item)}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      <div className={classes.sequenceActions}>
                        <Button
                          color="primary"
                          disabled={
                            values.streamingServicesMainMenuSequenceId ===
                            NO_STREAMING_SERVICES_MAIN_MENU_SEQUENCE
                          }
                          onClick={() =>
                            openEditSequence(
                              null,
                              values.streamingServicesMainMenuSequenceId,
                              values.connectionId,
                              "streamingServicesMainMenu",
                              sequenceId =>
                                setFieldValue("streamingServicesMainMenuSequenceId", sequenceId)
                            )
                          }
                        >
                          Editar secuencia
                        </Button>
                      </div>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {activeConfigSections.includes("agendaReservations") && (
                <Paper
                  className={classes.block}
                  elevation={0}
                  style={{ order: getConfigSectionOrder("agendaReservations") }}
                >
                  <div className={classes.blockHeader}>
                    <Typography variant="subtitle2">Mis reservas</Typography>
                    <div className={classes.blockActions}>
                      <IconButton size="small" onClick={() => removeConfigSection("agendaReservations", setFieldValue)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                  <Typography variant="caption" color="textSecondary" display="block">
                    Lista las reservas activas del contacto y permite cancelarlas o reprogramarlas desde el chatbot.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel>Secuencia para volver al menu principal</InputLabel>
                        <Select
                          value={
                            values.agendaReservationsMainMenuSequenceId ||
                            NO_AGENDA_RESERVATIONS_MAIN_MENU_SEQUENCE
                          }
                          label="Secuencia para volver al menu principal"
                          MenuProps={sequenceSelectMenuProps}
                          onChange={event => {
                            if (event.target.value === "__create__") {
                              openCreateSequence(
                                null,
                                values.connectionId,
                                "agendaReservationsMainMenu",
                                sequenceId =>
                                  setFieldValue(
                                    "agendaReservationsMainMenuSequenceId",
                                    sequenceId
                                  )
                              );
                              return;
                            }

                            setFieldValue(
                              "agendaReservationsMainMenuSequenceId",
                              event.target.value
                            );
                          }}
                        >
                          <MenuItem value={NO_AGENDA_RESERVATIONS_MAIN_MENU_SEQUENCE}>
                            Sin secuencia
                          </MenuItem>
                          <MenuItem value="__create__">Crear secuencia...</MenuItem>
                          {sequences
                            .filter(
                              item =>
                                item.id !== sequenceId &&
                                recordOwnsWhatsapp(item, values.connectionId)
                            )
                            .map(item => (
                              <MenuItem key={item.id} value={item.id}>
                                {formatSequenceOption(item)}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      <div className={classes.sequenceActions}>
                        <Button
                          color="primary"
                          disabled={
                            values.agendaReservationsMainMenuSequenceId ===
                            NO_AGENDA_RESERVATIONS_MAIN_MENU_SEQUENCE
                          }
                          onClick={() =>
                            openEditSequence(
                              null,
                              values.agendaReservationsMainMenuSequenceId,
                              values.connectionId,
                              "agendaReservationsMainMenu",
                              sequenceId =>
                                setFieldValue(
                                  "agendaReservationsMainMenuSequenceId",
                                  sequenceId
                                )
                            )
                          }
                        >
                          Editar secuencia
                        </Button>
                      </div>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {activeConfigSections.includes("agenda") && (
                <Paper
                  className={classes.block}
                  elevation={0}
                  style={{ order: getConfigSectionOrder("agenda") }}
                >
                  <div className={classes.blockHeader}>
                    <Typography variant="subtitle2">Agenda</Typography>
                    <div className={classes.blockActions}>
                      <IconButton size="small" onClick={() => removeConfigSection("agenda", setFieldValue)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                  <Typography variant="caption" color="textSecondary" display="block">
                    Permite agendar desde el chatbot mostrando servicios, dias y horarios disponibles, con resumen y correccion antes de confirmar.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel>Servicio</InputLabel>
                        <Select
                          value={values.agendaServiceId || ALL_AGENDA_SERVICES}
                          label="Servicio"
                          onChange={event => setFieldValue("agendaServiceId", event.target.value)}
                        >
                          <MenuItem value={ALL_AGENDA_SERVICES}>
                            Mostrar servicios disponibles
                          </MenuItem>
                          {!agendaServices.filter(
                            service =>
                              !values.connectionId ||
                              recordOwnsWhatsapp(service, values.connectionId)
                          ).length && (
                            <MenuItem value={NO_AGENDA_SERVICE} disabled>
                              No hay servicios para esta conexion
                            </MenuItem>
                          )}
                          {agendaServices
                            .filter(
                              service =>
                                !values.connectionId ||
                                recordOwnsWhatsapp(service, values.connectionId)
                            )
                            .map(service => (
                              <MenuItem key={service.id} value={Number(service.id)}>
                                {service.name}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel>Estado al confirmar</InputLabel>
                        <Select
                          value={values.agendaReservationStatus || "confirmed"}
                          label="Estado al confirmar"
                          onChange={event =>
                            setFieldValue("agendaReservationStatus", event.target.value)
                          }
                        >
                          <MenuItem value="confirmed">Confirmada</MenuItem>
                          <MenuItem value="pending">Pendiente</MenuItem>
                          <MenuItem value="rescheduled">Reprogramada</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel>Secuencia para volver al menu principal</InputLabel>
                        <Select
                          value={
                            values.agendaMainMenuSequenceId ||
                            NO_AGENDA_MAIN_MENU_SEQUENCE
                          }
                          label="Secuencia para volver al menu principal"
                          MenuProps={sequenceSelectMenuProps}
                          onChange={event => {
                            if (event.target.value === "__create__") {
                              openCreateSequence(
                                null,
                                values.connectionId,
                                "agendaMainMenu",
                                sequenceId =>
                                  setFieldValue("agendaMainMenuSequenceId", sequenceId)
                              );
                              return;
                            }

                            setFieldValue("agendaMainMenuSequenceId", event.target.value);
                          }}
                        >
                          <MenuItem value={NO_AGENDA_MAIN_MENU_SEQUENCE}>
                            Sin secuencia
                          </MenuItem>
                          <MenuItem value="__create__">Crear secuencia...</MenuItem>
                          {sequences
                            .filter(
                              item =>
                                item.id !== sequenceId &&
                                recordOwnsWhatsapp(item, values.connectionId)
                            )
                            .map(item => (
                              <MenuItem key={item.id} value={item.id}>
                                {formatSequenceOption(item)}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      <div className={classes.sequenceActions}>
                        <Button
                          color="primary"
                          disabled={
                            values.agendaMainMenuSequenceId ===
                            NO_AGENDA_MAIN_MENU_SEQUENCE
                          }
                          onClick={() =>
                            openEditSequence(
                              null,
                              values.agendaMainMenuSequenceId,
                              values.connectionId,
                              "agendaMainMenu",
                              sequenceId =>
                                setFieldValue("agendaMainMenuSequenceId", sequenceId)
                            )
                          }
                        >
                          Editar secuencia
                        </Button>
                      </div>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {canUseProcesses && activeConfigSections.includes("processes") && (
                <Paper
                  className={classes.block}
                  elevation={0}
                  style={{ order: getConfigSectionOrder("processes") }}
                >
                  <div className={classes.blockHeader}>
                    <Typography variant="subtitle2">Procesos</Typography>
                    <div className={classes.blockActions}>
                      <IconButton size="small" onClick={() => removeConfigSection("processes", setFieldValue)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                  <Typography variant="caption" color="textSecondary" display="block">
                    Ejecuta un proceso desde el chatbot, captura campos, opciones y archivos, y permite corregir datos antes de confirmar.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel>Proceso</InputLabel>
                        <Select
                          value={
                            values.processSequenceProcessId ||
                            NO_PROCESS_SEQUENCE_PROCESS
                          }
                          label="Proceso"
                          onChange={event =>
                            setFieldValue("processSequenceProcessId", event.target.value)
                          }
                        >
                          <MenuItem value={NO_PROCESS_SEQUENCE_PROCESS}>
                            Selecciona un proceso
                          </MenuItem>
                          {availableProcesses
                            .filter(
                              process =>
                                !values.connectionId ||
                                recordOwnsWhatsapp(process, values.connectionId)
                            )
                            .map(process => (
                              <MenuItem key={process.id} value={Number(process.id)}>
                                {process.name}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel>Secuencia para volver al menu principal</InputLabel>
                        <Select
                          value={
                            values.processSequenceMainMenuSequenceId ||
                            NO_PROCESS_MAIN_MENU_SEQUENCE
                          }
                          label="Secuencia para volver al menu principal"
                          MenuProps={sequenceSelectMenuProps}
                          onChange={event => {
                            if (event.target.value === "__create__") {
                              openCreateSequence(
                                null,
                                values.connectionId,
                                "processMainMenu",
                                sequenceId =>
                                  setFieldValue("processSequenceMainMenuSequenceId", sequenceId)
                              );
                              return;
                            }

                            setFieldValue(
                              "processSequenceMainMenuSequenceId",
                              event.target.value
                            );
                          }}
                        >
                          <MenuItem value={NO_PROCESS_MAIN_MENU_SEQUENCE}>
                            Sin secuencia
                          </MenuItem>
                          <MenuItem value="__create__">Crear secuencia...</MenuItem>
                          {sequences
                            .filter(
                              item =>
                                item.id !== sequenceId &&
                                recordOwnsWhatsapp(item, values.connectionId)
                            )
                            .map(item => (
                              <MenuItem key={item.id} value={item.id}>
                                {formatSequenceOption(item)}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      <div className={classes.sequenceActions}>
                        <Button
                          color="primary"
                          disabled={
                            values.processSequenceMainMenuSequenceId ===
                            NO_PROCESS_MAIN_MENU_SEQUENCE
                          }
                          onClick={() =>
                            openEditSequence(
                              null,
                              values.processSequenceMainMenuSequenceId,
                              values.connectionId,
                              "processMainMenu",
                              sequenceId =>
                                setFieldValue("processSequenceMainMenuSequenceId", sequenceId)
                            )
                          }
                        >
                          Editar secuencia
                        </Button>
                      </div>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {canUseOrders && activeConfigSections.includes("cart") && (
                <Paper
                  className={classes.block}
                  elevation={0}
                  style={{ order: getConfigSectionOrder("cart") }}
                >
                  <div className={classes.blockHeader}>
                    <Typography variant="subtitle2">Carrito</Typography>
                    <div className={classes.blockActions}>
                      <IconButton size="small" onClick={() => removeConfigSection("cart", setFieldValue)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                  <Typography variant="caption" color="textSecondary" display="block">
                    Usa el catalogo de pedidos para mostrar categorias, productos, opciones, cantidades, editar, eliminar y confirmar el pedido desde el chatbot.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel>Secuencia para volver al menu principal</InputLabel>
                        <Select
                          value={
                            values.cartMainMenuSequenceId ||
                            NO_CART_MAIN_MENU_SEQUENCE
                          }
                          label="Secuencia para volver al menu principal"
                          MenuProps={sequenceSelectMenuProps}
                          onChange={event => {
                            if (event.target.value === "__create__") {
                              openCreateSequence(
                                null,
                                values.connectionId,
                                "cartMainMenu",
                                sequenceId =>
                                  setFieldValue("cartMainMenuSequenceId", sequenceId)
                              );
                              return;
                            }

                            setFieldValue("cartMainMenuSequenceId", event.target.value);
                          }}
                        >
                          <MenuItem value={NO_CART_MAIN_MENU_SEQUENCE}>
                            Sin secuencia
                          </MenuItem>
                          <MenuItem value="__create__">Crear secuencia...</MenuItem>
                          {sequences
                            .filter(
                              item =>
                                item.id !== sequenceId &&
                                recordOwnsWhatsapp(item, values.connectionId)
                            )
                            .map(item => (
                              <MenuItem key={item.id} value={item.id}>
                                {formatSequenceOption(item)}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      <div className={classes.sequenceActions}>
                        <Button
                          color="primary"
                          disabled={
                            values.cartMainMenuSequenceId ===
                            NO_CART_MAIN_MENU_SEQUENCE
                          }
                          onClick={() =>
                            openEditSequence(
                              null,
                              values.cartMainMenuSequenceId,
                              values.connectionId,
                              "cartMainMenu",
                              sequenceId =>
                                setFieldValue("cartMainMenuSequenceId", sequenceId)
                            )
                          }
                        >
                          Editar secuencia
                        </Button>
                      </div>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {canUseOrders && activeConfigSections.includes("product") && (
                <Paper
                  className={classes.block}
                  elevation={0}
                  style={{ order: getConfigSectionOrder("product") }}
                >
                  <div className={classes.blockHeader}>
                    <Typography variant="subtitle2">Producto</Typography>
                    <div className={classes.blockActions}>
                      <IconButton size="small" onClick={() => removeConfigSection("product", setFieldValue)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                  <Typography variant="caption" color="textSecondary" display="block">
                    Inicia un producto especifico, muestra su imagen, descripcion y opciones comerciales configuradas en el producto. Comprar ahora continua con el flujo normal del carrito.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel>Producto</InputLabel>
                        <Select
                          value={values.productId || NO_PRODUCT}
                          label="Producto"
                          onChange={event =>
                            setFieldValue("productId", event.target.value)
                          }
                        >
                          <MenuItem value={NO_PRODUCT}>Selecciona un producto</MenuItem>
                          {products
                            .filter(
                              product =>
                                recordOwnsWhatsapp(product, values.connectionId) ||
                                String(product.id) === String(values.productId || "")
                            )
                            .map(product => (
                              <MenuItem key={product.id} value={product.id}>
                                {product.name}
                                {product.price ? ` - ${product.price}` : ""}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel>Secuencia despues de la imagen</InputLabel>
                        <Select
                          value={
                            values.productIntroSequenceId ||
                            NO_PRODUCT_INTRO_SEQUENCE
                          }
                          label="Secuencia despues de la imagen"
                          MenuProps={sequenceSelectMenuProps}
                          onChange={event => {
                            if (event.target.value === "__create__") {
                              openCreateSequence(
                                null,
                                values.connectionId,
                                "productIntro",
                                sequenceId =>
                                  setFieldValue("productIntroSequenceId", sequenceId)
                              );
                              return;
                            }

                            setFieldValue("productIntroSequenceId", event.target.value);
                          }}
                        >
                          <MenuItem value={NO_PRODUCT_INTRO_SEQUENCE}>
                            Sin secuencia
                          </MenuItem>
                          <MenuItem value="__create__">Crear secuencia...</MenuItem>
                          {sequences
                            .filter(
                              item =>
                                item.id !== sequenceId &&
                                recordOwnsWhatsapp(item, values.connectionId)
                            )
                            .map(item => (
                              <MenuItem key={item.id} value={item.id}>
                                {formatSequenceOption(item)}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      <div className={classes.sequenceActions}>
                        <Button
                          color="primary"
                          disabled={
                            values.productIntroSequenceId ===
                            NO_PRODUCT_INTRO_SEQUENCE
                          }
                          onClick={() =>
                            openEditSequence(
                              null,
                              values.productIntroSequenceId,
                              values.connectionId,
                              "productIntro",
                              sequenceId =>
                                setFieldValue("productIntroSequenceId", sequenceId)
                            )
                          }
                        >
                          Editar secuencia
                        </Button>
                      </div>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel>Secuencia para volver al menu principal</InputLabel>
                        <Select
                          value={
                            values.productMainMenuSequenceId ||
                            NO_PRODUCT_MAIN_MENU_SEQUENCE
                          }
                          label="Secuencia para volver al menu principal"
                          MenuProps={sequenceSelectMenuProps}
                          onChange={event => {
                            if (event.target.value === "__create__") {
                              openCreateSequence(
                                null,
                                values.connectionId,
                                "productMainMenu",
                                sequenceId =>
                                  setFieldValue("productMainMenuSequenceId", sequenceId)
                              );
                              return;
                            }

                            setFieldValue("productMainMenuSequenceId", event.target.value);
                          }}
                        >
                          <MenuItem value={NO_PRODUCT_MAIN_MENU_SEQUENCE}>
                            Sin secuencia
                          </MenuItem>
                          <MenuItem value="__create__">Crear secuencia...</MenuItem>
                          {sequences
                            .filter(
                              item =>
                                item.id !== sequenceId &&
                                recordOwnsWhatsapp(item, values.connectionId)
                            )
                            .map(item => (
                              <MenuItem key={item.id} value={item.id}>
                                {formatSequenceOption(item)}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      <div className={classes.sequenceActions}>
                        <Button
                          color="primary"
                          disabled={
                            values.productMainMenuSequenceId ===
                            NO_PRODUCT_MAIN_MENU_SEQUENCE
                          }
                          onClick={() =>
                            openEditSequence(
                              null,
                              values.productMainMenuSequenceId,
                              values.connectionId,
                              "productMainMenu",
                              sequenceId =>
                                setFieldValue("productMainMenuSequenceId", sequenceId)
                            )
                          }
                        >
                          Editar secuencia
                        </Button>
                      </div>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {activeConfigSections.includes("quickReply") && (
                <Paper
                  className={classes.block}
                  elevation={0}
                  style={{ order: getConfigSectionOrder("quickReply") }}
                >
                  <div className={classes.blockHeader}>
                    <Typography variant="subtitle2">
                      {i18n.t("chatbotModal.sections.quickReply")}
                    </Typography>
                    <div className={classes.blockActions}>
                      <IconButton size="small" onClick={() => removeConfigSection("quickReply", setFieldValue)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
              <Typography className={classes.sectionTitle} style={{ display: "none" }}>
                {i18n.t("chatbotModal.sections.quickReply")}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.quickReplyEnabled}
                    onChange={event => setFieldValue("quickReplyEnabled", event.target.checked)}
                    color="primary"
                  />
                }
                label={i18n.t("chatbotModal.form.quickReplyEnabled")}
              />
              {values.quickReplyEnabled && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl variant="outlined" className={classes.formControl}>
                      <InputLabel>{i18n.t("chatbotModal.form.quickReplyMatchType")}</InputLabel>
                      <Field
                        as={Select}
                        name="quickReplyMatchType"
                        label={i18n.t("chatbotModal.form.quickReplyMatchType")}
                      >
                        <MenuItem value="exact">{i18n.t("chatbotModal.matchTypes.exact")}</MenuItem>
                        <MenuItem value="contains">{i18n.t("chatbotModal.matchTypes.contains")}</MenuItem>
                        <MenuItem value="conditional">{i18n.t("chatbotModal.matchTypes.conditional")}</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Field
                      as={TextField}
                      label={i18n.t("chatbotModal.form.quickReplyTrigger")}
                      name="quickReplyTrigger"
                      variant="outlined"
                      margin="dense"
                      multiline
                      minRows={4}
                      className={classes.textField}
                      helperText={
                        values.quickReplyMatchType === "conditional"
                          ? i18n.t("chatbotModal.form.conditionalTriggerHelp")
                          : i18n.t("chatbotModal.form.triggerHelp")
                      }
                    />
                  </Grid>
                </Grid>
              )}
                </Paper>
              )}

              {activeConfigSections.includes("schedule") && (
                <Paper
                  className={classes.block}
                  elevation={0}
                  style={{ order: getConfigSectionOrder("schedule") }}
                >
                  <div className={classes.blockHeader}>
                    <Typography variant="subtitle2">
                      {i18n.t("chatbotModal.sections.schedule")}
                    </Typography>
                    <div className={classes.blockActions}>
                      <IconButton size="small" onClick={() => removeConfigSection("schedule", setFieldValue)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.scheduleEnabled}
                        onChange={event => setFieldValue("scheduleEnabled", event.target.checked)}
                        color="primary"
                      />
                    }
                    label={i18n.t("chatbotModal.form.scheduleEnabled")}
                  />
                  {values.scheduleEnabled && (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2">
                          Configurar horarios por dia:
                        </Typography>
                        <div className={classes.scheduleDayList}>
                          {(values.scheduleDays || createScheduleDays()).map((scheduleDay, dayIndex) => {
                            const dayLabel =
                              weekDays.find(day => day.value === Number(scheduleDay.day))?.label ||
                              scheduleDay.day;

                            return (
                              <div
                                className={classes.scheduleDayRow}
                                key={`schedule-day-${scheduleDay.day}`}
                              >
                                <FormControlLabel
                                  className={classes.scheduleDayLabel}
                                  control={
                                    <Checkbox
                                      checked={Boolean(scheduleDay.enabled)}
                                      onChange={event => {
                                        const nextDays = [...(values.scheduleDays || createScheduleDays())];
                                        nextDays[dayIndex] = {
                                          ...nextDays[dayIndex],
                                          enabled: event.target.checked,
                                        };
                                        setFieldValue("scheduleDays", nextDays);
                                      }}
                                      color="primary"
                                    />
                                  }
                                  label={dayLabel}
                                />
                                <TextField
                                  type="time"
                                  value={scheduleDay.startTime || "09:00"}
                                  disabled={!scheduleDay.enabled}
                                  variant="outlined"
                                  margin="dense"
                                  fullWidth
                                  InputLabelProps={{ shrink: true }}
                                  onChange={event => {
                                    const nextDays = [...(values.scheduleDays || createScheduleDays())];
                                    nextDays[dayIndex] = {
                                      ...nextDays[dayIndex],
                                      startTime: event.target.value,
                                    };
                                    setFieldValue("scheduleDays", nextDays);
                                  }}
                                />
                                <Typography className={classes.scheduleSeparator}>
                                  a
                                </Typography>
                                <TextField
                                  type="time"
                                  value={scheduleDay.endTime || "18:00"}
                                  disabled={!scheduleDay.enabled}
                                  variant="outlined"
                                  margin="dense"
                                  fullWidth
                                  InputLabelProps={{ shrink: true }}
                                  onChange={event => {
                                    const nextDays = [...(values.scheduleDays || createScheduleDays())];
                                    nextDays[dayIndex] = {
                                      ...nextDays[dayIndex],
                                      endTime: event.target.value,
                                    };
                                    setFieldValue("scheduleDays", nextDays);
                                  }}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </Grid>
                      <Grid item xs={12}>
                        {renderOutOfHoursSequenceSelector(values, setFieldValue)}
                      </Grid>
                    </Grid>
                  )}
                </Paper>
              )}

              {canUseLabels && activeConfigSections.includes("labelCondition") && (
                <Paper
                  className={classes.block}
                  elevation={0}
                  style={{ order: getConfigSectionOrder("labelCondition") }}
                >
                  <div className={classes.blockHeader}>
                    <Typography variant="subtitle2">
                      Condicion de etiquetas
                    </Typography>
                    <div className={classes.blockActions}>
                      <IconButton size="small" onClick={() => removeConfigSection("labelCondition", setFieldValue)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                      <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel>Tipo de condicion</InputLabel>
                        <Select
                          value={values.labelConditionType || LABEL_CONDITION_NONE}
                          label="Tipo de condicion"
                          onChange={event => {
                            const nextType = event.target.value;
                            const availableLabelIds = labels
                              .filter(label => recordOwnsWhatsapp(label, values.connectionId))
                              .map(label => label.id);
                            setFieldValue("labelConditionType", nextType);
                            setFieldValue(
                              "labelConditionIds",
                              nextType === LABEL_CONDITION_NONE
                                ? []
                                : (values.labelConditionIds || []).length
                                ? values.labelConditionIds
                                : availableLabelIds.slice(0, 1)
                            );
                          }}
                        >
                          <MenuItem value={LABEL_CONDITION_NONE}>Sin condicion de etiquetas</MenuItem>
                          <MenuItem value="has_all">Debe tener estas etiquetas</MenuItem>
                          <MenuItem value="has_none">NO debe tener estas etiquetas</MenuItem>
                          <MenuItem value="has_any">Debe tener al menos una de estas etiquetas</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {values.labelConditionType !== LABEL_CONDITION_NONE && (
                      <Grid item xs={12} md={7}>
                        {labels.filter(label => recordOwnsWhatsapp(label, values.connectionId)).length ? (
                          <FormControl variant="outlined" className={classes.formControl}>
                            <InputLabel>Etiquetas</InputLabel>
                            <Select
                              multiple
                              value={
                                (values.labelConditionIds || []).length
                                  ? values.labelConditionIds
                                  : [NO_LABEL_CONDITION_LABEL]
                              }
                              label="Etiquetas"
                              onChange={event =>
                                setFieldValue(
                                  "labelConditionIds",
                                  event.target.value.filter(
                                    value => value !== NO_LABEL_CONDITION_LABEL
                                  )
                                )
                              }
                              renderValue={selected => {
                                if (selected.includes(NO_LABEL_CONDITION_LABEL)) {
                                  return "Selecciona etiquetas";
                                }

                                return labels
                                  .filter(label => selected.includes(label.id))
                                  .map(label => label.name)
                                  .join(", ");
                              }}
                            >
                              <MenuItem value={NO_LABEL_CONDITION_LABEL}>
                                <Checkbox checked={false} />
                                <ListItemText primary="Selecciona etiquetas" />
                              </MenuItem>
                              {labels
                                .filter(label => recordOwnsWhatsapp(label, values.connectionId))
                                .map(label => (
                                  <MenuItem key={label.id} value={label.id}>
                                    <Checkbox checked={(values.labelConditionIds || []).indexOf(label.id) > -1} />
                                    <ListItemText primary={label.name} />
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography color="textSecondary" style={{ marginTop: 18 }}>
                            No hay etiquetas disponibles para esta conexion.
                          </Typography>
                        )}
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary">
                        La secuencia solo se ejecutara si el ticket cumple con la condicion seleccionada.
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {activeConfigSections.includes("execution") && (
                <Paper
                  className={classes.block}
                  elevation={0}
                  style={{ order: getConfigSectionOrder("execution") }}
                >
                  <div className={classes.blockHeader}>
                    <Typography variant="subtitle2">
                      {i18n.t("chatbotModal.sections.execution")}
                    </Typography>
                    <div className={classes.blockActions}>
                      <IconButton size="small" onClick={() => removeConfigSection("execution", setFieldValue)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
              <Typography className={classes.sectionTitle} style={{ display: "none" }}>
                {i18n.t("chatbotModal.sections.execution")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl variant="outlined" className={classes.formControl}>
                    <InputLabel>{i18n.t("chatbotModal.form.executionMode")}</InputLabel>
                    <Field as={Select} name="executionMode" label={i18n.t("chatbotModal.form.executionMode")}>
                      <MenuItem value="unlimited">{i18n.t("chatbotModal.executionModes.unlimited")}</MenuItem>
                      <MenuItem value="executionCount">{i18n.t("chatbotModal.executionModes.executionCount")}</MenuItem>
                      <MenuItem value="timeWindow">{i18n.t("chatbotModal.executionModes.timeWindow")}</MenuItem>
                    </Field>
                  </FormControl>
                </Grid>
                {values.executionMode === "executionCount" && (
                  <Grid item xs={12} md={4}>
                    <Field
                      as={TextField}
                      label={i18n.t("chatbotModal.form.executionLimit")}
                      name="executionLimit"
                      type="number"
                      variant="outlined"
                      margin="dense"
                      className={classes.textField}
                    />
                  </Grid>
                )}
                {values.executionMode === "timeWindow" && (
                  <>
                    <Grid item xs={12} md={4}>
                      <Field
                        as={TextField}
                        label={i18n.t("chatbotModal.form.executionInterval")}
                        name="executionInterval"
                        type="number"
                        variant="outlined"
                        margin="dense"
                        className={classes.textField}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel>{i18n.t("chatbotModal.form.executionIntervalUnit")}</InputLabel>
                        <Field as={Select} name="executionIntervalUnit" label={i18n.t("chatbotModal.form.executionIntervalUnit")}>
                          <MenuItem value="seconds">{i18n.t("chatbotModal.units.seconds")}</MenuItem>
                          <MenuItem value="minutes">{i18n.t("chatbotModal.units.minutes")}</MenuItem>
                          <MenuItem value="hours">{i18n.t("chatbotModal.units.hours")}</MenuItem>
                          <MenuItem value="days">{i18n.t("chatbotModal.units.days")}</MenuItem>
                          <MenuItem value="months">{i18n.t("chatbotModal.units.months")}</MenuItem>
                        </Field>
                      </FormControl>
                    </Grid>
                  </>
                )}
              </Grid>
                </Paper>
              )}

              {canUseLabels && (
                <>
                  {canUseTransfer && activeConfigSections.includes("transfer") && (
                    <Paper
                      className={classes.block}
                      elevation={0}
                      style={{ order: getConfigSectionOrder("transfer") }}
                    >
                      <div className={classes.blockHeader}>
                        <Typography variant="subtitle2">
                          {i18n.t("chatbotModal.sections.transfer")}
                        </Typography>
                        <div className={classes.blockActions}>
                          <IconButton size="small" onClick={() => removeConfigSection("transfer", setFieldValue)}>
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                  <Typography className={classes.sectionTitle} style={{ display: "none" }}>
                    {i18n.t("chatbotModal.sections.transfer")}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel>{i18n.t("chatbotModal.form.transferType")}</InputLabel>
                        <Field
                          as={Select}
                          name="transferType"
                          label={i18n.t("chatbotModal.form.transferType")}
                          onChange={event => {
                            setFieldValue("transferType", event.target.value);
                            if (!["queue", "both"].includes(event.target.value)) {
                              setFieldValue("transferQueueId", NO_TRANSFER_QUEUE);
                            }
                            if (!["user", "both"].includes(event.target.value)) {
                              setFieldValue("transferUserId", NO_TRANSFER_USER);
                            }
                            if (event.target.value !== TRANSFER_AUTO_ASSIGN) {
                              setFieldValue("transferAutoAssignEnabled", false);
                              setFieldValue("transferAutoAssignQueueIds", []);
                              setFieldValue("transferAutoAssignUserIds", []);
                            } else {
                              setFieldValue("transferAutoAssignEnabled", true);
                            }
                          }}
                        >
                          <MenuItem value={NO_TRANSFER_TYPE}>{i18n.t("chatbotModal.transferTypes.none")}</MenuItem>
                          <MenuItem value="queue">{i18n.t("chatbotModal.transferTypes.queue")}</MenuItem>
                          <MenuItem value="user">{i18n.t("chatbotModal.transferTypes.user")}</MenuItem>
                          <MenuItem value="both">{i18n.t("chatbotModal.transferTypes.both")}</MenuItem>
                          <MenuItem value={TRANSFER_AUTO_ASSIGN}>{i18n.t("chatbotModal.transferTypes.auto")}</MenuItem>
                          <MenuItem value="resolve">{i18n.t("chatbotModal.transferTypes.resolve")}</MenuItem>
                        </Field>
                      </FormControl>
                    </Grid>
                    {["queue", "both"].includes(values.transferType) && (
                      <Grid item xs={12} md={4}>
                        <FormControl variant="outlined" className={classes.formControl}>
                          <InputLabel>{i18n.t("chatbotModal.form.transferQueue")}</InputLabel>
                          <Field
                            as={Select}
                            name="transferQueueId"
                            label={i18n.t("chatbotModal.form.transferQueue")}
                          >
                            <MenuItem value={NO_TRANSFER_QUEUE}>Sin área</MenuItem>
                            {queues
                              .filter(queue =>
                                recordOwnsWhatsapp(
                                  { ...queue, connectionId: queue.connectionId || queue.connection?.id },
                                  values.connectionId
                                )
                              )
                              .map(queue => (
                              <MenuItem key={queue.id} value={queue.id}>
                                {queue.name}
                              </MenuItem>
                            ))}
                          </Field>
                        </FormControl>
                      </Grid>
                    )}
                    {["user", "both"].includes(values.transferType) && (
                      <Grid item xs={12} md={4}>
                        <FormControl variant="outlined" className={classes.formControl}>
                          <InputLabel>{i18n.t("chatbotModal.form.transferUser")}</InputLabel>
                          <Field
                            as={Select}
                            name="transferUserId"
                            label={i18n.t("chatbotModal.form.transferUser")}
                          >
                            <MenuItem value={NO_TRANSFER_USER}>Sin usuario</MenuItem>
                            {users.map(user => (
                              <MenuItem key={user.id} value={user.id}>
                                {user.name}
                              </MenuItem>
                            ))}
                          </Field>
                        </FormControl>
                      </Grid>
                    )}
                    {values.transferType === TRANSFER_AUTO_ASSIGN && (
                      <>
                        <Grid item xs={12} md={4}>
                          <FormControl variant="outlined" className={classes.formControl}>
                            <InputLabel>{i18n.t("chatbotModal.form.transferQueue")}</InputLabel>
                            <Select
                              multiple
                              value={values.transferAutoAssignQueueIds || []}
                              label={i18n.t("chatbotModal.form.transferQueue")}
                              onChange={event =>
                                setFieldValue(
                                  "transferAutoAssignQueueIds",
                                  normalizeConnectionIds(event.target.value)
                                )
                              }
                              renderValue={selected => {
                                const ids = normalizeConnectionIds(selected);
                                if (!ids.length) return i18n.t("chatbotModal.form.noTransferAutoAssignQueues");
                                return ids
                                  .map(id => queues.find(queue => Number(queue.id) === Number(id))?.name)
                                  .filter(Boolean)
                                  .join(", ");
                              }}
                            >
                              {queues
                                .filter(queue =>
                                  recordOwnsWhatsapp(
                                    { ...queue, connectionId: queue.connectionId || queue.connection?.id },
                                    values.connectionId
                                  )
                                )
                                .map(queue => (
                                  <MenuItem key={queue.id} value={queue.id}>
                                    <Checkbox
                                      checked={normalizeConnectionIds(values.transferAutoAssignQueueIds).includes(Number(queue.id))}
                                    />
                                    <ListItemText primary={queue.name} />
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <FormControl variant="outlined" className={classes.formControl}>
                            <InputLabel>{i18n.t("chatbotModal.form.transferUser")}</InputLabel>
                            <Select
                              multiple
                              value={values.transferAutoAssignUserIds || []}
                              label={i18n.t("chatbotModal.form.transferUser")}
                              onChange={event =>
                                setFieldValue(
                                  "transferAutoAssignUserIds",
                                  normalizeConnectionIds(event.target.value)
                                )
                              }
                              renderValue={selected => {
                                const ids = normalizeConnectionIds(selected);
                                if (!ids.length) return i18n.t("chatbotModal.form.noTransferAutoAssignUsers");
                                return ids
                                  .map(id => users.find(user => Number(user.id) === Number(id))?.name)
                                  .filter(Boolean)
                                  .join(", ");
                              }}
                            >
                              {users.map(user => (
                                <MenuItem key={user.id} value={user.id}>
                                  <Checkbox
                                    checked={normalizeConnectionIds(values.transferAutoAssignUserIds).includes(Number(user.id))}
                                  />
                                  <ListItemText primary={user.name} />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        {!normalizeConnectionIds(values.transferAutoAssignQueueIds).length &&
                          !normalizeConnectionIds(values.transferAutoAssignUserIds).length && (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="error">
                                {i18n.t("chatbotModal.form.transferAutoAssignRequired")}
                              </Typography>
                            </Grid>
                          )}
                      </>
                    )}
                  </Grid>
                    </Paper>
                  )}

                  {activeConfigSections.includes("labels") && (
                    <Paper
                      className={classes.block}
                      elevation={0}
                      style={{ order: getConfigSectionOrder("labels") }}
                    >
                      <div className={classes.blockHeader}>
                        <Typography variant="subtitle2">
                          {i18n.t("chatbotModal.sections.labels")}
                        </Typography>
                        <div className={classes.blockActions}>
                          <IconButton size="small" onClick={() => removeConfigSection("labels", setFieldValue)}>
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                  <Typography className={classes.sectionTitle} style={{ display: "none" }}>
                    {i18n.t("chatbotModal.sections.labels")}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel>{i18n.t("chatbotModal.form.addLabels")}</InputLabel>
                        <Select
                          multiple
                          value={values.addLabelIds || []}
                          label={i18n.t("chatbotModal.form.addLabels")}
                          onChange={event => setFieldValue("addLabelIds", event.target.value)}
                          renderValue={selected =>
                            labels
                              .filter(label => selected.includes(label.id))
                              .map(label => label.name)
                              .join(", ")
                          }
                        >
                          {labels
                            .filter(label => recordOwnsWhatsapp(label, values.connectionId))
                            .map(label => (
                            <MenuItem key={label.id} value={label.id}>
                              <Checkbox checked={(values.addLabelIds || []).indexOf(label.id) > -1} />
                              <ListItemText primary={label.name} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel>{i18n.t("chatbotModal.form.removeLabels")}</InputLabel>
                        <Select
                          multiple
                          value={values.removeLabelIds || []}
                          label={i18n.t("chatbotModal.form.removeLabels")}
                          onChange={event => setFieldValue("removeLabelIds", event.target.value)}
                          renderValue={selected =>
                            labels
                              .filter(label => selected.includes(label.id))
                              .map(label => label.name)
                              .join(", ")
                          }
                        >
                          {labels
                            .filter(label => recordOwnsWhatsapp(label, values.connectionId))
                            .map(label => (
                            <MenuItem key={label.id} value={label.id}>
                              <Checkbox checked={(values.removeLabelIds || []).indexOf(label.id) > -1} />
                              <ListItemText primary={label.name} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                    </Paper>
                  )}
                </>
              )}

              {canUseFunnels && activeConfigSections.includes("funnel") && (
                <>
                  <Paper
                    className={classes.block}
                    elevation={0}
                    style={{ order: getConfigSectionOrder("funnel") }}
                  >
                    <div className={classes.blockHeader}>
                      <Typography variant="subtitle2">
                        Embudo
                      </Typography>
                      <div className={classes.blockActions}>
                        <IconButton size="small" onClick={() => removeConfigSection("funnel", setFieldValue)}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel>Embudo</InputLabel>
                        <Field
                          as={Select}
                          name="funnelActionFunnelId"
                          label="Embudo"
                          onChange={event => {
                            setFieldValue("funnelActionFunnelId", event.target.value);
                            setFieldValue("funnelActionStageId", NO_FUNNEL_STAGE);
                          }}
                        >
                          <MenuItem value={NO_FUNNEL_ACTION}>Sin embudo</MenuItem>
                          {funnels.map(funnel => (
                            <MenuItem key={funnel.id} value={funnel.id}>
                              {funnel.name}
                            </MenuItem>
                          ))}
                        </Field>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl
                        variant="outlined"
                        className={classes.formControl}
                        disabled={values.funnelActionFunnelId === NO_FUNNEL_ACTION}
                      >
                        <InputLabel>Etapa</InputLabel>
                        <Field
                          as={Select}
                          name="funnelActionStageId"
                          label="Etapa"
                        >
                          <MenuItem value={NO_FUNNEL_STAGE}>Sin etapa</MenuItem>
                          {getFunnelStages(values.funnelActionFunnelId).map(stage => (
                            <MenuItem key={stage.id} value={stage.id}>
                              {stage.name}
                            </MenuItem>
                          ))}
                        </Field>
                      </FormControl>
                    </Grid>
                  </Grid>
                  </Paper>
                </>
              )}

              {canUseAi && activeConfigSections.includes("ai") && (
                <>
                  <Paper
                    className={classes.block}
                    elevation={0}
                    style={{ order: getConfigSectionOrder("ai") }}
                  >
                    <div className={classes.blockHeader}>
                      <Typography variant="subtitle2">
                        {i18n.t("chatbotModal.sections.ai")}
                      </Typography>
                      <div className={classes.blockActions}>
                        <IconButton size="small" onClick={() => removeConfigSection("ai", setFieldValue)}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel>{i18n.t("chatbotModal.form.aiAction")}</InputLabel>
                        <Field
                          as={Select}
                          name="aiAction"
                          label={i18n.t("chatbotModal.form.aiAction")}
                          onChange={event => {
                            setFieldValue("aiAction", event.target.value);
                            if (!["activate", "activateReset"].includes(event.target.value)) {
                              setFieldValue("aiInitialMessage", "");
                              setFieldValue("aiPrompt", "");
                              setFieldValue("orchestratorModeEnabled", false);
                              setFieldValue("useSequencesTool", true);
                              setFieldValue("useWasheetsTool", canUseWasheets);
                              setFieldValue("useWebSearchTool", true);
                              setFieldValue("useAgendaTool", canUseAgenda);
                              setFieldValue("useOrdersTool", canUseOrders);
                              setFieldValue("useWebhookTools", canUseWebhooks);
                              setFieldValue("useContactTool", true);
                              setFieldValue("useProcessesTool", false);
                              setFieldValue("selectedProcessIds", []);
                              setFieldValue("naturalMessageModeEnabled", true);
                            }
                          }}
                        >
                          <MenuItem value={NO_AI_ACTION}>{i18n.t("chatbotModal.aiActions.none")}</MenuItem>
                          <MenuItem value="activate">{i18n.t("chatbotModal.aiActions.activate")}</MenuItem>
                          <MenuItem value="activateReset">{i18n.t("chatbotModal.aiActions.activateReset")}</MenuItem>
                          <MenuItem value="deactivate">{i18n.t("chatbotModal.aiActions.deactivate")}</MenuItem>
                          <MenuItem value="deactivateReset">{i18n.t("chatbotModal.aiActions.deactivateReset")}</MenuItem>
                        </Field>
                      </FormControl>
                    </Grid>
                    {["activate", "activateReset"].includes(values.aiAction) && (
                      <>
                        <Grid item xs={12} md={8}>
                          <Field
                            as={TextField}
                            label={i18n.t("chatbotModal.form.aiInitialMessage")}
                            name="aiInitialMessage"
                            variant="outlined"
                            margin="dense"
                            multiline
                            minRows={4}
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Field
                            as={TextField}
                            label={i18n.t("chatbotModal.form.aiPrompt")}
                            name="aiPrompt"
                            variant="outlined"
                            margin="dense"
                            multiline
                            minRows={6}
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={values.orchestratorModeEnabled === true}
                                onChange={event =>
                                  setFieldValue("orchestratorModeEnabled", event.target.checked)
                                }
                                color="primary"
                              />
                            }
                            label="Modo Orquestador"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography className={classes.sectionTitle}>
                            Funciones
                          </Typography>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={values.naturalMessageModeEnabled === true}
                                onChange={event =>
                                  setFieldValue("naturalMessageModeEnabled", event.target.checked)
                                }
                                color="primary"
                              />
                            }
                            label="Mensajes naturales"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={values.useSequencesTool !== false}
                                onChange={event =>
                                  setFieldValue("useSequencesTool", event.target.checked)
                                }
                                color="primary"
                              />
                            }
                            label="Secuencias"
                          />
                          {canUseWasheets && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={values.useWasheetsTool !== false}
                                  onChange={event =>
                                    setFieldValue("useWasheetsTool", event.target.checked)
                                  }
                                  color="primary"
                                />
                              }
                              label="Formularios Google Sheets"
                            />
                          )}
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={values.useWebSearchTool !== false}
                                onChange={event =>
                                  setFieldValue("useWebSearchTool", event.target.checked)
                                }
                                color="primary"
                              />
                            }
                            label="Búsqueda web"
                          />
                          {canUseAgenda && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={values.useAgendaTool !== false}
                                  onChange={event =>
                                    setFieldValue("useAgendaTool", event.target.checked)
                                  }
                                  color="primary"
                                />
                              }
                              label="Agendamiento"
                            />
                          )}
                          {canUseOrders && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={values.useOrdersTool !== false}
                                  onChange={event =>
                                    setFieldValue("useOrdersTool", event.target.checked)
                                  }
                                  color="primary"
                                />
                              }
                              label="Pedidos"
                            />
                          )}
                          {canUseWebhooks && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={values.useWebhookTools !== false}
                                  onChange={event =>
                                    setFieldValue("useWebhookTools", event.target.checked)
                                  }
                                  color="primary"
                                />
                              }
                              label="Webhooks IA"
                            />
                          )}
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={values.useContactTool !== false}
                                onChange={event =>
                                  setFieldValue("useContactTool", event.target.checked)
                                }
                                color="primary"
                              />
                            }
                            label="Datos de Contacto"
                          />
                          {canUseProcesses && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={values.useProcessesTool === true}
                                  onChange={event => {
                                    setFieldValue("useProcessesTool", event.target.checked);
                                    if (!event.target.checked) {
                                      setFieldValue("selectedProcessIds", []);
                                    }
                                  }}
                                  color="primary"
                                />
                              }
                              label="Procesos IA"
                            />
                          )}
                          {canUseProcesses && values.useProcessesTool === true && (
                            <FormControl variant="outlined" className={classes.formControl}>
                              <InputLabel>Procesos a listar</InputLabel>
                              <Select
                                multiple
                                label="Procesos a listar"
                                value={
                                  normalizeConnectionIds(values.selectedProcessIds).length
                                    ? normalizeConnectionIds(values.selectedProcessIds)
                                    : [ALL_AI_PROCESSES]
                                }
                                renderValue={selected =>
                                  selectedProcessLabels(availableProcesses, selected)
                                }
                                onChange={event =>
                                  setFieldValue(
                                    "selectedProcessIds",
                                    normalizeConnectionIds(event.target.value)
                                  )
                                }
                              >
                                <MenuItem value={ALL_AI_PROCESSES} disabled>
                                  <Checkbox
                                    checked={!normalizeConnectionIds(values.selectedProcessIds).length}
                                    color="primary"
                                  />
                                  <ListItemText primary="Todos los procesos" />
                                </MenuItem>
                                {availableProcesses
                                  .filter(
                                    process =>
                                      !values.connectionId ||
                                      recordOwnsWhatsapp(process, values.connectionId)
                                  )
                                  .map(process => (
                                    <MenuItem key={process.id} value={Number(process.id)}>
                                      <Checkbox
                                        checked={normalizeConnectionIds(
                                          values.selectedProcessIds
                                        ).includes(Number(process.id))}
                                        color="primary"
                                      />
                                      <ListItemText primary={process.name} />
                                    </MenuItem>
                                  ))}
                                {!availableProcesses.filter(
                                  process =>
                                    !values.connectionId ||
                                    recordOwnsWhatsapp(process, values.connectionId)
                                ).length && (
                                  <MenuItem disabled>No hay procesos disponibles</MenuItem>
                                )}
                              </Select>
                            </FormControl>
                          )}
                        </Grid>
                      </>
                    )}
                  </Grid>
                  </Paper>
                </>
              )}

              <div className={classes.configAddArea}>
                <IconButton
                  className={classes.configAddButton}
                  onClick={event => setConfigMenuAnchor(event.currentTarget)}
                  aria-label="Agregar configuracion"
                >
                  <Add />
                </IconButton>
                <Menu
                  anchorEl={configMenuAnchor}
                  open={Boolean(configMenuAnchor)}
                  onClose={() => setConfigMenuAnchor(null)}
                >
                  {configSectionOptions.map(option => (
                    <MenuItem
                      key={option.key}
                      disabled={activeConfigSections.includes(option.key)}
                      onClick={() => addConfigSection(option.key, setFieldValue)}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Menu>
              </div>

              </div>

              <div className={classes.messageBuilder}>
              {blocks.map((block, index) => (
                <Paper className={classes.block} elevation={0} key={`chatbot-block-${index}`}>
                  <div className={classes.blockHeader}>
                    <Typography variant="subtitle2">
                      {getBlockLabel(block)} {index + 1}
                    </Typography>
                    <div className={classes.blockActions}>
                      <IconButton size="small" onClick={() => moveBlock(index, -1)} disabled={index === 0}>
                        <ArrowUpward fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => moveBlock(index, 1)} disabled={index === blocks.length - 1}>
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => removeBlock(index)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                  {block.type === "inactivity" ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <TextField
                          label={i18n.t("chatbotModal.form.inactivityValue")}
                          type="number"
                          value={block.inactivityValue}
                          onChange={event =>
                            updateBlock(index, {
                              inactivityValue: Math.max(1, Number(event.target.value)),
                            })
                          }
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControl variant="outlined" className={classes.formControl}>
                          <InputLabel>{i18n.t("chatbotModal.form.inactivityUnit")}</InputLabel>
                          <Select
                            value={block.inactivityUnit}
                            label={i18n.t("chatbotModal.form.inactivityUnit")}
                            onChange={event =>
                              updateBlock(index, { inactivityUnit: event.target.value })
                            }
                          >
                            <MenuItem value="minutes">{i18n.t("chatbotModal.units.minutes")}</MenuItem>
                            <MenuItem value="hours">{i18n.t("chatbotModal.units.hours")}</MenuItem>
                            <MenuItem value="days">{i18n.t("chatbotModal.units.days")}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        {renderInactivitySequenceSelector(block, index, values)}
                      </Grid>
                    </Grid>
                  ) : block.type === "followup" ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <TextField
                          label="Tiempo"
                          type="number"
                          value={block.followupValue}
                          onChange={event =>
                            updateBlock(index, {
                              followupValue: Math.max(1, Number(event.target.value)),
                            })
                          }
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControl variant="outlined" className={classes.formControl}>
                          <InputLabel>Intervalo</InputLabel>
                          <Select
                            value={block.followupUnit}
                            label="Intervalo"
                            onChange={event =>
                              updateBlock(index, { followupUnit: event.target.value })
                            }
                          >
                            <MenuItem value="minutes">{i18n.t("chatbotModal.units.minutes")}</MenuItem>
                            <MenuItem value="hours">{i18n.t("chatbotModal.units.hours")}</MenuItem>
                            <MenuItem value="days">{i18n.t("chatbotModal.units.days")}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl variant="outlined" className={classes.formControl}>
                          <InputLabel>Modo</InputLabel>
                          <Select
                            value={block.followupMode || "normal"}
                            label="Modo"
                            onChange={event =>
                              updateBlock(index, { followupMode: event.target.value })
                            }
                          >
                            <MenuItem value="normal">Normal</MenuItem>
                            <MenuItem value="conditions">Por condiciones</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      {block.followupMode === "conditions" ? (
                        renderFollowupConditions(block, index, values)
                      ) : (
                        <Grid item xs={12}>
                          {renderFollowupSequenceSelector(block, index, values)}
                        </Grid>
                      )}
                      {blocks.findIndex(item => item.type === "followup") === index && (
                        <Grid item xs={12}>
                          {renderFollowupCancelSequenceSelector(block, index, values)}
                        </Grid>
                      )}
                    </Grid>
                  ) : block.type === "notify" ? (
                    <>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Autocomplete
                            value={block.notifyContact}
                            options={notifyContactOptions}
                            getOptionLabel={option =>
                              option
                                ? privacyCensorSettings.contactNames
                                  ? "Contacto privado"
                                  : `${option.name || "Sin nombre"} - ${option.number || ""}`
                                : ""
                            }
                            getOptionSelected={(option, value) => option.id === value.id}
                            onInputChange={(_, value) => setContactSearch(value)}
                            onChange={(_, value) =>
                              updateBlock(index, {
                                notifyContact: value,
                                notifyContactId: value?.id || "",
                              })
                            }
                            renderOption={renderContactOption}
                            renderInput={params => (
                              <TextField
                                {...params}
                                label="Contacto"
                                variant="outlined"
                                margin="dense"
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                      <div className={classes.messageBlockGrid}>
                        <ChatbotMessagePreview block={block} classes={classes} />
                        <TextField
                          label="Mensaje para notificar"
                          value={block.text}
                          onChange={event => updateBlock(index, { text: event.target.value })}
                          variant="outlined"
                          margin="dense"
                          multiline
                          minRows={5}
                          fullWidth
                        />
                        <TextField
                          className={classes.messageDelayField}
                          label="Espera de"
                          type="number"
                          value={block.delaySeconds}
                          onChange={event =>
                            updateBlock(index, {
                              delaySeconds: clampDelaySeconds(event.target.value),
                            })
                          }
                          inputProps={{ min: 0, max: 60 }}
                          variant="outlined"
                          margin="dense"
                          helperText={i18n.t("chatbotModal.units.seconds")}
                          fullWidth
                        />
                      </div>
                      {canUseAi && (block.file || block.media) && (
                        <div className={classes.mediaTranscriptionBox}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={Boolean(block.mediaTranscriptionEnabled)}
                                onChange={event =>
                                  updateBlock(index, {
                                    mediaTranscriptionEnabled: event.target.checked,
                                  })
                                }
                                color="primary"
                              />
                            }
                            label="Agregar transcripción para la IA"
                          />
                          {block.mediaTranscriptionEnabled && (
                            <TextField
                              label="Transcripcion para la IA"
                              value={block.mediaTranscriptionText || ""}
                              onChange={event =>
                                updateBlock(index, {
                                  mediaTranscriptionText: event.target.value,
                                })
                              }
                              variant="outlined"
                              margin="dense"
                              multiline
                              minRows={3}
                              fullWidth
                            />
                          )}
                        </div>
                      )}
                      <div className={classes.variablesBox}>
                        <Typography variant="caption" className={classes.variablesLabel}>
                          Variables disponibles
                        </Typography>
                        <div className={classes.variablesChips}>
                          {availableMessageVariables.map(variable => (
                            <Chip
                              key={`notify-${index}-${variable.value}`}
                              size="small"
                              label={variable.label}
                              onClick={() => addVariableToBlock(index, variable.value)}
                            />
                          ))}
                        </div>
                      </div>
                      <div className={classes.fileRow}>
                        <input
                          accept={acceptedMedia}
                          id={`chatbot-notify-file-${index}`}
                          type="file"
                          style={{ display: "none" }}
                          onChange={event =>
                            updateBlock(index, {
                              file: event.target.files?.[0] || null,
                              media: event.target.files?.[0] ? null : block.media,
                              mediaTranscriptionEnabled: event.target.files?.[0]
                                ? block.mediaTranscriptionEnabled
                                : Boolean(block.mediaTranscriptionEnabled && block.media),
                            })
                          }
                        />
                        <label htmlFor={`chatbot-notify-file-${index}`}>
                          <Button variant="outlined" component="span" size="small">
                            {i18n.t("chatbotModal.buttons.file")}
                          </Button>
                        </label>
                        <Typography variant="body2" className={classes.fileName}>
                          {block.file?.name || block.media?.originalName || i18n.t("chatbotModal.form.noFile")}
                        </Typography>
                        {(block.file || block.media) && (
                          <Button
                            size="small"
                            onClick={() =>
                              updateBlock(index, {
                                file: null,
                                media: null,
                                mediaTranscriptionEnabled: false,
                                mediaTranscriptionText: "",
                              })
                            }
                          >
                            {i18n.t("chatbotModal.buttons.removeFile")}
                          </Button>
                        )}
                      </div>
                      <small className={classes.helper}>
                        Imagenes, stickers .webp, audios, videos y documentos.
                      </small>
                    </>
                  ) : block.type === "poll" ? (
                    <div className={classes.messageBlockGrid}>
                      <ChatbotPollPreview block={block} classes={classes} />
                      <Grid container spacing={2} className={classes.pollFieldsGrid}>
                        <Grid item xs={12} md={8}>
                          <TextField
                            label="Titulo de encuesta"
                            value={block.pollTitle || ""}
                            onChange={event => updateBlock(index, { pollTitle: event.target.value })}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            label="Espera de"
                            type="number"
                            value={block.delaySeconds}
                            onChange={event =>
                              updateBlock(index, { delaySeconds: clampDelaySeconds(event.target.value) })
                            }
                            inputProps={{ min: 0, max: 60 }}
                            variant="outlined"
                            margin="dense"
                            helperText={i18n.t("chatbotModal.units.seconds")}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={getPollAllowMultiple(block)}
                                onChange={event =>
                                  updateBlock(index, {
                                    pollAllowMultiple: event.target.checked,
                                    pollSelectableCount: event.target.checked
                                      ? Math.max(2, Number(block.pollSelectableCount || 2))
                                      : 1,
                                  })
                                }
                                color="primary"
                              />
                            }
                            label="Permitir varias respuestas"
                          />
                        </Grid>
                        <Grid item xs={12} md={getPollAllowMultiple(block) ? 8 : 12}>
                          <TextField
                            label="Opciones"
                            value={block.pollOptions || ""}
                            onChange={event => updateBlock(index, { pollOptions: event.target.value })}
                            variant="outlined"
                            margin="dense"
                            multiline
                            minRows={4}
                            helperText="Usa un salto de linea por opcion."
                            fullWidth
                          />
                        </Grid>
                        {getPollAllowMultiple(block) && (
                          <Grid item xs={12} md={4}>
                            <TextField
                              label="Maximo seleccionable"
                              type="number"
                              value={block.pollSelectableCount || 2}
                              onChange={event =>
                                updateBlock(index, {
                                  pollSelectableCount: Math.max(2, Number(event.target.value || 2)),
                                })
                              }
                              inputProps={{ min: 2 }}
                              variant="outlined"
                              margin="dense"
                              fullWidth
                            />
                          </Grid>
                        )}
                      </Grid>
                    </div>
                  ) : block.type === "whatsappList" ? (
                    <div className={classes.messageBlockGrid}>
                      <ChatbotListPreview block={block} classes={classes} />
                      <Grid container spacing={2} className={classes.pollFieldsGrid}>
                        <Grid item xs={12} md={8}>
                          <TextField
                            label="Titulo de lista"
                            value={block.listTitle || ""}
                            onChange={event => updateBlock(index, { listTitle: event.target.value })}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            label="Espera de"
                            type="number"
                            value={block.delaySeconds}
                            onChange={event =>
                              updateBlock(index, { delaySeconds: clampDelaySeconds(event.target.value) })
                            }
                            inputProps={{ min: 0, max: 60 }}
                            variant="outlined"
                            margin="dense"
                            helperText={i18n.t("chatbotModal.units.seconds")}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={8}>
                          <TextField
                            label="Mensaje"
                            value={block.listBody || ""}
                            onChange={event => updateBlock(index, { listBody: event.target.value })}
                            variant="outlined"
                            margin="dense"
                            multiline
                            minRows={3}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            label="Boton"
                            value={block.listButtonText || ""}
                            onChange={event => updateBlock(index, { listButtonText: event.target.value })}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                          />
                          <TextField
                            label="Pie"
                            value={block.listFooter || ""}
                            onChange={event => updateBlock(index, { listFooter: event.target.value })}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" className={classes.sectionTitle}>
                            Opciones
                          </Typography>
                          {normalizeListSections(block).map((section, sectionIndex) => (
                            <Paper className={classes.block} key={`list-section-${index}-${sectionIndex}`}>
                              <Grid container spacing={1} alignItems="center">
                                <Grid item xs={12} md={10}>
                                  <TextField
                                    label="Seccion"
                                    value={section.title || ""}
                                    onChange={event =>
                                      updateListSection(index, sectionIndex, {
                                        title: event.target.value,
                                      })
                                    }
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                  />
                                </Grid>
                                <Grid item xs={12} md={2} style={{ textAlign: "right" }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => removeListSection(index, sectionIndex)}
                                    disabled={normalizeListSections(block).length === 1}
                                  >
                                    <DeleteOutline />
                                  </IconButton>
                                </Grid>
                              </Grid>
                              {section.rows.map((row, rowIndex) => (
                                <Grid container spacing={1} alignItems="center" key={`list-row-${index}-${sectionIndex}-${rowIndex}`}>
                                  <Grid item xs={12} md={5}>
                                    <TextField
                                      label="Opcion"
                                      value={row.title || ""}
                                      onChange={event =>
                                        updateListRow(index, sectionIndex, rowIndex, {
                                          title: event.target.value,
                                        })
                                      }
                                      variant="outlined"
                                      margin="dense"
                                      fullWidth
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      label="Descripcion"
                                      value={row.description || ""}
                                      onChange={event =>
                                        updateListRow(index, sectionIndex, rowIndex, {
                                          description: event.target.value,
                                        })
                                      }
                                      variant="outlined"
                                      margin="dense"
                                      fullWidth
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={1}>
                                    <IconButton
                                      size="small"
                                      onClick={() => removeListRow(index, sectionIndex, rowIndex)}
                                      disabled={section.rows.length === 1}
                                    >
                                      <DeleteOutline />
                                    </IconButton>
                                  </Grid>
                                </Grid>
                              ))}
                              <Button
                                startIcon={<Add />}
                                color="primary"
                                onClick={() => addListRow(index, sectionIndex)}
                                style={{ marginTop: 8 }}
                              >
                                Agregar opcion
                              </Button>
                            </Paper>
                          ))}
                          <Button
                            startIcon={<Add />}
                            color="primary"
                            onClick={() => addListSection(index)}
                            style={{ marginTop: 8 }}
                          >
                            Agregar seccion
                          </Button>
                        </Grid>
                      </Grid>
                    </div>
                  ) : block.type === "contact" ? (
                    <div className={classes.messageBlockGrid}>
                      <ChatbotContactPreview block={block} classes={classes} />
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Nombre"
                            value={block.contactName || ""}
                            onChange={event => updateBlock(index, { contactName: event.target.value })}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Telefono"
                            value={block.contactPhone || ""}
                            onChange={event => updateBlock(index, { contactPhone: event.target.value })}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                      <TextField
                        className={classes.messageDelayField}
                        label="Espera de"
                        type="number"
                        value={block.delaySeconds}
                        onChange={event =>
                          updateBlock(index, { delaySeconds: clampDelaySeconds(event.target.value) })
                        }
                        inputProps={{ min: 0, max: 60 }}
                        variant="outlined"
                        margin="dense"
                        helperText={i18n.t("chatbotModal.units.seconds")}
                        fullWidth
                      />
                    </div>
                  ) : block.type === "location" ? (
                    <div className={classes.messageBlockGrid}>
                      <ChatbotLocationPreview block={block} classes={classes} />
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Latitud"
                            type="number"
                            value={block.locationLatitude}
                            onChange={event => updateBlock(index, { locationLatitude: event.target.value })}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Longitud"
                            type="number"
                            value={block.locationLongitude}
                            onChange={event => updateBlock(index, { locationLongitude: event.target.value })}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label="Nombre"
                            value={block.locationName || ""}
                            onChange={event => updateBlock(index, { locationName: event.target.value })}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label="Direccion"
                            value={block.locationAddress || ""}
                            onChange={event => updateBlock(index, { locationAddress: event.target.value })}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                      <TextField
                        className={classes.messageDelayField}
                        label="Espera de"
                        type="number"
                        value={block.delaySeconds}
                        onChange={event =>
                          updateBlock(index, { delaySeconds: clampDelaySeconds(event.target.value) })
                        }
                        inputProps={{ min: 0, max: 60 }}
                        variant="outlined"
                        margin="dense"
                        helperText={i18n.t("chatbotModal.units.seconds")}
                        fullWidth
                      />
                    </div>
                  ) : (
                    <>
                      <div className={classes.messageBlockGrid}>
                        <ChatbotMessagePreview block={block} classes={classes} />
                        <TextField
                          label={i18n.t("chatbotModal.form.message")}
                          value={block.text}
                          onChange={event => updateBlock(index, { text: event.target.value })}
                          variant="outlined"
                          margin="dense"
                          multiline
                          minRows={5}
                          fullWidth
                        />
                        <TextField
                          className={classes.messageDelayField}
                          label="Espera de"
                          type="number"
                          value={block.delaySeconds}
                          onChange={event => updateBlock(index, { delaySeconds: clampDelaySeconds(event.target.value) })}
                          inputProps={{ min: 0, max: 60 }}
                          variant="outlined"
                          margin="dense"
                          helperText={i18n.t("chatbotModal.units.seconds")}
                          fullWidth
                        />
                      </div>
                      {canUseAi && (block.file || block.media) && (
                        <div className={classes.mediaTranscriptionBox}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={Boolean(block.mediaTranscriptionEnabled)}
                                onChange={event =>
                                  updateBlock(index, {
                                    mediaTranscriptionEnabled: event.target.checked,
                                  })
                                }
                                color="primary"
                              />
                            }
                            label="Agregar transcripción para la IA"
                          />
                          {block.mediaTranscriptionEnabled && (
                            <TextField
                              label="Transcripcion para la IA"
                              value={block.mediaTranscriptionText || ""}
                              onChange={event =>
                                updateBlock(index, {
                                  mediaTranscriptionText: event.target.value,
                                })
                              }
                              variant="outlined"
                              margin="dense"
                              multiline
                              minRows={3}
                              fullWidth
                            />
                          )}
                        </div>
                      )}
                      <div className={classes.variablesBox}>
                        <Typography variant="caption" className={classes.variablesLabel}>
                          Variables disponibles
                        </Typography>
                        <div className={classes.variablesChips}>
                          {availableMessageVariables.map(variable => (
                            <Chip
                              key={`message-${index}-${variable.value}`}
                              size="small"
                              label={variable.label}
                              onClick={() => addVariableToBlock(index, variable.value)}
                            />
                          ))}
                        </div>
                      </div>
                      <div className={classes.fileRow}>
                        <input
                          accept={acceptedMedia}
                          id={`chatbot-file-${index}`}
                          type="file"
                          style={{ display: "none" }}
                          onChange={event =>
                            updateBlock(index, {
                              file: event.target.files?.[0] || null,
                              media: event.target.files?.[0] ? null : block.media,
                              mediaTranscriptionEnabled: event.target.files?.[0]
                                ? block.mediaTranscriptionEnabled
                                : Boolean(block.mediaTranscriptionEnabled && block.media),
                            })
                          }
                        />
                        <label htmlFor={`chatbot-file-${index}`}>
                          <Button variant="outlined" component="span" size="small">
                            {i18n.t("chatbotModal.buttons.file")}
                          </Button>
                        </label>
                        <Typography variant="body2" className={classes.fileName}>
                          {block.file?.name || block.media?.originalName || i18n.t("chatbotModal.form.noFile")}
                        </Typography>
                        {(block.file || block.media) && (
                          <Button
                            size="small"
                            onClick={() =>
                              updateBlock(index, {
                                file: null,
                                media: null,
                                mediaTranscriptionEnabled: false,
                                mediaTranscriptionText: "",
                              })
                            }
                          >
                            {i18n.t("chatbotModal.buttons.removeFile")}
                          </Button>
                        )}
                      </div>
                      <small className={classes.helper}>
                        Imagenes, stickers .webp, audios, videos y documentos.
                      </small>
                    </>
                    )}
                </Paper>
              ))}
              <div className={classes.blockTypeActions}>
                <Button
                  startIcon={<Add />}
                  color="primary"
                  onClick={() => setBlocks(prevBlocks => [...prevBlocks, createBlock()])}
                >
                  Mensaje
                </Button>
                <Button
                  startIcon={<Add />}
                  color="primary"
                  onClick={() => setBlocks(prevBlocks => [...prevBlocks, createPollBlock()])}
                >
                  Encuesta
                </Button>
                <Button
                  startIcon={<Add />}
                  color="primary"
                  onClick={() => setBlocks(prevBlocks => [...prevBlocks, createWhatsAppListBlock()])}
                >
                  Lista
                </Button>
                <Button
                  startIcon={<Add />}
                  color="primary"
                  onClick={() => setBlocks(prevBlocks => [...prevBlocks, createContactBlock()])}
                >
                  Contacto
                </Button>
                <Button
                  startIcon={<Add />}
                  color="primary"
                  onClick={() => setBlocks(prevBlocks => [...prevBlocks, createLocationBlock()])}
                >
                  Ubicacion
                </Button>
                <Button
                  startIcon={<Add />}
                  color="primary"
                  onClick={() => setBlocks(prevBlocks => [...prevBlocks, createInactivityBlock()])}
                >
                  Inactividad
                </Button>
                <Button
                  startIcon={<Add />}
                  color="primary"
                  onClick={() => setBlocks(prevBlocks => [...prevBlocks, createNotifyBlock()])}
                >
                  Notificar
                </Button>
                <Button
                  startIcon={<Add />}
                  color="primary"
                  onClick={() => setBlocks(prevBlocks => [...prevBlocks, createFollowupBlock()])}
                >
                  Seguimiento
                </Button>
              </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} color="secondary" disabled={isSubmitting} variant="outlined">
                {i18n.t("chatbotModal.buttons.cancel")}
              </Button>
              <Button type="submit" color="primary" disabled={isSubmitting} variant="contained" className={classes.btnWrapper}>
                {sequenceId ? i18n.t("chatbotModal.buttons.okEdit") : i18n.t("chatbotModal.buttons.okAdd")}
                {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
    {sequenceModalOpen && (
      <ChatbotModal
        open={sequenceModalOpen}
        onClose={handleSequenceModalClose}
        onSave={handleSequenceSaved}
        sequenceId={sequenceModalId}
        defaultConnectionId={sequenceModalConnectionId}
        initialSequenceCategory={sequence.category || initialSequenceCategory}
        categories={categoryOptions}
        extraVariables={extraVariables}
      />
    )}
    </>
  );
};

export default ChatbotModal;

